import React, { useContext, useState, useEffect, useMemo } from "react";
import ToasterWithMessage from "../../components/shared-components/toaster";
import styles from "./Userdetails.module.css";
import {
  User, Mail, Phone, Briefcase, MapPinned, TrendingUp, CheckCircle,
  CreditCard, MessageCircle, FileText, Plus, Clock, Award, ChevronLeft,
  Search, Edit, Delete, Pencil, Trash2, MoreVertical, X, Loader, Building, MapPin, CheckSquare,
  ChevronDown, AlertTriangle, ArrowRight, Package, Download, CheckCheck, Lock, DollarSign, Lightbulb
} from "lucide-react";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import DarkModeOutlinedIcon from "@mui/icons-material/DarkModeOutlined";
import LightModeOutlinedIcon from "@mui/icons-material/LightModeOutlined";
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useLazyQuery, useApolloClient } from '@apollo/client';
import {
  GET_USER_BY_ID, GET_SUPER_USER_BY_USER_ID, GET_USER_TICKETS, GET_USER_PERFORMANCE,
  GET_USER_ANALYTICS,
  UPDATE_USER, DELETE_USER,
  GET_USER_NOTES, ADD_USER_NOTE, UPDATE_USER_NOTE, DELETE_USER_NOTE, VOID_USER_PAYOUTS,
  RESTORE_USER_PAYOUTS, UPDATE_USER_EMAIL_STATUS, GET_USER_EMAIL_STATUS, UPDATE_SUPER_USER_PERCENTAGE,
  DEACTIVATE_SUPER_USER, INSERT_SUPER_USER, GET_USER_PREFERENCE_TEAMS,
  GET_LEAGUE_CATEGORIES_BY_IDS, GET_USER_SAVED_SEAT_INFO, INSERT_USER_SAVED_SEAT_INFO,
  UPDATE_USER_SAVED_SEAT_INFO, GET_USER_TEAMS_WITH_SOLD_STATUS, GET_AVAILABLE_TEAMS_FOR_USER,
  ADD_USER_PREFERENCE_TEAM, REMOVE_USER_PREFERENCE_TEAM , ADMIN_REMOVE_USER_PREFERENCE_TEAM,
  GET_USER_TICKET_MARKETPLACES,
} from './api/UserdetailsQuery';
import { GET_USERS_LIST, GET_ALL_LEAGUES } from '../Users/api/UsersQuery';
import {
  GET_MANAGE_TICKETS,
  EDIT_TICKET_PRICE_BY_SUPER_USER,
  UPDATE_TICKET_STATUS,
  UPDATE_TICKET_PAYMENT_STATUS,
  UPDATE_TICKETS_PAYMENT_STATUS,
  RESTORE_CANCELLED_LISTING_TO_VERIFICATION,
} from '../ManageTickets/api/TicketsQuery';
import { GET_MARKETPLACES_FOR_TICKETS } from '../ManageTickets/api/TicketsQuery';
import { GET_USER_FEE_PERCENT_BY_ID } from '../ManageTickets/api/TicketsQuery';
import { globalContext } from "../../Provider/AppProvider";
import { roles, roundToFixed, formatNumberWithCommas } from "../../helper";
import { getStatusIcon } from '../ManageTickets/getStatusIcon';
import { config } from '../../config/config';
import { sortUserDetailTickets, MANAGE_TICKETS_ORDER_BY } from '../ManageTickets/manageTicketSort';
import { GET_NOTIFICATIONS } from '../../components/shared-components/notification/api/NotificationQuery';
import { getNotificationQueryVariables } from '../../utils/notification-query-variables';

// ------------------- Interfaces -------------------
interface UserData {
  id: string; name: string; initials: string; type: string; tier: string;
  business: string; email: string; phone: string; address1: string; address2: string;
  address3: string; lastActive: string; annualSales: string; teams: string[];
  assignedTeams?: string[]; userFee: string; status: string; createdAt: string;
  city: string; state: string; zip: string; country: string; payoutsVoided: boolean;
  voidReason?: string; voidDate?: string; emailEnabled: boolean; hearAboutUs?: string;
}
interface Ticket {
  id: string; tpId: string; rawStatus: string; event: string; date: string;
  section: string; row: string; seatNo: string; status: string; price: string;
  soldPrice: string;
  paymentStatus?: string | null;
  payoutStatus?: string | null;
  updatedAt?: string;
  eventDateRaw?: string;
  eventTime?: string;
  eventId?: string;
  marketplace?: string | null;
}

interface PerformanceMetrics {
  active: number; potential: string; sold: number; revenue: string; paidOut: string;
  feeRate: string; phone: number; verified: number; listed: number; ticketsSold: number;
  delisted: number; unsold: number; neverTransferred: number;
}

interface Note {
  id: string; type: 'qa' | 'internal' | 'external'; content: string;
  createdAt: string; createdBy: string; updatedAt?: string;
}

interface LeagueCategoryPreference {
  lc_id: string; lc_abbrev?: string | null; lc_name?: string | null; lc_colour_code?: string | null; l_id?: string;
}

interface SeatInfoRow {
  id: string; event: string; date: string; section: string; row: string;
  seatNo: string; source: 'saved' | 'ticket'; ussiId?: string; ticketId?: string;
}

type MarketplaceOption = {
  id: string; name: string; vl_fee_percentage: number; active?: boolean;
};

type SoldFeeConfig = {
  feeMode: 'super' | 'default';
  superClientPercent: number | null;
};

const DEFAULT_SOLD_FEE_CONFIG: SoldFeeConfig = { feeMode: 'default', superClientPercent: null };

type SelectedTeam = {
  id: string; abbreviation: string; name: string; color: string; hasSoldTicket?: boolean; leagueId?: string; leagueName?: string;
};

// ------------------- Helpers -------------------
const DEFAULT_CONCIERGE_PERCENT = 20;

function effectiveSoldQuickCutPercent(
  marketplaceFeePercent: number, feeMode: 'super' | 'default', superClientPercent: number | null
): number {
  const clientPercent = feeMode === 'super' && superClientPercent != null ? superClientPercent : DEFAULT_CONCIERGE_PERCENT;
  return Math.max(0, clientPercent - marketplaceFeePercent);
}

const formatCurrency = (value: number) => {
  if (!Number.isFinite(value)) return '$0.00';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
};

const formatOptionalCurrency = (value: unknown): string => {
  if (value == null || String(value).trim() === '') return '-';
  const num = Number(value);
  if (!Number.isFinite(num)) return '-';
  return formatCurrency(num);
};

const pickTicketValue = (...values: Array<unknown>) => {
  for (const value of values) {
    if (value == null) continue;
    if (typeof value === 'string' && value.trim() === '') continue;
    return value;
  }
  return null;
};

const formatPercent = (value: number) => {
  if (!Number.isFinite(value)) return '0%';
  return `${value.toFixed(1)}%`;
};

const formatDate = (dateString: string): string => {
  if (!dateString) return 'N/A';
  return new Date(dateString).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const getStatusLabel = (status: string): string => {
  if (!status) return 'Unknown';
  const normalizedStatus = status.toLowerCase();
  if (normalizedStatus === 'list') return 'Listed';
  if (normalizedStatus === 'delistinprogress' || normalizedStatus === 'delistrequested') return 'Delist Requested';
  if (normalizedStatus === 'listingrequestcancelled') return 'Listing Request Cancelled';
  if (normalizedStatus === 'closed') return 'Listing Request Expired';
  if (normalizedStatus === 'settled') return 'Settled';
  if (normalizedStatus === 'settlementinprogress') return 'Settlement In Progress';
  if (normalizedStatus === 'return') return 'Returned';
  if (normalizedStatus === 'tobeverified') return 'To Be Verified';
  if (normalizedStatus === 'verified') return 'Verified';
  if (normalizedStatus === 'invalid') return 'Invalid';
  if (normalizedStatus === 'sold') return 'Sold';
  if (normalizedStatus === 'unsold') return 'Unsold';
  if (normalizedStatus === 'delist') return 'Delisted';
  return status;
};

const getTicketStatusPresentation = (ticket: Ticket): { label: string; iconStatus: string } => {
  const placementStatus = mapStatusForMutation(ticket.rawStatus || ticket.status);
  if (placementStatus.toLowerCase() !== 'sold') {
    const label = getStatusLabel(ticket.rawStatus || ticket.status);
    return { label, iconStatus: placementStatus };
  }
  const pay = (ticket.paymentStatus || '').trim();
  if (pay === 'Success') {
    return { label: 'Settled', iconStatus: 'settled' };
  }
  if (pay === 'Inprogress') {
    return { label: 'Settlement In Progress', iconStatus: 'settlementinprogress' };
  }
  if (pay === 'Failed') {
    return { label: 'Failed', iconStatus: 'failed' };
  }
  if (!pay) {
    const payout = (ticket.payoutStatus || '').trim();
    if (payout && payout !== 'Unvoided_Payout') {
      return { label: payout.replace(/_/g, ' '), iconStatus: 'sold' };
    }
    return { label: 'Sold', iconStatus: 'sold' };
  }
  return { label: pay, iconStatus: 'sold' };
};

const getSoldPresentationFilterKey = (ticket: Ticket): 'sold' | 'settled' | 'settlementinprogress' | 'failed' => {
  const key = getTicketStatusPresentation(ticket).iconStatus.toLowerCase();
  if (key === 'settled') return 'settled';
  if (key === 'settlementinprogress') return 'settlementinprogress';
  if (key === 'failed') return 'failed';
  return 'sold';
};

const getStatusColorClass = (status: string): string => {
  if (!status) return '';
  const s = status.toLowerCase().replace(/\s+/g, '');
  if (s === "verified") return "verified";
  if (s === "list" || s === "listed") return "listed";
  if (s === "tobeverified" || s === "tobeeverified") return "tobeverified";
  if (s === "invalid") return "invalid";
  if (s === "sold") return "sold";
  if (s === "return" || s === "returned") return "return";
  if (
    s === "delist" ||
    s === "delistinprogress" ||
    s === "delistrequested" ||
    s === "listingrequestcancelled"
  ) {
    return "delist";
  }
  if (s === "unsold") return "unsold";
  if (s === "closed" || s === "listingrequestexpired") return "closed";
  if (s === "settled") return "settled";
  if (s === "settlementinprogress") return "settlementinprogress";
  return "";
};

const displayPrice = (priceStr: string): string => {
  if (!priceStr || priceStr === '-' || priceStr.trim() === '') return '-';
  const numeric = parseCurrency(priceStr);
  if (!Number.isFinite(numeric)) return '-';
  return formatCurrency(numeric);
};

const mapStatusForMutation = (status: string): string => {
  const s = status.toLowerCase();
  if (s === 'listed' || s === 'list') return 'List';
  if (s === 'sold') return 'Sold';
  if (s === 'settled') return 'Settled';
  if (s === 'settlement in progress' || s === 'settlementinprogress') return 'SettlementInProgress';
  if (s === 'unsold') return 'Unsold';
  if (s === 'verified') return 'Verified';
  if (s === 'to be verified' || s === 'tobeverified') return 'ToBeVerified';
  if (s === 'delisted') return 'Delist';
  if (s === 'delist requested') return 'DelistInProgress';
  if (s === 'delistrequested') return 'DelistInProgress';
  if (s === 'delistinprogress') return 'DelistInProgress';
  if (s === 'listing request expired') return 'Closed';
  if (s === 'listing request cancelled') return 'ListingRequestCancelled';
  if (s === 'listingrequestcancelled') return 'ListingRequestCancelled';
  if (s === 'invalid') return 'ListingRequestCancelled';
  if (s === 'return' || s === 'returned') return 'Return';
  if (s === 'move to listed') return 'List';
  return status;
};

const emailRegexPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

const getStatusTransitions = (currentStatus: string) => {
  const normalizedStatus = currentStatus 
    ? ((currentStatus || '').toLowerCase().replace(/\s/g, ''))
    : '';
  
  const statusCanonicalMapLocal: Record<string, string> = {
    closed: 'Closed',
    delist: 'Delist',
    delistinprogress: 'DelistInProgress',
    delistrequested: 'DelistInProgress',
    invalid: 'Invalid',
    list: 'List',
    listed: 'List',
    listingrequestcancelled: 'ListingRequestCancelled',
    return: 'Return',
    sold: 'Sold',
    tobeverified: 'ToBeVerified',
    unsold: 'Unsold',
    verified: 'Verified'
  };
  
  const canonicalStatus = statusCanonicalMapLocal[normalizedStatus] || currentStatus;
  
  const statusTransitions: { [key: string]: { value: string, label: string }[] } = {
    ToBeVerified: [
      { value: 'Verified', label: 'Verified' },
      { value: 'ListingRequestCancelled', label: 'Listing Request Cancelled' }
    ],
    Verified: [
      { value: 'List', label: 'Listed' },
      { value: 'DelistInProgress', label: 'Delist Requested' }
    ],
    List: [
       { value: 'Delist', label: 'Delisted' },
      { value: 'Sold', label: 'Sold' },
      { value: 'Unsold', label: 'Unsold' }
    ],
    DelistInProgress: [
      { value: 'Delist', label: 'Delist' },
      { value: 'List', label: 'Listed (Undo)' },
      { value: 'Return', label: 'Returned' },
    ],
    Delist: [
      { value: 'DelistInProgress', label: 'Delist Requested' },
      { value: 'List', label: 'Listed (Undo)' },
      { value: 'Return', label: 'Returned' },
    ],
    Sold: [
      { value: 'Settled', label: 'Settled' },
      { value: 'SettlementInProgress', label: 'Settlement In Progress' },
    ],
    Settled: [
      { value: 'Sold', label: 'Sold' },
      { value: 'SettlementInProgress', label: 'Settlement In Progress' },
    ],
    SettlementInProgress: [
      { value: 'Sold', label: 'Sold' },
      { value: 'Settled', label: 'Settled' },
    ],
    Unsold: [
      { value: 'Sold', label: 'Sold' }
    ],
    Invalid: [
      { value: 'ToBeVerified', label: 'To Be Verified' }
    ],
    ListingRequestCancelled: [
      { value: 'ToBeVerified', label: 'To Be Verified' }
    ],
    Return: [],
    Closed: []
  };
  return statusTransitions[canonicalStatus] || [];
};

const normalizeStatusToken = (status: string) => {
  return (status || '').toLowerCase().replace(/[^a-z0-9]/g, '');
};

const parseCurrency = (value: string): number => {
  const normalized = value.replace(/[^0-9.]/g, '');
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : 0;
};

const formatListPrice = (listPrice: unknown): string => {
  if (listPrice == null || String(listPrice).trim() === '') return '-';
  const num = Number(listPrice);
  if (!Number.isFinite(num) || num <= 0) return '-';
  return formatCurrency(num);
};

const isMissingVerifyRequiredFields = (ticket: Ticket): boolean =>
  !ticket.section ||
  ticket.section === '-' ||
  !ticket.row ||
  ticket.row === '-' ||
  !ticket.seatNo ||
  ticket.seatNo === '-' ||
  ticket.price == null ||
  String(ticket.price).trim() === '' ||
  Number.isNaN(parseCurrency(ticket.price));

const isValidUuid = (value?: string) => Boolean(value && /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value));

// ------------------- Main Component -------------------
const UserDetailsPage = () => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { globalState, handleGlobalState } = useContext<any>(globalContext);
  const apolloClient = useApolloClient();
  
  const activeHasuraRole = (localStorage.getItem('x-hasura-user-role') || '').toLowerCase().trim();
  const profileRole = (globalState?.profile?.userRole || '').toLowerCase().trim();
  const normalizedPortalRole = activeHasuraRole || profileRole;
  const isAffiliateAdmin = normalizedPortalRole === roles.AFFILIATER_ADMIN;
  const isStaffAdminEquivalent = (role: string) => [roles.ADMIN, roles.SUB_ADMIN, roles.AFFILIATER_ADMIN].includes(role);
  const canEditPricing = isStaffAdminEquivalent(normalizedPortalRole);
  const canOverrideSettlementStatus = isStaffAdminEquivalent(normalizedPortalRole);
  
  // UI State
  const [showQANote, setShowQANote] = useState(false);
  const [showInternalNote, setShowInternalNote] = useState(false);
  const [showExternalNote, setShowExternalNote] = useState(false);
  const [editingNote, setEditingNote] = useState<{ id: string; type: string; content: string } | null>(null);
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const saved = localStorage.getItem('theme');
    if (saved) return saved === 'dark';
    return document.documentElement.getAttribute('data-theme') === 'dark';
  });
  const [searchQuery, setSearchQuery] = useState("");
  const [teamSearchQuery, setTeamSearchQuery] = useState("");
  const initialTab = searchParams.get('tab') || 'profile';
  const [activeTab, setActiveTab] = useState(initialTab);
  const [isEditing, setIsEditing] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [openActionTicketId, setOpenActionTicketId] = useState<string | null>(null);
  const [localTickets, setLocalTickets] = useState<Ticket[]>([]);
  const [activeFilterTab, setActiveFilterTab] = useState('All');
  const [soldSubFilter, setSoldSubFilter] = useState<'all' | 'sold' | 'settled' | 'settlementinprogress'>('all');
  const [notes, setNotes] = useState<Note[]>([]);
  const [newNoteContentByType, setNewNoteContentByType] = useState<Record<'qa' | 'internal' | 'external', string>>({ qa: '', internal: '', external: '' });
  const [showDelistEmailModal, setShowDelistEmailModal] = useState(false);
  const [pendingDelistTpIds, setPendingDelistTpIds] = useState<string[]>([]);
  const [delistEmail, setDelistEmail] = useState('');
  const [delistConfirmEmail, setDelistConfirmEmail] = useState('');
  const [delistRequestLoading, setDelistRequestLoading] = useState(false);
  const [selectedTeamForSeats, setSelectedTeamForSeats] = useState<SelectedTeam | null>(null);
  const [editingSeatTicketId, setEditingSeatTicketId] = useState<string | null>(null);
  const [seatEditForm, setSeatEditForm] = useState({ section: '', row: '', seatNo: '' });
  const [savingSeatTicketId, setSavingSeatTicketId] = useState<string | null>(null);
  const [localSeatInfoOverrides, setLocalSeatInfoOverrides] = useState<Record<string, SeatInfoRow[]>>({});
  const [showVoidPayoutsModal, setShowVoidPayoutsModal] = useState(false);
  const [voidReason, setVoidReason] = useState('');
  const [isVoidingPayouts, setIsVoidingPayouts] = useState(false);
  const [openStatusDropdown, setOpenStatusDropdown] = useState<string | null>(null);
  
  // Team Management State
  const [showAddTeamModal, setShowAddTeamModal] = useState(false);
  const [availableTeamsForUser, setAvailableTeamsForUser] = useState<Array<{lc_id: string; lc_abbrev: string; lc_name: string; lc_colour_code: string; l_id?: string}>>([]);
  const [teamsWithSoldStatus, setTeamsWithSoldStatus] = useState<Record<string, boolean>>({});
  const [addingTeamId, setAddingTeamId] = useState<string | null>(null);
  const [removingTeamId, setRemovingTeamId] = useState<string | null>(null);
  const [adminRemovingTeamId, setAdminRemovingTeamId] = useState<string | null>(null);
  const [selectedLeagueFilter, setSelectedLeagueFilter] = useState<string>('all');
  
  // Modal states for ticket status transitions
  const [showListedModal, setShowListedModal] = useState(false);
  const [listedModalLoading, setListedModalLoading] = useState(false);
  const [listedModalTicket, setListedModalTicket] = useState<Ticket | null>(null);
  const [listedPrice, setListedPrice] = useState('');
  const [showSoldModal, setShowSoldModal] = useState(false);
  const [soldModalTicket, setSoldModalTicket] = useState<Ticket | null>(null);
  const [soldMarketplaceId, setSoldMarketplaceId] = useState('');
  const [soldLogitixAmount, setSoldLogitixAmount] = useState('');
  const [soldFeeMode, setSoldFeeMode] = useState<'super' | 'default'>('default');
  const [soldSuperClientPercent, setSoldSuperClientPercent] = useState<number | null>(null);
  const [soldFeeConfigReady, setSoldFeeConfigReady] = useState(false);
  const [soldModalLoading, setSoldModalLoading] = useState(false);
  const [showPriceModal, setShowPriceModal] = useState(false);
  const [priceModalTicket, setPriceModalTicket] = useState<Ticket | null>(null);
  const [newPrice, setNewPrice] = useState('');
  const [priceModalLoading, setPriceModalLoading] = useState(false);
  const [showChangeStatusModal, setShowChangeStatusModal] = useState(false);
  const [changeStatusTicket, setChangeStatusTicket] = useState<Ticket | null>(null);
  const [selectedNewStatus, setSelectedNewStatus] = useState('');
  const [changeStatusLoading, setChangeStatusLoading] = useState(false);
  const [showEditTicketModal, setShowEditTicketModal] = useState(false);
  const [editTicket, setEditTicket] = useState<Ticket | null>(null);
  const [editTicketForm, setEditTicketForm] = useState({ section: '', row: '', seatNo: '', price: '' });
  const [editTicketLoading, setEditTicketLoading] = useState(false);
  const [bulkSelectedTicketIds, setBulkSelectedTicketIds] = useState<Set<string>>(new Set());
  const [showBulkStatusModal, setShowBulkStatusModal] = useState(false);
  const [bulkSelectedTickets, setBulkSelectedTickets] = useState<Ticket[]>([]);
  const [bulkNewStatus, setBulkNewStatus] = useState('');
  const [bulkStatusListingPrices, setBulkStatusListingPrices] = useState<Record<string, string>>({});
  const [bulkStatusListingCommonPrice, setBulkStatusListingCommonPrice] = useState('');
  const [bulkStatusChangeLoading, setBulkStatusChangeLoading] = useState(false);
  const [bulkSoldMarketplaceId, setBulkSoldMarketplaceId] = useState('');
  const [bulkSoldLogitixAmounts, setBulkSoldLogitixAmounts] = useState<Record<string, string>>({});
  const [bulkSoldCommonLogitixAmount, setBulkSoldCommonLogitixAmount] = useState('');
  const [bulkSoldFeeConfigByTicketId, setBulkSoldFeeConfigByTicketId] = useState<Record<string, SoldFeeConfig>>({});
  const [bulkSoldFeeConfigReady, setBulkSoldFeeConfigReady] = useState(false);
  const [bulkSoldLoading, setBulkSoldLoading] = useState(false);
  const [bulkPriceTpIds, setBulkPriceTpIds] = useState<string[]>([]);
  
  // Bulk pricing modal state
  const [showBulkPriceModal, setShowBulkPriceModal] = useState(false);
  const [bulkPriceModalTickets, setBulkPriceModalTickets] = useState<Ticket[]>([]);
  const [bulkPrices, setBulkPrices] = useState<Record<string, string>>({});
  const [bulkCommonPrice, setBulkCommonPrice] = useState('');
  const [bulkPriceLoading, setBulkPriceLoading] = useState(false);
  
  // GraphQL Queries & Mutations
  const bulkSoldActiveInStatusModal =
    showBulkStatusModal && mapStatusForMutation(bulkNewStatus) === 'Sold';

  const { data: marketplacesData, refetch: refetchMarketplaces } = useQuery(GET_MARKETPLACES_FOR_TICKETS, {
    fetchPolicy: 'network-only',
    skip: !showSoldModal && !bulkSoldActiveInStatusModal,
  });
  const marketplacesList: MarketplaceOption[] = React.useMemo(() => {
    const dbList: MarketplaceOption[] = Array.isArray(marketplacesData?.marketplaces) ? marketplacesData.marketplaces : [];
    return dbList.filter((mp) => mp.active ?? true).sort((a, b) => a.name.localeCompare(b.name));
  }, [marketplacesData]);

  useEffect(() => {
    if (bulkSoldActiveInStatusModal && marketplacesList.length > 0 && !bulkSoldMarketplaceId) {
      setBulkSoldMarketplaceId(marketplacesList[0].id);
    }
  }, [bulkSoldActiveInStatusModal, marketplacesList, bulkSoldMarketplaceId]);

  useEffect(() => {
    if (!bulkSoldActiveInStatusModal || !bulkSoldMarketplaceId || !bulkSoldFeeConfigReady) return;
    const mp = marketplacesList.find((m) => m.id === bulkSoldMarketplaceId);
    if (!mp || bulkSelectedTickets.length === 0) return;
    setBulkSoldLogitixAmounts((prev) => {
      const next = { ...prev };
      bulkSelectedTickets.forEach((ticket) => {
        const list = getTicketListPrice(ticket);
        const expected = Math.round(list * (100 - mp.vl_fee_percentage)) / 100;
        next[ticket.tpId] = String(Number(roundToFixed(expected, 2)));
      });
      return next;
    });
  }, [
    bulkSoldActiveInStatusModal,
    bulkSoldMarketplaceId,
    bulkSoldFeeConfigReady,
    bulkSelectedTickets,
    marketplacesList,
  ]);
  
  const [getUserFeePercentById] = useLazyQuery(GET_USER_FEE_PERCENT_BY_ID, { fetchPolicy: 'no-cache' });
  const [getSuperUserForSold] = useLazyQuery(GET_SUPER_USER_BY_USER_ID, { fetchPolicy: 'no-cache' });
  
  const { loading: userLoading, error: userError, data: userData, refetch: refetchUser } = useQuery(GET_USER_BY_ID, {
    variables: { userId }, skip: !userId, fetchPolicy: 'network-only'
  });
  const isGetUserByIdAllowlistError = (userError?.message || '').toLowerCase().includes('query is not allowed');
  const { loading: fallbackUserLoading, data: fallbackUsersData } = useQuery(GET_USERS_LIST, {
    variables: { pageSize: 1000, pageOffset: 0, searchText: '%', order_by: [{ u_created_at: 'desc' }] },
    skip: !userId || (!isGetUserByIdAllowlistError && !isAffiliateAdmin), fetchPolicy: 'network-only'
  });
  const fallbackUserRecord = Array.isArray(fallbackUsersData?.user) ? fallbackUsersData.user.find((u: { u_id: string }) => u.u_id === userId) : null;
  const affiliateManagedUserIds = new Set(Array.isArray(fallbackUsersData?.user) ? fallbackUsersData.user.map((u: { u_id: string }) => u.u_id) : []);
  const isAffiliateScopeResolved = !isAffiliateAdmin || Boolean(fallbackUsersData?.user);
  const isAffiliateAllowedUser = !isAffiliateAdmin || !userId || affiliateManagedUserIds.has(userId);
  const effectiveUserRecord = userData?.user?.[0] || fallbackUserRecord || null;
  const isUserLoading = userLoading || (isGetUserByIdAllowlistError && fallbackUserLoading);
  
  const { data: superUserData, refetch: refetchSuperUser } = useQuery(GET_SUPER_USER_BY_USER_ID, {
    variables: { userId }, skip: !userId, fetchPolicy: 'network-only'
  });
  
  const { loading: ticketsLoading, error: ticketsError, data: ticketsData, refetch: refetchTickets } = useQuery(GET_USER_TICKETS, {
    variables: { userId }, skip: !userId, fetchPolicy: 'network-only'
  });
  const isGetUserTicketsAllowlistError = (ticketsError?.message || '').toLowerCase().includes('query is not allowed');
  const { data: userTicketMarketplacesData } = useQuery(GET_USER_TICKET_MARKETPLACES, {
    variables: { userId },
    skip: !userId,
    fetchPolicy: 'network-only',
  });
  const { loading: fallbackTicketsLoading, data: fallbackManageTicketsData } = useQuery(GET_MANAGE_TICKETS, {
    variables: { enddate: null, leagueId: null, startdate: null, ticketStatus: null, ticketId: null, tpId: null, pageSize: 5000, pageOffset: 0, day: null, search_event: '%', array_tpid: null, pricingMode: '%', order_by: MANAGE_TICKETS_ORDER_BY },
    skip: !userId || !isGetUserTicketsAllowlistError, fetchPolicy: 'network-only'
  });
  
  const { loading: performanceLoading, data: performanceData, refetch: refetchPerformance } = useQuery(GET_USER_PERFORMANCE, {
    variables: { userId }, skip: !userId, fetchPolicy: 'network-only'
  });
  
  const { loading: analyticsLoading, data: analyticsData, refetch: refetchAnalytics } = useQuery(GET_USER_ANALYTICS, {
    variables: { userId }, skip: !userId || activeTab !== 'analytics', fetchPolicy: 'network-only'
  });

  const hearAboutUsLoading = false;
  const hearAboutUsData: any = null;
  
  const { data: userPreferenceData, loading: userPreferenceLoading, refetch: refetchUserPreferences } = useQuery(GET_USER_PREFERENCE_TEAMS, {
    variables: { userId }, skip: !userId, fetchPolicy: 'network-only'
  });
  const preferredCategoryIds: string[] = Array.isArray(userPreferenceData?.user_preference)
    ? userPreferenceData.user_preference.map((p: { lc_id?: string }) => p?.lc_id).filter(Boolean) : [];
  
  const { data: availableTeamsData, loading: availableTeamsLoading } = useQuery(GET_AVAILABLE_TEAMS_FOR_USER, {
    fetchPolicy: 'cache-first'
  });
  const { data: allLeaguesData, loading: allLeaguesLoading } = useQuery(GET_ALL_LEAGUES, {
    fetchPolicy: 'cache-first'
  });
  const leagueNameById = new Map<string, string>(
    (allLeaguesData?.leagues || []).map((league: any) => [league.l_id, league.l_name || league.l_id]),
  );
  
  const { data: teamsWithSoldData, loading: teamsWithSoldLoading, refetch: refetchTeamsWithSoldStatus } = useQuery(GET_USER_TEAMS_WITH_SOLD_STATUS, {
    variables: { userId }, skip: !userId, fetchPolicy: 'network-only'
  });
  
  const { data: preferredLeagueCategoriesData, loading: preferredLeagueCategoriesLoading } = useQuery(GET_LEAGUE_CATEGORIES_BY_IDS, {
    variables: { categoryIds: preferredCategoryIds }, skip: preferredCategoryIds.length === 0, fetchPolicy: 'network-only'
  });
  
  const selectedTeamHasCategoryId = selectedTeamForSeats ? isValidUuid(selectedTeamForSeats.id) : false;
  const { data: userSavedSeatInfoData, loading: userSavedSeatInfoLoading, refetch: refetchUserSavedSeatInfo } = useQuery(GET_USER_SAVED_SEAT_INFO, {
    variables: { userId, lcId: selectedTeamForSeats?.id },
    skip: !userId || !selectedTeamForSeats || !selectedTeamHasCategoryId, fetchPolicy: 'network-only'
  });
  
  const { loading: notesLoading, data: notesData, refetch: refetchNotes } = useQuery(GET_USER_NOTES, {
    variables: { userId }, skip: !userId, fetchPolicy: 'network-only',
    onCompleted: (data) => { if (data?.user_notes) setNotes(transformNotesData(data.user_notes)); }
  });

  const { data: emailStatusData } = useQuery(GET_USER_EMAIL_STATUS, {
    variables: { userId }, skip: !userId, fetchPolicy: 'network-only', pollInterval: 5000,
    onCompleted: (d) => {
      const remoteEnabled = d?.user?.[0]?.u_email_enabled;
      const localEnabled = effectiveUserRecord?.u_email_enabled;
      if (typeof remoteEnabled !== 'undefined' && remoteEnabled !== localEnabled) {
        refetchUser();
        refetchNotes();
      }
    }
  });

  const [voidPayoutNote, setVoidPayoutNote] = useState('');
  const [addNoteOnVoid, setAddNoteOnVoid] = useState(false);
  
  const { error: showError, success: showSuccess, info: showInfo } = ToasterWithMessage();

  const [refreshNotifications] = useLazyQuery(GET_NOTIFICATIONS, {
    fetchPolicy: 'network-only',
    onCompleted: (notificationData) => {
      if (notificationData && globalState?.profile?.id) {
        handleGlobalState((prev: typeof globalState) => ({
          ...prev,
          notifications: notificationData.notifications || prev?.notifications || [],
        }));
      }
    },
  });

  const syncDashboardNotifications = () => {
    if (!globalState?.profile?.id) return;
    const adminId = globalState.profile.id;
    const runSync = () => {
      refreshNotifications({
        variables: getNotificationQueryVariables(adminId),
      });
    };
    runSync();
  };

  const refreshTicketsAndNotifications = async () => {
    await refetchTickets();
    syncDashboardNotifications();
  };
  
  const escapeCSV = (value: string | number | null | undefined) => {
    const safeValue = value == null ? '' : String(value);
    return `"${safeValue.replace(/"/g, '""')}"`;
  };

  const formatTicketTime = (dateString?: string): string => {
    if (!dateString || !/[T\s]\d{1,2}:\d{2}/.test(String(dateString))) return '';
    const parsed = new Date(dateString);
    if (Number.isNaN(parsed.getTime())) return '';
    return parsed.toLocaleTimeString('en-US', {
      hour: '2-digit', minute: '2-digit'
    });
  };

  const getUserDetailCsvClientType = () => {
    const pricingMode = String(effectiveUserRecord?.u_pricing_mode || '').trim().toLowerCase();
    if (pricingMode === 'self-pricing') return 'Self Pricing';
    if (pricingMode === 'concierge') return 'Concierge';
    return '';
  };

  const getUserDetailCsvNotes = (type: 'internal' | 'external') => {
    return notes
      .filter((note) => note.type === type)
      .map((note) => note.content)
      .filter(Boolean)
      .join(' | ');
  };
  const getEventTimeForExport = (ticket: Ticket): string => {
  if (ticket.eventTime && ticket.eventTime.trim()) return ticket.eventTime;
  if (ticket.eventDateRaw && ticket.eventDateRaw.includes('T')) {
    try {
      const parsed = new Date(ticket.eventDateRaw);
      if (!isNaN(parsed.getTime())) {
        return parsed.toLocaleTimeString('en-US', {
          hour: '2-digit',
          minute: '2-digit',
          hour12: true,
        });
      }
    } catch { /* ignore */ }
  }
  return '';
};

 const exportTicketsCsv = (tickets: Ticket[], filterName: string) => {
  if (!tickets || tickets.length === 0) {
    showError('No tickets to export');
    return;
  }

  const normalizedFilter = filterName ? filterName.toLowerCase().replace(/[^a-z0-9]+/g, '_') : 'all';
  const filename = `user_tickets_${normalizedFilter}`;
  const headers = [
    'Ticket ID', 'Event', 'Date', 'Event Time', 'Section', 'Row', 'Seat #', 'Status', 'Price', 'Sold Price',
    'Client Type', 'Internal Notes', 'External Notes'
  ];
  const rows = [headers.map(escapeCSV).join(',')];
  const clientTypeLabel = getUserDetailCsvClientType();
  const internalNotes = getUserDetailCsvNotes('internal');
  const externalNotes = getUserDetailCsvNotes('external');

  tickets.forEach((ticket) => {
    rows.push([
      escapeCSV(ticket.tpId),
      escapeCSV(ticket.event),
      escapeCSV(ticket.date),
      escapeCSV(getEventTimeForExport(ticket)),
      escapeCSV(ticket.section),
      escapeCSV(ticket.row),
      escapeCSV(ticket.seatNo),
      escapeCSV(ticket.status),
      escapeCSV(ticket.price),
      escapeCSV(ticket.soldPrice),
      escapeCSV(clientTypeLabel),
      escapeCSV(internalNotes),
      escapeCSV(externalNotes),
    ].join(','));
  });

    const blob = new Blob([rows.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `${filename}_${new Date().toISOString().slice(0,10)}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };
  
  // Mutations
  const [updateUser, { loading: updateLoading }] = useMutation(UPDATE_USER);
  const [deleteUser, { loading: deleteLoading }] = useMutation(DELETE_USER);
  const [voidUserPayouts, { loading: voidLoading }] = useMutation(VOID_USER_PAYOUTS);
  const [updateEmailStatus, { loading: emailStatusLoading }] = useMutation(UPDATE_USER_EMAIL_STATUS);
  const [restoreUserPayouts, { loading: restoreLoading }] = useMutation(RESTORE_USER_PAYOUTS);
  const [updateSuperUserPercentage] = useMutation(UPDATE_SUPER_USER_PERCENTAGE);
  const [deactivateSuperUser] = useMutation(DEACTIVATE_SUPER_USER);
  const [insertSuperUser] = useMutation(INSERT_SUPER_USER);
  const [addNote, { loading: addNoteLoading }] = useMutation(ADD_USER_NOTE);
  const [updateNote, { loading: updateNoteLoading }] = useMutation(UPDATE_USER_NOTE);
  const [deleteNote, { loading: deleteNoteLoading }] = useMutation(DELETE_USER_NOTE);
  const [editTicketPriceBySuperUser] = useMutation(EDIT_TICKET_PRICE_BY_SUPER_USER);
  const [updateTicketStatus] = useMutation(UPDATE_TICKET_STATUS);
  const [updateTicketPaymentStatus] = useMutation(UPDATE_TICKET_PAYMENT_STATUS);
  const [updateTicketsPaymentStatus] = useMutation(UPDATE_TICKETS_PAYMENT_STATUS);
  const [restoreCancelledListingToVerification] = useMutation(RESTORE_CANCELLED_LISTING_TO_VERIFICATION);
  const [insertUserSavedSeatInfo] = useMutation(INSERT_USER_SAVED_SEAT_INFO);
  const [updateUserSavedSeatInfo] = useMutation(UPDATE_USER_SAVED_SEAT_INFO);
  const [addUserTeam] = useMutation(ADD_USER_PREFERENCE_TEAM);
  const [removeUserTeam] = useMutation(REMOVE_USER_PREFERENCE_TEAM);
  const [adminRemoveUserTeam] = useMutation(ADMIN_REMOVE_USER_PREFERENCE_TEAM);
  
  const [editFormData, setEditFormData] = useState<UserData | null>(null);
  
  useEffect(() => {
    if (effectiveUserRecord) {
      setEditFormData(transformUserData(effectiveUserRecord, superUserData?.super_user?.[0]?.percentage));
    }
  }, [effectiveUserRecord, superUserData]);
  
  useEffect(() => {
    localStorage.setItem('theme', isDarkMode ? 'dark' : 'light');
    document.documentElement.setAttribute('data-theme', isDarkMode ? 'dark' : 'light');
    if (isDarkMode) {
      document.documentElement.classList.add('dark-mode');
      document.body.classList.add('dark-mode');
    } else {
      document.documentElement.classList.remove('dark-mode');
      document.body.classList.remove('dark-mode');
    }
  }, [isDarkMode]);
  
  useEffect(() => {
    const handleClick = () => setOpenStatusDropdown(null);
    if (openStatusDropdown) {
      window.addEventListener('click', handleClick);
      return () => window.removeEventListener('click', handleClick);
    }
  }, [openStatusDropdown]);
  
  useEffect(() => {
    if (Array.isArray(availableTeamsData?.league_categories)) {
      setAvailableTeamsForUser(availableTeamsData.league_categories);
    }
  }, [availableTeamsData]);
  

  // Data Transformations
  const transformUserData = (data: any, superUserPercentage?: number): UserData => {
    const fullName = data.u_full_name || `${data.u_first_name || ''} ${data.u_last_name || ''}`.trim() || 'Unknown User';
    const initials = fullName.split(' ').map((n: string) => n[0]).join('').toUpperCase().substring(0, 2);
    const pricingMode = data.u_pricing_mode;
    const isSelfPricing = pricingMode === 'self-pricing';
    const persistedUserFee = Number(data.u_fee_percentage);
    const superUserFee = Number(superUserPercentage);
    const derivedUserFee = isSelfPricing
      ? (Number.isFinite(superUserFee) && superUserFee > 0 ? superUserFee : (Number.isFinite(persistedUserFee) && persistedUserFee > 0 && persistedUserFee !== 20 ? persistedUserFee : 15))
      : (Number.isFinite(persistedUserFee) && persistedUserFee > 0 ? persistedUserFee : 20);
    return {
      id: data.u_id, name: fullName, initials, type: isSelfPricing ? 'Self Pricing' : 'Concierge',
      tier: data.u_seller_tier || 'No Status', business: data.u_stripe_business_name || '',
      email: data.u_email_id || '', phone: data.u_phone_number || '',
      address1: data.u_address_line1 || '', address2: data.u_address_line2 || '', address3: data.u_address_line3 || 'USA',
      city: data.u_city || '', state: data.u_state || '', zip: data.u_zip || '', country: data.u_country || 'USA',
      lastActive: data.u_last_online ? new Date(data.u_last_online).toLocaleString() : 'N/A',
      annualSales: `${formatCurrency(data.tickets_sold_amount || 0)}/yr`,
      teams: data.teams?.map((team: any) => team.team_name).filter(Boolean) || [],
      assignedTeams: Array.isArray(data.u_teams) ? data.u_teams : [],
      userFee: derivedUserFee.toString(), status: data.u_status || 'active', createdAt: data.u_created_at || '',
      payoutsVoided: data.u_payouts_voided || false, voidReason: data.u_void_reason, voidDate: data.u_void_date,
      emailEnabled: data.u_email_enabled !== false, hearAboutUs: data.u_hear_about_us || undefined
    };
  };

  // ========== TIMING ANALYTICS HELPERS ==========
const parseDateAnalytics = (value?: string | null): Date | null => {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
};

const getDaysBetweenDates = (date1: Date, date2: Date): number => {
  return (date2.getTime() - date1.getTime()) / (1000 * 60 * 60 * 24);
};

const formatCurrencyForAnalytics = (value: number): string => {
  if (!Number.isFinite(value)) return '$0.00';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 }).format(value);
};

  
  const transformTicketsData = (data: any, targetUserId?: string): Ticket[] => {
    if (!data?.tickets) return [];
    return sortUserDetailTickets(data.tickets.map((ticket: any): Ticket => {
      const listPriceValue = pickTicketValue(
        ticket.t_list_price,
        ticket.tp_list_price,
        ticket.list_price,
        ticket.listPrice,
        ticket.price,
        ticket.t_price,
        ticket.tp_price,
      );
      const soldAmount = pickTicketValue(
        ticket.t_sold_price,
        ticket.t_sold_amount,
        ticket.tp_sold_amount,
        ticket.tp_sold_price,
        ticket.sold_price,
        ticket.soldAmount,
        ticket.sold_amount,
        ticket.sale_amount,
        ticket.settlement_amount,
      );

      const marketplaceFields = [
        'tp_marketplace_name',
        't_marketplace_name',
        'marketplace_name',
        'marketplace',
        'mp_name',
      ];
      
      let { isValid: mpIsValid, value: marketplace, source: mpSource } = validateTicketMarketplaceData(ticket, marketplaceFields);
      
      const tpId = ticket.tp_id || ticket.t_id;
      if (!mpIsValid && !marketplace && targetUserId) {
        const cachedMarketplace = restoreMarketplaceFromCache(targetUserId, tpId);
        if (cachedMarketplace) {
          marketplace = cachedMarketplace;
          mpIsValid = true;
          console.log(`✅ RESTORED marketplace from cache for ${tpId}: ${cachedMarketplace}`);
        }
      }
      
      if ((ticket.t_status?.toLowerCase() === 'sold' || ticket.tp_status?.toLowerCase() === 'sold') && !mpIsValid) {
        console.warn('⚠️ PRIMARY DATA - SOLD ticket missing marketplace:', {
          tpId: tpId,
          status: ticket.t_status || ticket.tp_status,
          availableFields: marketplaceFields.map(f => ({ field: f, value: ticket[f] }))
        });
      }

      const ticketRow: Ticket = {
        id: ticket.t_id, 
        tpId: tpId, 
        rawStatus: ticket.t_status || 'Unknown',
        updatedAt: ticket.t_updated_at || undefined,
        event: ticket.event?.e_name || ticket.e_name || 'Unknown Event',
        eventId: ticket.e_id || undefined,
        eventDateRaw: ticket.t_event_date || ticket.event?.e_date || undefined,
        eventTime: formatTicketTime(ticket.t_event_date_time_zone),
        date: ticket.t_event_date ? new Date(ticket.t_event_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'TBD',
        section: ticket.t_section || '-', 
        row: ticket.t_row || '-', 
        seatNo: ticket.t_seat_no != null && String(ticket.t_seat_no).trim() !== '' ? String(ticket.t_seat_no) : '-',
        status: getStatusLabel(ticket.t_status) || ticket.t_status || 'Unknown',
        paymentStatus: ticket.t_payment_status ?? null,
        payoutStatus: ticket.t_payout_status ?? null,
        price: formatListPrice(listPriceValue),
        soldPrice: formatOptionalCurrency(soldAmount),
        marketplace: marketplace,
      };
      const pres = getTicketStatusPresentation(ticketRow);
      return { ...ticketRow, status: pres.label };
    }));
  };

  
  // ========== MARKETPLACE & LEAGUE DATA VALIDATION UTILITIES ==========
const KNOWN_MARKETPLACES = [
  'StubHub', 'Ticketmaster', 'SeatGeek', 'Vivid Seats', 'Gametime', 
  'TickPick', 'Logitix', 'AXS', 'Eventbrite', 'Viagogo', 'Live Nation',
  'StudHub', 'Facebook', 'Craigslist', 'Other'
];

const normalizeMarketplaceName = (raw: string | null | undefined): string | null => {
  if (!raw || typeof raw !== 'string') return null;
  const trimmed = raw.trim();
  if (!trimmed) return null;
  
  const exactMatch = KNOWN_MARKETPLACES.find(m => m.toLowerCase() === trimmed.toLowerCase());
  if (exactMatch) return exactMatch;
  
  const lower = trimmed.toLowerCase();
  if (lower.includes('stub')) return 'StubHub';
  if (lower.includes('ticketmaster')) return 'Ticketmaster';
  if (lower.includes('seatgeek')) return 'SeatGeek';
  if (lower.includes('vivid')) return 'Vivid Seats';
  if (lower.includes('game')) return 'Gametime';
  if (lower.includes('tickpick')) return 'TickPick';
  if (lower.includes('logitix')) return 'Logitix';
  if (lower.includes('axs')) return 'AXS';
  if (lower.includes('eventbrite')) return 'Eventbrite';
  if (lower.includes('viagogo')) return 'Viagogo';
  
  return trimmed.length > 0 ? trimmed : null;
};

const validateTicketMarketplaceData = (ticket: any, fieldNames: string[]): { isValid: boolean; value: string | null; source: string } => {
  for (const field of fieldNames) {
    const value = ticket[field];
    if (value && typeof value === 'string' && value.trim()) {
      const normalized = normalizeMarketplaceName(value);
      if (normalized) {
        return { isValid: true, value: normalized, source: field };
      }
    }
  }
  return { isValid: false, value: null, source: 'none' };
};

const getMarketplaceCache = (userId: string): Record<string, string> => {
  try {
    const cached = localStorage.getItem(`marketplace_cache_${userId}`);
    return cached ? JSON.parse(cached) : {};
  } catch {
    return {};
  }
};

const saveMarketplaceCache = (userId: string, tpId: string, marketplace: string) => {
  try {
    const cache = getMarketplaceCache(userId);
    cache[tpId] = marketplace;
    localStorage.setItem(`marketplace_cache_${userId}`, JSON.stringify(cache));
    console.log(`✅ CACHED marketplace for ${tpId}: ${marketplace}`);
  } catch (err) {
    console.warn('Failed to cache marketplace:', err);
  }
};

const restoreMarketplaceFromCache = (userId: string, tpId: string): string | null => {
  const cache = getMarketplaceCache(userId);
  return cache[tpId] || null;
};

const marketplaceByTicketPlacementId = useMemo(() => {
  const map = new Map<string, string>();
  const rows = Array.isArray(userTicketMarketplacesData?.ticket_placement)
    ? userTicketMarketplacesData.ticket_placement
    : [];

  rows.forEach((row: any) => {
    const tpId = String(row?.tp_id ?? '').trim();
    const marketplace = typeof row?.tp_marketplace_name === 'string' ? row.tp_marketplace_name.trim() : '';
    if (tpId && marketplace && marketplace !== 'Other' && marketplace !== 'Unknown') {
      map.set(tpId, marketplace);
    }
  });

  return map;
}, [userTicketMarketplacesData]);

const applyMarketplaceLookupToTickets = React.useCallback((tickets: Ticket[]): Ticket[] => {
  if (marketplaceByTicketPlacementId.size === 0) return tickets;
  return tickets.map((ticket) => {
    if (ticket.marketplace && ticket.marketplace.trim() && ticket.marketplace !== 'Other' && ticket.marketplace !== 'Unknown') {
      return ticket;
    }
    const marketplace = marketplaceByTicketPlacementId.get(ticket.tpId);
    return marketplace ? { ...ticket, marketplace } : ticket;
  });
}, [marketplaceByTicketPlacementId]);

const transformFallbackManageTicketsData = (data: any, targetUserId?: string): Ticket[] => {
    if (!data?.filtermanagetickets || !targetUserId) return [];
    return sortUserDetailTickets(
      data.filtermanagetickets.filter((ticket: any) => ticket.u_id === targetUserId).map((ticket: any): Ticket => {
      const listPriceValue = pickTicketValue(
        ticket.tp_list_price,
        ticket.t_list_price,
        ticket.list_price,
        ticket.listPrice,
        ticket.price,
        ticket.tp_price,
        ticket.t_price,
      );
      const soldAmount = pickTicketValue(
        ticket.tp_sold_amount,
        ticket.tp_sold_price,
        ticket.t_sold_amount,
        ticket.t_sold_price,
        ticket.sold_amount,
        ticket.soldAmount,
        ticket.sale_amount,
        ticket.settlement_amount,
      );

      const marketplaceFields = [
        'tp_marketplace_name',
        't_marketplace_name',
        'marketplace_name',
        'marketplace',
        'mp_name',
        'vl_marketplace_name',
      ];
      
      let { isValid: mpIsValid, value: marketplace, source: mpSource } = validateTicketMarketplaceData(ticket, marketplaceFields);
      
      const tpId = ticket.tp_id;
      if (!mpIsValid && !marketplace && targetUserId) {
        const cachedMarketplace = restoreMarketplaceFromCache(targetUserId, tpId);
        if (cachedMarketplace) {
          marketplace = cachedMarketplace;
          mpIsValid = true;
          console.log(`✅ RESTORED marketplace from cache for ${tpId}: ${cachedMarketplace}`);
        }
      }
      
      if (ticket.tp_status?.toLowerCase() === 'sold' && !mpIsValid) {
        console.warn('⚠️ SOLD ticket missing marketplace:', {
          tpId: tpId,
          status: ticket.tp_status,
          availableFields: marketplaceFields.map(f => ({ field: f, value: ticket[f] }))
        });
      }

      const ticketRow: Ticket = {
        id: ticket.t_id, 
        tpId: ticket.tp_id || ticket.t_id, 
        rawStatus: ticket.tp_status || 'Unknown',
        updatedAt: ticket.tp_updated_at || undefined,
        event: ticket.e_name || 'Unknown Event',
        eventId: ticket.e_id || undefined,
        eventDateRaw: ticket.e_date || undefined,
        eventTime: formatTicketTime(ticket.e_date_time_zone),
        date: ticket.e_date ? new Date(ticket.e_date).toLocaleDateString() : 'TBD',
        section: ticket.tp_section || '-', 
        row: ticket.tp_row || '-', 
        seatNo: ticket.tp_seat_no != null && String(ticket.tp_seat_no).trim() !== '' ? String(ticket.tp_seat_no) : '-',
        status: getStatusLabel(ticket.tp_status) || ticket.tp_status || 'Unknown',
        paymentStatus: ticket.tp_payment_status ?? null,
        payoutStatus: ticket.tp_payout_status ?? null,
        price: formatListPrice(listPriceValue),
        soldPrice: formatOptionalCurrency(soldAmount),
        marketplace: marketplace,
      };
      const pres = getTicketStatusPresentation(ticketRow);
      return { ...ticketRow, status: pres.label };
    }),
    );
  };
  
  const transformNotesData = (data: any[]): Note[] => {
    if (!data) return [];
    return data.map((note: any) => ({
      id: note.n_id, type: note.n_type, content: note.n_content,
      createdAt: note.n_created_at, createdBy: note.n_created_by, updatedAt: note.n_updated_at
    }));
  };
  
  const transformPerformanceData = (data: any): PerformanceMetrics => {
    const perf = data?.user_performance?.[0] || {};
    return {
      active: perf.up_active_count || 0, potential: perf.up_potential_revenue ? `$${perf.up_potential_revenue.toLocaleString()}` : '$0',
      sold: perf.up_sold_count || 0, revenue: perf.up_total_revenue ? `$${perf.up_total_revenue.toLocaleString()}` : '$0',
      paidOut: perf.up_paid_out ? `$${perf.up_paid_out.toLocaleString()}` : '$0', feeRate: perf.up_fee_rate ? `${perf.up_fee_rate}%` : '0%',
      phone: perf.up_phone_verified || 0, verified: perf.up_verified_count || 0, listed: perf.up_listed_count || 0,
      ticketsSold: perf.up_sold_count || 0, delisted: perf.up_delisted_count || 0, unsold: perf.up_unsold_count || 0,
      neverTransferred: perf.up_never_transferred || 0
    };
  };
  
  // Handlers
  const handleUserClick = () => navigate(`/users`);
  const handleRefresh = () => window.location.reload();
  const handleSearch = (query: string) => setSearchQuery(query);
  
  const handleEditClick = () => {
    if (effectiveUserRecord) {
      setEditFormData(transformUserData(effectiveUserRecord, superUserData?.super_user?.[0]?.percentage));
      setIsEditing(true);
    }
  };
  const handleCancelEdit = () => { setIsEditing(false); setEditFormData(null); };

  const isDuplicateEmailUpdateError = (error: any): boolean => {
    const graphQlMessage = error?.graphQLErrors?.[0]?.message || '';
    const message = (error?.message || graphQlMessage || '').toLowerCase();
    return (
      message.includes('user_u_email_id_key') ||
      message.includes('duplicate key value violates unique constraint') ||
      message.includes('duplicate key') ||
      message.includes('already exists')
    );
  };
  

  
  const handleSaveEdit = async () => {
    if (!editFormData || !userId) return;
    try {
      if (!canEditPricing) {
        const currentPricingMode = String(effectiveUserRecord?.u_pricing_mode || '').trim().toLowerCase();
        const originalType = currentPricingMode === 'self-pricing' ? 'Self Pricing' : 'Concierge';
        const originalPercentage = Number(effectiveUserRecord?.u_fee_percentage);
        const hasPricingTypeChanged = editFormData.type !== originalType;
        const hasPercentageChanged = Number.isFinite(originalPercentage)
          ? Number(editFormData.userFee) !== originalPercentage
          : Number(editFormData.userFee) !== (originalType === 'Self Pricing' ? 15 : 20);
        if (hasPricingTypeChanged || hasPercentageChanged) {
          showError('Only admin, sub-admin, or sales rep can update pricing mode or percentage.');
          return;
        }
      }
      const isSelfPricing = editFormData.type === 'Self Pricing';
      const feePercentage = Number(editFormData.userFee);
      if (!Number.isFinite(feePercentage) || feePercentage <= 0 || feePercentage > 100) {
        showError('Percentage must be greater than 0 and at most 100.');
        return;
      }
      await updateUser({
        variables: {
          userId: userId,
          input: {
            u_email_id: editFormData.email,
            u_phone_number: editFormData.phone || null,
            u_stripe_business_name: editFormData.business || null,
            u_pricing_mode: editFormData.type === 'Self Pricing' ? 'self-pricing' : 'concierge',
            u_fee_percentage: feePercentage,
          }
        }
      });
      const existingSuperUser = superUserData?.super_user?.[0];
      if (isSelfPricing) {
        if (existingSuperUser?.su_id) {
          await updateSuperUserPercentage({ variables: { su_id: existingSuperUser.su_id, percentage: feePercentage } });
        } else {
          const rawUser = effectiveUserRecord;
          const firstName = rawUser?.u_first_name || '';
          const lastName = rawUser?.u_last_name || '';
          const emailId = rawUser?.u_email_id || editFormData.email;
          if (!emailId || !firstName || !lastName) {
            showError('Missing user details required to create super user record.');
            return;
          }
          await insertSuperUser({ variables: { u_id: userId, su_email_id: emailId, su_first_name: firstName, su_last_name: lastName, percentage: feePercentage } });
        }
      } else if (existingSuperUser?.su_id) {
        await deactivateSuperUser({ variables: { su_id: existingSuperUser.su_id } });
      }
      await Promise.all([refetchUser(), refetchSuperUser()]);
      setIsEditing(false);
      setEditFormData(null);
      showSuccess('User updated successfully!');
    } catch (error: any) {
      if (isDuplicateEmailUpdateError(error)) {
        showError('This email already belongs to another user.');
        return;
      }
      const gqlMsgs = Array.isArray(error?.graphQLErrors)
        ? error.graphQLErrors.map((e: { message?: string }) => e.message).filter(Boolean) : [];
      const detail = gqlMsgs.length ? gqlMsgs.join(' ') : error?.message;
      showError(detail?.trim() || 'Failed to update user. Please try again.');
    }
  };
  
  const handleDeleteUser = async () => {
    if (!userId) return;
    try {
      await updateUser({ variables: { userId: userId, input: { u_delete_requested: true } } });
      showSuccess('User deleted successfully!');
      navigate('/users');
    } catch (error: any) {
      showError(`Error: ${error?.message || 'Failed to delete user'}`);
    }
  };
  
  const handleVoidPayouts = async () => {
    if (!userId) return;
    setIsVoidingPayouts(true);
    try {
      await voidUserPayouts({
        variables: {
          userId: userId,
          reason: voidReason || null,
          date: new Date().toISOString()
        }
      });
      if (addNoteOnVoid && voidPayoutNote && voidPayoutNote.trim()) {
        try {
          await addNote({ variables: { userId: userId, type: 'internal', content: `Void Payouts: ${voidReason || ''}\n\n${voidPayoutNote.trim()}` } });
          await refetchNotes();
        } catch (e) { console.error(e); }
      }
      await refetchUser();
      showSuccess('Payouts voided successfully');
      setShowVoidPayoutsModal(false);
      setVoidReason('');
      setVoidPayoutNote('');
      setAddNoteOnVoid(false);
    } catch (error) {
      showError('Failed to void payouts');
    } finally {
      setIsVoidingPayouts(false);
    }
  };
  
  const handleRestorePayouts = async () => {
    if (!userId) return;
    try {
      await restoreUserPayouts({ variables: { userId: userId } });
      await refetchUser();
      showSuccess('Payouts restored successfully');
    } catch (error) {
      showError('Failed to restore payouts');
    }
  };
  
  const handleToggleEmail = async () => {
    if (!userId) return;
    const currentEmailEnabled = (emailStatusData?.user?.[0]?.u_email_enabled) ?? (currentUser?.emailEnabled ?? true);
    try {
      await updateEmailStatus({ variables: { userId: userId, u_email_enabled: !currentEmailEnabled } });
      await refetchUser();
      showSuccess(`${!currentEmailEnabled ? 'Enabled' : 'Disabled'} emails successfully`);
    } catch (error) {
      showError('Failed to update email status');
    }
  };
  
  const handleAddNote = async (type: 'qa' | 'internal' | 'external') => {
    const content = newNoteContentByType[type]?.trim() || '';
    if (!userId || !content) {
      showError('Please enter note content');
      return;
    }
    try {
      const result = await addNote({ variables: { userId: userId, type, content } });
      const inserted = result?.data?.insert_user_notes_one;
      if (inserted) {
        setNotes((prev) => [{
          id: inserted.n_id,
          type: inserted.n_type,
          content: inserted.n_content,
          createdAt: inserted.n_created_at || new Date().toISOString(),
          createdBy: inserted.n_created_by || 'admin',
          updatedAt: inserted.n_updated_at || inserted.n_created_at || new Date().toISOString(),
        }, ...prev]);
      }
      await refetchNotes();
      setNewNoteContentByType((prev) => ({ ...prev, [type]: '' }));
      if (type === 'qa') setShowQANote(false);
      if (type === 'internal') setShowInternalNote(false);
      if (type === 'external') setShowExternalNote(false);
      showSuccess('Note added successfully');
    } catch (error: any) {
      const detail = Array.isArray(error?.graphQLErrors)
        ? error.graphQLErrors.map((e: any) => e.message).join(' ')
        : error?.message;
      showError(detail || 'Failed to add note');
    }
  };
  
  const handleUpdateNote = async () => {
    if (!editingNote || !editingNote.content.trim()) {
      showError('Please enter note content');
      return;
    }
    try {
      await updateNote({ variables: { noteId: editingNote.id, content: editingNote.content.trim(), updatedAt: new Date().toISOString() } });
      await refetchNotes();
      setEditingNote(null);
      showSuccess('Note updated successfully');
    } catch (error: any) {
      const detail = Array.isArray(error?.graphQLErrors)
        ? error.graphQLErrors.map((e: any) => e.message).join(' ')
        : error?.message;
      showError(detail || 'Failed to update note');
    }
  };
  
  const handleDeleteNote = async (noteId: string) => {
   
      try {
        await deleteNote({ variables: { noteId: noteId } });
        setNotes((prev) => prev.filter((note) => note.id !== noteId));
        await refetchNotes();
        showSuccess('Note deleted successfully');
      } catch (error: any) {
        const detail = Array.isArray(error?.graphQLErrors)
          ? error.graphQLErrors.map((e: any) => e.message).join(' ')
          : error?.message;
        showError(detail || 'Failed to delete note');
      }
    
  };
  
  // Team Management Handlers
  const handleOpenAddTeamModal = () => {
    setShowAddTeamModal(true);
    setSelectedLeagueFilter('all');
    setTeamSearchQuery('');
    refetchUserPreferences().catch((error) => {
      console.error('Failed to refetch user preferences:', error);
    });
  };
  
  const handleAddTeam = async (teamId: string) => {
    if (!userId) {
      showError('User ID is required');
      return;
    }
    setAddingTeamId(teamId);
    try {
      await addUserTeam({ variables: { userId, teamId } });
      showSuccess('Team added successfully!');
      await Promise.all([
        refetchUser(),
        refetchUserPreferences(),
        refetchTeamsWithSoldStatus(),
      ]);
      setShowAddTeamModal(false);
    } catch (error: any) {
      const detail = Array.isArray(error?.graphQLErrors)
        ? error.graphQLErrors.map((e: any) => e.message).join(' ')
        : error?.message;
      showError(detail || 'Failed to add team');
    } finally {
      setAddingTeamId(null);
    }
  };
  
  const handleRemoveTeam = async (teamId: string) => {
    if (!userId) {
      showError('User ID is required');
      return;
    }
    
    if (teamsWithSoldStatus[teamId]) {
      showError('Cannot remove team with sold tickets');
      return;
    }
    
    setRemovingTeamId(teamId);
    try {
      await removeUserTeam({ variables: { userId, teamId } });
      showSuccess('Team removed successfully!');
      console.log('🗑️ Team deleted. Refetching user preferences immediately...');
      await refetchUserPreferences();
      await Promise.all([
        refetchUser(),
        refetchTeamsWithSoldStatus(),
      ]);
    } catch (error: any) {
      const detail = Array.isArray(error?.graphQLErrors)
        ? error.graphQLErrors.map((e: any) => e.message).join(' ')
        : error?.message;
      showError(detail || 'Failed to remove team');
    } finally {
      setRemovingTeamId(null);
    }
  };
  
  const handleAdminRemoveTeam = async (teamId: string) => {
    if (!userId) {
      showError('User ID is required');
      return;
    }
    
   const team = selectedTeams.find(t => t.id === teamId);
  if (team && hasSoldTicketsForTeam(teamId, team.name, team.abbreviation)) {
    showError('Cannot remove team with sold tickets. User has active sales for this team.');
    return;
  }
    setAdminRemovingTeamId(teamId);
    try {
      await adminRemoveUserTeam({ variables: { userId, teamId } });
      showSuccess('Team removed from user successfully!');
      console.log('🗑️ Team deleted. Refetching user preferences immediately...');
      await refetchUserPreferences();
      console.log('✅ User preferences refetched. UI should update now.');
      await Promise.all([
        refetchUser(),
        refetchTeamsWithSoldStatus(),
      ]);
    } catch (error: any) {
      const detail = Array.isArray(error?.graphQLErrors)
        ? error.graphQLErrors.map((e: any) => e.message).join(' ')
        : error?.message;
      showError(detail || 'Failed to remove team');
    } finally {
      setAdminRemovingTeamId(null);
    }
  };
  
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    if (!editFormData) return;
    setEditFormData(prev => {
      if (!prev) return prev;
      if (name === 'type') {
        const isSwitchingToSelfPricing = value === 'Self Pricing';
        const shouldResetToSelfPricingDefault = isSwitchingToSelfPricing && (!prev.userFee || Number(prev.userFee) === 20);
        return { ...prev, [name]: value, ...(shouldResetToSelfPricingDefault ? { userFee: '15' } : {}) };
      }
      return { ...prev, [name]: value };
    });
  };
  
  const matchesTicketSearchQuery = (ticket: Ticket, query: string) => {
    const token = query.trim().toLowerCase();
    if (!token) return true;
    const haystack = [
      ticket.event,
      ticket.date,
      ticket.section,
      ticket.row,
      ticket.seatNo,
      ticket.status,
      ticket.price,
      ticket.soldPrice,
    ]
      .filter((v) => v != null && String(v).trim() !== '' && String(v) !== '-')
      .join(' ')
      .toLowerCase();
    return haystack.includes(token);
  };

  const getFilteredTickets = () => {
    return sortUserDetailTickets(
      localTickets.filter(
        (ticket) =>
          matchesFilterTab(ticket, activeFilterTab, soldSubFilter) &&
          matchesTicketSearchQuery(ticket, searchQuery),
      ),
    );
  };

  useEffect(() => {
    setBulkSelectedTicketIds(new Set());
    setBulkSelectedTickets([]);
  }, [activeFilterTab, searchQuery]);

  useEffect(() => {
    if (activeFilterTab !== 'Sold') {
      setSoldSubFilter('all');
    }
  }, [activeFilterTab]);

  const getFilteredTeams = () => {
    if (!teamSearchQuery.trim()) return selectedTeams;
    const token = teamSearchQuery.toLowerCase();
    return selectedTeams.filter(
      (team) =>
        team.name.toLowerCase().includes(token) ||
        team.abbreviation.toLowerCase().includes(token)
    );
  };
  


  
  const getTabCount = (tab: string, soldFilter: typeof soldSubFilter = 'all') => {
    return localTickets.filter(
      (ticket) =>
        matchesFilterTab(ticket, tab, soldFilter) && matchesTicketSearchQuery(ticket, searchQuery),
    ).length;
  };

  const getSoldSubFilterCounts = () => {
    const soldTickets = localTickets.filter(
      (t) => matchesFilterTab(t, 'Sold', 'all') && matchesTicketSearchQuery(t, searchQuery),
    );
    return {
      all: soldTickets.length,
      sold: soldTickets.filter((t) => getSoldPresentationFilterKey(t) === 'sold').length,
      settled: soldTickets.filter((t) => getSoldPresentationFilterKey(t) === 'settled').length,
      settlementinprogress: soldTickets.filter((t) => getSoldPresentationFilterKey(t) === 'settlementinprogress').length,
    };
  };
  
  const matchesFilterTab = (ticket: Ticket, tab: string, soldFilter: typeof soldSubFilter = 'all') => {
    if (tab === 'All') return true;
    const rawToken = normalizeStatusToken(ticket.rawStatus || ticket.status);
    if (tab === 'Verification') return ['tobeverified', 'verified', 'verification', 'invalid'].includes(rawToken);
    if (tab === 'Listed') return ['list', 'listed'].includes(rawToken);
    if (tab === 'Sold') {
      if (rawToken !== 'sold') return false;
      if (soldFilter === 'all') return true;
      return getSoldPresentationFilterKey(ticket) === soldFilter;
    }
    if (tab === 'Delisted') {
      return ['delist', 'delisted', 'delistinprogress', 'delistrequested', 'return'].includes(rawToken);
    }
    if (tab === 'Listing Request Cancelled') {
      return rawToken === 'listingrequestcancelled';
    }
    if (tab === 'Unsold') return rawToken === 'unsold';
    if (tab === 'Expired') return rawToken === 'closed' || rawToken === 'listingrequestexpired';
    return true;
  };
  
  const isListedStatus = (status: string) => {
    const s = status.toLowerCase();
    return s === 'listed' || s === 'list';
  };

  const isSoldStatus = (ticket: Ticket) =>
    normalizeStatusToken(ticket.rawStatus || ticket.status) === 'sold';

  const getTicketTransitionStatus = (ticket: Ticket): string => {
    const baseStatus = mapStatusForMutation(ticket.rawStatus || ticket.status);
    if (baseStatus !== 'Sold') return baseStatus;
    const presentationKey = getTicketStatusPresentation(ticket).iconStatus.toLowerCase();
    if (presentationKey === 'settled') return 'Settled';
    if (presentationKey === 'settlementinprogress') return 'SettlementInProgress';
    return 'Sold';
  };

  const getPaymentStatusForSettlementOverride = (status: string): string | null | undefined => {
    const normalized = mapStatusForMutation(status);
    if (normalized === 'SettlementInProgress') return 'Inprogress';
    if (normalized === 'Settled') return 'Success';
    if (normalized === 'Sold') return null;
    return undefined;
  };
  
  const updateLocalTicket = (tpIds: string[], updates: Partial<Ticket>) => {
    const touchedAt = new Date().toISOString();
    setLocalTickets((prev) =>
      sortUserDetailTickets(
        prev.map((t) =>
          tpIds.includes(t.tpId)
            ? { ...t, ...updates, updatedAt: updates.updatedAt ?? touchedAt }
            : t,
        ),
      ),
    );
  };

  const getDefaultDelistEmail = (): string =>
    (effectiveUserRecord?.u_email_id || editFormData?.email || '').trim();

  const openDelistEmailModal = (tpIds: string[]) => {
    const defaultEmail = getDefaultDelistEmail();
    setPendingDelistTpIds(tpIds);
    setDelistEmail(defaultEmail);
    setDelistConfirmEmail(defaultEmail);
    setShowDelistEmailModal(true);
  };

  const normalizeStatusValue = (status: string) => mapStatusForMutation(status);

  const getTicketListPrice = (ticket: Ticket) => parseCurrency(ticket.price);

  const getTicketSoldPrice = (ticket: Ticket) => parseCurrency(ticket.soldPrice);

  const getTicketDisplayPrice = (ticket: Ticket): number | null => {
    const soldPrice = getTicketSoldPrice(ticket);
    if (soldPrice > 0) return soldPrice;
    const listPrice = getTicketListPrice(ticket);
    if (listPrice > 0) return listPrice;
    return null;
  };

  const canBulkUpdateTickets = (ticketIds: string[]): { allowed: boolean; reason?: string } => {
    if (ticketIds.length === 0) return { allowed: false, reason: 'No tickets selected' };
    const selected = getFilteredTickets().filter((t) => ticketIds.includes(t.tpId));
    const groupKey = (ticket: Ticket) =>
      `${ticket.eventId || ticket.event}|${(ticket.section || '').toLowerCase().trim()}|${(ticket.row || '').toLowerCase().trim()}`;
    const groups = new Map<string, Ticket[]>();
    for (const ticket of selected) {
      const key = groupKey(ticket);
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key)!.push(ticket);
    }
    if (groups.size > 1) {
      return {
        allowed: false,
        reason: `Tickets must be from the same event, section, and row. Found ${groups.size} different groups.`,
      };
    }
    return { allowed: true };
  };

const getBulkStatusTransitions = (tickets: Ticket[]): { value: string; label: string }[] => {
  if (!tickets.length) return [];
  const allTransitions = new Map<string, { value: string; label: string }>();
  for (const ticket of tickets) {
    const transitions = getStatusTransitions(getTicketTransitionStatus(ticket));
    for (const trans of transitions) {
      if (!allTransitions.has(trans.value)) allTransitions.set(trans.value, trans);
    }
  }
  const result: { value: string; label: string }[] = [];
  for (const [value, trans] of allTransitions) {
    const isCommon = tickets.every((ticket) => {
      const ticketTransitions = getStatusTransitions(getTicketTransitionStatus(ticket));
      return ticketTransitions.some((t) => t.value === value);
    });
    if (isCommon) result.push(trans);
  }
  return result;
};

  const getDefaultBulkStatusListingPrices = (tickets: Ticket[]): Record<string, string> =>
    Object.fromEntries(tickets.map((t) => [t.tpId, getTicketListPrice(t) > 0 ? String(getTicketListPrice(t)) : '']));

  const areAllBulkStatusListingPricesValid = (tickets: Ticket[], prices: Record<string, string>): boolean =>
    tickets.every((ticket) => {
      const value = String(prices[ticket.tpId] || '').trim();
      const num = parseFloat(value);
      return !!value && !isNaN(num) && num > 0;
    });

  const resetBulkSoldState = () => {
    setBulkSoldMarketplaceId('');
    setBulkSoldLogitixAmounts({});
    setBulkSoldCommonLogitixAmount('');
    setBulkSoldFeeConfigReady(false);
    setBulkSoldFeeConfigByTicketId({});
    setBulkSoldLoading(false);
  };

  const closeBulkStatusModal = () => {
    setShowBulkStatusModal(false);
    setBulkNewStatus('');
    setBulkStatusListingPrices({});
    setBulkStatusListingCommonPrice('');
    resetBulkSoldState();
  };

  const resolveSoldFeeConfigForUser = async (): Promise<SoldFeeConfig> => {
    const pricingMode = effectiveUserRecord?.u_pricing_mode;
    const profileFee = Number(effectiveUserRecord?.u_fee_percentage ?? currentUser?.userFee);
    const isSelfPricing =
      pricingMode === 'self-pricing' ||
      (pricingMode !== 'concierge' && currentUser?.type === 'Self Pricing');

    if (isSelfPricing && userId) {
      try {
        const { data } = await getSuperUserForSold({ variables: { userId } });
        const superUserPercent = Number(data?.super_user?.[0]?.percentage);
        const percent = Number.isFinite(superUserPercent) && superUserPercent > 0
          ? superUserPercent
          : Number.isFinite(profileFee) && profileFee > 0
            ? profileFee
            : 15;
        return { feeMode: 'super', superClientPercent: percent };
      } catch (err) {
        console.error('Failed to fetch super user percent for sold:', err);
        const percent = Number.isFinite(profileFee) && profileFee > 0 ? profileFee : 15;
        return { feeMode: 'super', superClientPercent: percent };
      }
    }

    if (userId) {
      try {
        const { data: userFeeData } = await getUserFeePercentById({ variables: { u_id: userId } });
        const conciergePercent = Number(userFeeData?.user_by_pk?.u_fee_percentage);
        if (Number.isFinite(conciergePercent) && conciergePercent > 0) {
          return { feeMode: 'super', superClientPercent: conciergePercent };
        }
      } catch (err) {
        console.error('Failed to fetch user fee percent for sold:', err);
      }
    }

    if (Number.isFinite(profileFee) && profileFee > 0) {
      return { feeMode: 'super', superClientPercent: profileFee };
    }

    return DEFAULT_SOLD_FEE_CONFIG;
  };

  const initBulkSoldFeeConfig = async (tickets: Ticket[]) => {
    try {
      await refetchMarketplaces();
    } catch {
      /* use cached marketplaces */
    }
    setBulkSoldLogitixAmounts(Object.fromEntries(tickets.map((t) => [t.tpId, ''])));
    setBulkSoldCommonLogitixAmount('');
    setBulkSoldFeeConfigReady(false);
    const feeConfig = await resolveSoldFeeConfigForUser();
    setBulkSoldFeeConfigByTicketId(Object.fromEntries(tickets.map((t) => [t.tpId, feeConfig])));
    setBulkSoldFeeConfigReady(true);
  };

  const getBulkTicketSoldFeeConfig = (ticketId: string): SoldFeeConfig =>
    bulkSoldFeeConfigByTicketId[ticketId] ?? DEFAULT_SOLD_FEE_CONFIG;

  const isBulkSoldTransitionReady = (
    tickets: Ticket[],
    logitixAmounts: Record<string, string>,
    marketplaceId: string,
    feeConfigReady: boolean,
  ): boolean => {
    if (!feeConfigReady || !marketplaceId) return false;
    const mp = marketplacesList.find((m) => m.id === marketplaceId);
    if (!mp) return false;
    return tickets.every((ticket) => {
      const listPrice = getTicketListPrice(ticket);
      const rawValue = logitixAmounts[ticket.tpId];
      const value = String(rawValue ?? '').trim();
      const logitixValue = value
        ? Number(parseFloat(value))
        : Math.round(listPrice * (100 - mp.vl_fee_percentage)) / 100;
      if (!Number.isFinite(logitixValue) || logitixValue <= 0) return false;
      const feeConfig = getBulkTicketSoldFeeConfig(ticket.tpId);
      const quickPct = effectiveSoldQuickCutPercent(mp.vl_fee_percentage, feeConfig.feeMode, feeConfig.superClientPercent);
      const quickCutDollars = Number(roundToFixed(listPrice * (quickPct / 100), 2));
      const settlement = Math.round((logitixValue - quickCutDollars) * 100) / 100;
      return settlement > 0;
    });
  };

  const handleApplyBulkStatusListingPrice = () => {
    const value = String(bulkStatusListingCommonPrice).trim();
    const priceNum = parseFloat(value);
    if (!value || isNaN(priceNum) || priceNum <= 0) {
      showError('Enter a valid price to apply to all tickets');
      return;
    }
    setBulkStatusListingPrices(
      Object.fromEntries(bulkSelectedTickets.map((ticket) => [ticket.tpId, String(priceNum)])),
    );
  };

  const handleApplyBulkSoldAmount = () => {
    const value = String(bulkSoldCommonLogitixAmount).trim();
    const num = parseFloat(value);
    if (!value || isNaN(num) || num <= 0) {
      showError('Enter a valid marketplace amount to apply to all tickets');
      return;
    }
    setBulkSoldLogitixAmounts(
      Object.fromEntries(bulkSelectedTickets.map((ticket) => [ticket.tpId, String(num)])),
    );
  };

  const handleBulkNewStatusSelect = (statusValue: string) => {
    if (
      normalizeStatusValue(statusValue) === 'List' &&
      bulkSelectedTickets.length === 1 &&
      normalizeStatusValue(bulkSelectedTickets[0].rawStatus || bulkSelectedTickets[0].status) === 'Verified'
    ) {
      const ticket = bulkSelectedTickets[0];
      closeBulkStatusModal();
      setListedModalTicket(ticket);
      setListedPrice(getTicketListPrice(ticket) > 0 ? String(getTicketListPrice(ticket)) : '');
      setShowListedModal(true);
      return;
    }
    setBulkNewStatus(statusValue);
    if (normalizeStatusValue(statusValue) === 'List') {
      setBulkStatusListingPrices(getDefaultBulkStatusListingPrices(bulkSelectedTickets));
      setBulkStatusListingCommonPrice('');
      resetBulkSoldState();
    } else if (normalizeStatusValue(statusValue) === 'Sold') {
      setBulkStatusListingPrices({});
      setBulkStatusListingCommonPrice('');
      void initBulkSoldFeeConfig(bulkSelectedTickets);
    } else {
      setBulkStatusListingPrices({});
      setBulkStatusListingCommonPrice('');
      resetBulkSoldState();
    }
  };

  const clearBulkSelection = () => {
    setBulkSelectedTicketIds(new Set());
    setBulkSelectedTickets([]);
  };

  const handleBulkSelectTicket = (ticketId: string, checked: boolean) => {
    setBulkSelectedTicketIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(ticketId);
      else next.delete(ticketId);
      return next;
    });
  };

  const handleBulkSelectAllPage = (checked: boolean) => {
    if (checked) {
      setBulkSelectedTicketIds(new Set(getFilteredTickets().map((t) => t.tpId)));
    } else {
      clearBulkSelection();
    }
  };

  const openBulkStatusModal = () => {
    const selected = getFilteredTickets().filter((t) => bulkSelectedTicketIds.has(t.tpId));
    const validation = canBulkUpdateTickets(Array.from(bulkSelectedTicketIds));
    if (!validation.allowed) {
      showError(validation.reason || 'Cannot perform bulk action on selected tickets');
      return;
    }
    setBulkSelectedTickets(selected);
    setShowBulkStatusModal(true);
    setBulkNewStatus('');
    setBulkStatusListingPrices({});
    setBulkStatusListingCommonPrice('');
  };

  const handleBulkSoldConfirm = async () => {
    if (bulkSoldLoading || !bulkSelectedTickets.length) return;
    if (!bulkSoldMarketplaceId) {
      showError('Please select a marketplace.');
      return;
    }
    const mp = marketplacesList.find((m) => m.id === bulkSoldMarketplaceId);
    if (!mp) {
      showError('Selected marketplace not found.');
      return;
    }
    setBulkSoldLoading(true);
    try {
      const results = await Promise.allSettled(
        bulkSelectedTickets.map((ticket) => {
          const listPrice = getTicketListPrice(ticket);
          const rawValue = bulkSoldLogitixAmounts[ticket.tpId];
          const value = String(rawValue ?? '').trim();
          const logitixValue = value
            ? Number(parseFloat(value))
            : Math.round(listPrice * (100 - mp.vl_fee_percentage)) / 100;
          const feeConfig = getBulkTicketSoldFeeConfig(ticket.tpId);
          const quickPct = effectiveSoldQuickCutPercent(mp.vl_fee_percentage, feeConfig.feeMode, feeConfig.superClientPercent);
          const quickCutDollars = Number(roundToFixed(logitixValue * (quickPct / 100), 2));
          const settlement = Math.round((logitixValue - quickCutDollars) * 100) / 100;
          return callMarkAsSoldRest(ticket.tpId, settlement, logitixValue, mp.name).then(() => ticket.tpId);
        }),
      );
      const successIds = results
        .filter((r): r is PromiseFulfilledResult<string> => r.status === 'fulfilled')
        .map((r) => r.value);
      const failedCount = results.filter((r) => r.status === 'rejected').length;
      if (successIds.length > 0) {
        updateLocalTicket(
          successIds,
          { rawStatus: 'Sold', status: 'Sold', marketplace: mp.name },
        );
        showSuccess(`${successIds.length} ticket(s) marked as sold${failedCount ? `, ${failedCount} failed` : ''}`);
        setActiveFilterTab('Sold');
      }
      if (failedCount > 0 && successIds.length === 0) showError('Failed to mark selected tickets as sold');
      clearBulkSelection();
      closeBulkStatusModal();
      console.log('🔄 Refetching tickets and team sold status after bulk marking as sold...');
      await Promise.all([
        refetchTickets(),
        refetchTeamsWithSoldStatus(),
      ]);
      syncDashboardNotifications();
      console.log('✅ Refetch completed - delete button should now update for sold teams');
    } catch (err: any) {
      showError(err?.message || 'Failed to mark selected tickets as sold');
    } finally {
      setBulkSoldLoading(false);
    }
  };

const handleBulkStatusChange = async () => {
  if (!bulkSelectedTickets.length || !bulkNewStatus || bulkStatusChangeLoading) return;
  const targetStatus = normalizeStatusValue(bulkNewStatus);
  const tpIds = bulkSelectedTickets.map((t) => t.tpId);
  setBulkStatusChangeLoading(true);

  try {
    if (targetStatus === 'Verified') {
      const invalidStatus = bulkSelectedTickets.some(
        (t) => normalizeStatusValue(t.rawStatus || t.status) !== 'ToBeVerified',
      );
      if (invalidStatus) {
        showError('Only tickets with "To Be Verified" status can be verified in bulk.');
        return;
      }
      const missingFields = bulkSelectedTickets.some((t) => isMissingVerifyRequiredFields(t));
      if (missingFields) {
        showError('One or more selected tickets are missing required fields (section, row, seat, or price).');
        return;
      }
      const restData = await callUpdateTicketStatusRest(tpIds, true, false);
      const message = String(restData?.message || '').trim();
      const normalized = message.toLowerCase();
      const isSuccess =
        normalized.includes('success') &&
        !normalized.includes('0 ticket updated successfully') &&
        !normalized.includes('0 tickets updated successfully') &&
        !normalized.includes('0 ticket updated') &&
        !normalized.includes('not found') &&
        !normalized.includes('failed') &&
        !normalized.includes('error');
      if (isSuccess) {
        showSuccess(message || `Successfully verified ${tpIds.length} ticket(s).`);
        updateLocalTicket(tpIds, { rawStatus: 'Verified', status: 'Verified' });
        setActiveFilterTab('Verification');
        clearBulkSelection();
        closeBulkStatusModal();
      } else {
        showError(message || 'Bulk verification failed');
      }
    } else if (targetStatus === 'List') {
      const undoToListTickets = bulkSelectedTickets.filter((ticket) => {
        const status = normalizeStatusValue(ticket.rawStatus || ticket.status);
        return status === 'Delist' || status === 'DelistInProgress' || status === 'DelistRequested';
      });
      const publishToListTickets = bulkSelectedTickets.filter(
        (ticket) => normalizeStatusValue(ticket.rawStatus || ticket.status) === 'Verified',
      );
      const unsupportedToList = bulkSelectedTickets.filter(
        (ticket) => !undoToListTickets.includes(ticket) && !publishToListTickets.includes(ticket),
      );

      if (unsupportedToList.length > 0) {
        showError(
          'Only Verified tickets (publish) or Delist Requested / Delisted tickets (undo to Listed) can be moved to Listed in bulk.',
        );
        return;
      }

      if (undoToListTickets.length > 0 && publishToListTickets.length > 0) {
        showError('Select only Verified tickets or only Delisted tickets to move to Listed, not both.');
        return;
      }

      if (undoToListTickets.length > 0) {
        const invalidUndoPrice = undoToListTickets.find((ticket) => {
          const value = String(bulkStatusListingPrices[ticket.tpId] || '').trim();
          const num = parseFloat(value);
          return !value || isNaN(num) || num <= 0;
        });
        if (invalidUndoPrice) {
          showError('All selected tickets must have a valid listing price greater than 0.');
          return;
        }

        const undoResults = await Promise.allSettled(
          undoToListTickets.map(async (ticket) => {
            const priceNum = Number(parseFloat(String(bulkStatusListingPrices[ticket.tpId] || '').trim()));
            const currentStatus = normalizeStatusValue(ticket.rawStatus || ticket.status);
            await executeUndoDelistWithFallback(ticket.tpId, currentStatus);
            await editTicketPriceBySuperUser({
              variables: { tp_ids: [ticket.tpId], tp_list_price: priceNum },
            });
            return { tpId: ticket.tpId, priceNum };
          }),
        );
        const undoSuccess = undoResults
          .filter(
            (result): result is PromiseFulfilledResult<{ tpId: string; priceNum: number }> =>
              result.status === 'fulfilled',
          )
          .map((result) => result.value);
        const undoFailed = undoResults.filter((result) => result.status === 'rejected').length;
        if (undoSuccess.length > 0) {
          undoSuccess.forEach(({ tpId, priceNum }) => {
            updateLocalTicket([tpId], { rawStatus: 'List', status: 'Listed', price: formatCurrency(priceNum) });
          });
          showSuccess(
            `Moved ${undoSuccess.length} ticket(s) back to Listed${undoFailed ? `, ${undoFailed} failed` : ''}`,
          );
          setActiveFilterTab('Listed');
          clearBulkSelection();
          closeBulkStatusModal();
        } else if (undoFailed > 0) {
          showError('Failed to move tickets back to Listed');
        }
      } else {
        const invalidTicket = publishToListTickets.find((ticket) => {
          const value = String(bulkStatusListingPrices[ticket.tpId] || '').trim();
          const num = parseFloat(value);
          return !value || isNaN(num) || num <= 0;
        });
        if (invalidTicket) {
          showError('All selected tickets must have a valid listing price greater than 0.');
          return;
        }

        const results = await Promise.allSettled(
          publishToListTickets.map((ticket) => {
            const priceNum = Number(parseFloat(String(bulkStatusListingPrices[ticket.tpId] || '').trim()));
            return callPublishTicketsRest([ticket.tpId], priceNum).then(() => ({
              tpId: ticket.tpId,
              priceNum,
            }));
          }),
        );
        const published = results
          .filter(
            (result): result is PromiseFulfilledResult<{ tpId: string; priceNum: number }> =>
              result.status === 'fulfilled',
          )
          .map((result) => result.value);
        const failedCount = results.filter((result) => result.status === 'rejected').length;

        if (published.length > 0) {
          published.forEach(({ tpId, priceNum }) => {
            updateLocalTicket([tpId], { rawStatus: 'List', status: 'Listed', price: formatCurrency(priceNum) });
          });
          showSuccess(
            `Published ${published.length} ticket(s) to Listed${failedCount ? `, ${failedCount} failed` : ''}`,
          );
          setActiveFilterTab('Listed');
          clearBulkSelection();
          closeBulkStatusModal();
        } else if (failedCount > 0) {
          showError('Failed to publish selected tickets');
        }
      }
    } else if (
      bulkSelectedTickets.every((ticket) => normalizeStatusValue(ticket.rawStatus || ticket.status) === 'Sold') &&
      (targetStatus === 'Sold' || targetStatus === 'Settled' || targetStatus === 'SettlementInProgress')
    ) {
      if (!canOverrideSettlementStatus) {
        showInfo('It will update automatically when payment completes.');
        return;
      }
      const paymentStatus = getPaymentStatusForSettlementOverride(targetStatus);
      if (paymentStatus === undefined) {
        showError('That settlement status could not be applied.');
        return;
      }
      const result = await callUpdateSettlementPaymentStatusRest(tpIds, paymentStatus);
      const updatedIds: string[] = Array.isArray(result?.updatedTpIds) ? result.updatedTpIds : tpIds;
      if (!updatedIds.length) {
        showError('No sold tickets were updated.');
        return;
      }
      const nextStatusLabel =
        paymentStatus === 'Success'
          ? 'Settled'
          : paymentStatus === 'Inprogress'
            ? 'Settlement In Progress'
            : 'Sold';
      updateLocalTicket(updatedIds, {
        rawStatus: 'Sold',
        status: nextStatusLabel,
        paymentStatus,
      });
      showSuccess(`Updated settlement status for ${updatedIds.length} ticket(s).`);
      setActiveFilterTab('Sold');
      clearBulkSelection();
      closeBulkStatusModal();
      await refreshTicketsAndNotifications();
    } else if (targetStatus === 'Sold') {
      await handleBulkSoldConfirm();
    }
    else if (targetStatus === 'Unsold') {
      await callMarkTicketsUnsoldRest(tpIds);
      updateLocalTicket(tpIds, { rawStatus: 'Unsold', status: 'Unsold' });
      showSuccess(`Marked ${tpIds.length} ticket(s) as Unsold`);
      setActiveFilterTab('Unsold');
      clearBulkSelection();
      closeBulkStatusModal();
    }
    else if (targetStatus === 'Return') {
      await callReturnTicketsRest(tpIds);
      updateLocalTicket(tpIds, { rawStatus: 'Return', status: 'Returned' });
      showSuccess(`Returned ${tpIds.length} ticket(s)`);
      setActiveFilterTab('Delisted');
      clearBulkSelection();
      closeBulkStatusModal();
    }
    else if (targetStatus === 'Delist') {
      const allListed = bulkSelectedTickets.every((ticket) => 
        normalizeStatusValue(ticket.rawStatus || ticket.status) === 'List'
      );
      if (!allListed) {
        showError('Only Listed tickets can be directly delisted in bulk.');
        return;
      }
      const results = await Promise.allSettled(
        bulkSelectedTickets.map((t) => callDelistTicketByAdminRest(t.tpId))
      );
      const successIds = bulkSelectedTickets
        .filter((_, i) => results[i].status === 'fulfilled')
        .map((t) => t.tpId);
      if (successIds.length) {
        updateLocalTicket(successIds, { rawStatus: 'Delist', status: 'Delisted' });
        showSuccess(`Delisted ${successIds.length} ticket(s)`);
        setActiveFilterTab('Delisted');
      } else {
        showError('Failed to delist selected tickets');
      }
      clearBulkSelection();
      closeBulkStatusModal();
    }
    else if (targetStatus === 'DelistInProgress' || targetStatus === 'DelistRequested') {
      closeBulkStatusModal();
      openDelistEmailModal(tpIds);
    }
    else if (targetStatus === 'ListingRequestCancelled') {
      const results = await Promise.allSettled(
        bulkSelectedTickets.map((t) => callCancelListingRequestByAdminRest(t.tpId))
      );
      const successIds = bulkSelectedTickets
        .filter((_, i) => results[i].status === 'fulfilled')
        .map((t) => t.tpId);
      if (successIds.length) {
        updateLocalTicket(successIds, { rawStatus: 'ListingRequestCancelled', status: 'Listing Request Cancelled' });
        showSuccess(`Cancelled ${successIds.length} listing request(s)`);
        setActiveFilterTab('Listing Request Cancelled');
      } else {
        showError('Failed to cancel listing requests');
      }
      clearBulkSelection();
      closeBulkStatusModal();
    }
    else if (targetStatus === 'ToBeVerified') {
      const results = await Promise.allSettled(
        bulkSelectedTickets.map((ticket) =>
          restoreCancelledListingToVerification({ variables: { tp_id: ticket.tpId } }).then((result) => {
            const updated = result?.data?.update_ticket_placement?.returning?.length || 0;
            if (updated <= 0) throw new Error('Unable to restore ticket');
            return ticket.tpId;
          })
        )
      );
      const successIds = results
        .filter((r): r is PromiseFulfilledResult<string> => r.status === 'fulfilled')
        .map((r) => r.value);
      if (successIds.length) {
        updateLocalTicket(successIds, { rawStatus: 'ToBeVerified', status: 'To Be Verified' });
        showSuccess(`Restored ${successIds.length} ticket(s) to To Be Verified`);
        setActiveFilterTab('Verification');
      } else {
        showError('Failed to restore tickets to To Be Verified');
      }
      clearBulkSelection();
      closeBulkStatusModal();
    }
    else {
      showInfo('This bulk status change is not available from User Details.');
    }
  } catch (err: any) {
    showError(err?.message || 'Bulk status update failed');
  } finally {
    setBulkStatusChangeLoading(false);
    await refreshTicketsAndNotifications();
  }
};

  const handleBulkMarkSold = async () => {
    const selected = getFilteredTickets().filter((t) => bulkSelectedTicketIds.has(t.tpId));
    const validation = canBulkUpdateTickets(Array.from(bulkSelectedTicketIds));
    if (!validation.allowed) {
      showError(validation.reason || 'Cannot mark selected tickets as sold');
      return;
    }
    if (selected.length === 1) {
      handleMarkAsSoldFromMenu(selected[0]);
      return;
    }
    setBulkSelectedTickets(selected);
    setBulkNewStatus('Sold');
    setShowBulkStatusModal(true);
    await initBulkSoldFeeConfig(selected);
  };

  const delistListedTicketsWithLogitix = async (tpIds: string[]) => {
    if (!tpIds.length) return;
    const listedTpIds = tpIds.filter((tpId) => {
      const ticket = getFilteredTickets().find((row) => row.tpId === tpId);
      return mapStatusForMutation(ticket?.rawStatus || ticket?.status || '') === 'List';
    });
    if (!listedTpIds.length) {
      openDelistEmailModal(tpIds);
      return;
    }
    try {
      const results = await Promise.allSettled(
        listedTpIds.map((tpId) => callDelistTicketByAdminRest(tpId)),
      );
      const successIds = listedTpIds.filter((_, index) => results[index].status === 'fulfilled');
      const failedCount = listedTpIds.length - successIds.length;
      if (!successIds.length) {
        showError('Failed to delist selected tickets');
        return;
      }
      updateLocalTicket(successIds, { rawStatus: 'Delist', status: 'Delisted' });
      if (failedCount > 0) {
        showError(`${failedCount} ticket(s) could not be delisted`);
      } else {
        showSuccess(`Delisted ${successIds.length} ticket(s)`);
      }
      setActiveFilterTab('Delisted');
      await refreshTicketsAndNotifications();
    } catch (error: any) {
      showError(error?.message || 'Failed to delist selected tickets');
    }
  };

  const handleBulkDelist = () => {
    const tpIds = Array.from(bulkSelectedTicketIds);
    if (!tpIds.length) return;
    const validation = canBulkUpdateTickets(tpIds);
    if (!validation.allowed) {
      showError(validation.reason || 'Cannot delist selected tickets');
      return;
    }
    void delistListedTicketsWithLogitix(tpIds);
  };

  const handleBulkChangePrice = () => {
    const selected = getFilteredTickets().filter((t) => bulkSelectedTicketIds.has(t.tpId));
    const validation = canBulkUpdateTickets(Array.from(bulkSelectedTicketIds));
    if (!validation.allowed) {
      showError(validation.reason || 'Cannot change price for selected tickets');
      return;
    }
    if (!selected.length) return;
    
    if (selected.length === 1) {
      setPriceModalTicket(selected[0]);
      setNewPrice(getTicketListPrice(selected[0]) > 0 ? String(getTicketListPrice(selected[0])) : '');
      setShowPriceModal(true);
      return;
    }
    
    setBulkPriceModalTickets(selected);
    setBulkPrices(Object.fromEntries(selected.map((t) => [t.tpId, getTicketListPrice(t) > 0 ? String(getTicketListPrice(t)) : ''])));
    setBulkCommonPrice('');
    setShowBulkPriceModal(true);
  };

  const handleApplyBulkCommonPrice = () => {
    const value = String(bulkCommonPrice).trim();
    const priceNum = parseFloat(value);
    if (!value || isNaN(priceNum) || priceNum <= 0) {
      showError('Enter a valid price to apply to all tickets');
      return;
    }
    setBulkPrices(
      Object.fromEntries(bulkPriceModalTickets.map((ticket) => [ticket.tpId, String(priceNum)])),
    );
  };

  const areAllBulkPricesValid = (tickets: Ticket[], prices: Record<string, string>): boolean =>
    tickets.every((ticket) => {
      const value = String(prices[ticket.tpId] || '').trim();
      const num = parseFloat(value);
      return !!value && !isNaN(num) && num > 0;
    });

  const handleConfirmBulkPricing = async () => {
    if (bulkPriceLoading || !bulkPriceModalTickets.length) return;
    if (!areAllBulkPricesValid(bulkPriceModalTickets, bulkPrices)) {
      showError('All tickets must have a valid price greater than 0');
      return;
    }

    setBulkPriceLoading(true);
    try {
      const tpIds = bulkPriceModalTickets.map((t) => t.tpId);
      const uniquePrices = Array.from(new Set(Object.values(bulkPrices).map((p) => parseFloat(p.toString()))));

      if (uniquePrices.length === 1) {
        const priceNum = uniquePrices[0];
        const res = await editTicketPriceBySuperUser({
          variables: { tp_ids: tpIds, tp_list_price: priceNum },
        });
        const payload = res?.data?.editTicketPriceBySuperUser;
        const rawMsg = String(payload?.message || '').trim();
        const normalized = rawMsg.toLowerCase();

        if (
          normalized.includes('fail') ||
          normalized.includes('error') ||
          normalized.includes('invalid') ||
          normalized.includes('unauthorized')
        ) {
          showError(rawMsg || 'Failed to update prices');
          return;
        }

        showSuccess(
          rawMsg ||
            `Price updated to $${priceNum} for ${tpIds.length} ticket(s)`,
        );
        updateLocalTicket(tpIds, { price: formatCurrency(priceNum) });
      } else {
        const results = await Promise.allSettled(
          bulkPriceModalTickets.map((ticket) => {
            const priceNum = parseFloat(String(bulkPrices[ticket.tpId] || '').trim());
            return editTicketPriceBySuperUser({
              variables: { tp_ids: [ticket.tpId], tp_list_price: priceNum },
            }).then(() => ({ tpId: ticket.tpId, priceNum }));
          }),
        );

        const successUpdates = results
          .filter((r): r is PromiseFulfilledResult<{ tpId: string; priceNum: number }> => r.status === 'fulfilled')
          .map((r) => r.value);
        const failedCount = results.filter((r) => r.status === 'rejected').length;

        if (successUpdates.length > 0) {
          successUpdates.forEach(({ tpId, priceNum }) => {
            updateLocalTicket([tpId], { price: formatCurrency(priceNum) });
          });
          showSuccess(
            `Price updated for ${successUpdates.length} ticket(s)${failedCount ? `, ${failedCount} failed` : ''}`,
          );
        } else if (failedCount > 0) {
          showError('Failed to update prices for selected tickets');
        }
      }

      setShowBulkPriceModal(false);
      setBulkPriceModalTickets([]);
      setBulkPrices({});
      setBulkCommonPrice('');
      clearBulkSelection();
      await refreshTicketsAndNotifications();
    } catch (error: any) {
      const gqlMsgs = Array.isArray(error?.graphQLErrors)
        ? error.graphQLErrors.map((e: { message?: string }) => e.message).filter(Boolean)
        : [];
      const detail = gqlMsgs.length ? gqlMsgs.join(' ') : error?.message;
      showError(detail?.trim() || 'Failed to update prices');
    } finally {
      setBulkPriceLoading(false);
    }
  };
  
const handleStatusChange = async (ticket: Ticket, newStatusRaw: string) => {
  const currentStatus = mapStatusForMutation(ticket.rawStatus || ticket.status);
  let targetStatus = mapStatusForMutation(newStatusRaw);
  
  if (newStatusRaw === 'Move to Listed' || newStatusRaw === 'Listed (Undo)') {
    targetStatus = 'List';
  }
  
  if (targetStatus === 'DelistRequested') {
    targetStatus = 'DelistInProgress';
  }
  if (currentStatus === 'List' && targetStatus === 'Delist') {
    try {
      const res = await callDelistTicketByAdminRest(ticket.tpId);
      const rawMsg = String(res?.message || '').trim();
      const normalized = rawMsg.toLowerCase();
      if (normalized.includes('fail') || normalized.includes('error')) {
        showError(rawMsg || 'Failed to delist ticket');
        return;
      }
      showSuccess(rawMsg || 'Ticket delisted successfully');
      updateLocalTicket([ticket.tpId], { rawStatus: 'Delist', status: 'Delisted' });
      await refreshTicketsAndNotifications();
      setActiveFilterTab('Delisted');
    } catch (error: any) {
      showError(error?.message || 'Failed to delist ticket');
    }
    return;
  }

  if (currentStatus === 'ToBeVerified' && targetStatus === 'Verified') {
    if (isMissingVerifyRequiredFields(ticket)) {
      showError('Ticket is missing required fields (section, row, seat, or price).');
      return;
    }
    try {
      const restData = await callUpdateTicketStatusRest([ticket.tpId], true, false);
      const message = String(restData?.message || '').trim();
      const normalized = message.toLowerCase();
      const isSuccess =
        normalized.includes('success') &&
        !normalized.includes('0 ticket updated successfully') &&
        !normalized.includes('0 tickets updated successfully') &&
        !normalized.includes('0 ticket updated') &&
        !normalized.includes('not found') &&
        !normalized.includes('failed') &&
        !normalized.includes('error');
      if (isSuccess) {
        showSuccess(message || 'Ticket verified successfully');
        updateLocalTicket([ticket.tpId], { rawStatus: 'Verified', status: 'Verified' });
        await refreshTicketsAndNotifications();
        setActiveFilterTab('Verification');
      } else {
        showError(message || 'Ticket verification failed');
      }
    } catch (error: any) {
      showError(error?.message || 'Failed to verify ticket');
    }
    return;
  }

  if (currentStatus === 'ToBeVerified' && (targetStatus === 'Invalid' || targetStatus === 'ListingRequestCancelled')) {
    try {
      await callCancelListingRequestByAdminRest(ticket.tpId);
      showSuccess('Ticket marked as Listing Request Cancelled');
      updateLocalTicket([ticket.tpId], { rawStatus: 'ListingRequestCancelled', status: 'Listing Request Cancelled' });
      await refreshTicketsAndNotifications();
      setActiveFilterTab('Listing Request Cancelled');
    } catch (error: any) {
      showError(error?.message || 'Failed to cancel listing request');
    }
    return;
  }

  if (currentStatus === 'Verified' && targetStatus === 'List') {
    setListedModalTicket(ticket);
    setListedPrice(parseCurrency(ticket.price).toString());
    setShowListedModal(true);
    return;
  }

  if ((currentStatus === 'List' || currentStatus === 'Unsold') && targetStatus === 'Sold') {
    setSoldModalTicket(ticket);
    setSoldMarketplaceId('');
    setSoldLogitixAmount('');
    setSoldFeeConfigReady(false);
    setShowSoldModal(true);
    try {
      await refetchMarketplaces();
    } catch {
      /* use cached marketplaces */
    }
    const feeConfig = await resolveSoldFeeConfigForUser();
    setSoldFeeMode(feeConfig.feeMode);
    setSoldSuperClientPercent(feeConfig.superClientPercent);
    setSoldFeeConfigReady(true);
    return;
  }

  if (currentStatus === 'Sold' && (targetStatus === 'Sold' || targetStatus === 'Settled' || targetStatus === 'SettlementInProgress')) {
    if (!canOverrideSettlementStatus) {
      showInfo('It will update automatically when payment completes.');
      return;
    }
    const paymentStatus = getPaymentStatusForSettlementOverride(targetStatus);
    if (paymentStatus === undefined) {
      showError('That settlement status could not be applied.');
      return;
    }
    try {
      const result = await callUpdateSettlementPaymentStatusRest([ticket.tpId], paymentStatus);
      const updatedIds: string[] = Array.isArray(result?.updatedTpIds) ? result.updatedTpIds : [ticket.tpId];
      if (!updatedIds.length) {
        showError('No sold ticket was updated.');
        return;
      }
      const nextStatusLabel =
        paymentStatus === 'Success'
          ? 'Settled'
          : paymentStatus === 'Inprogress'
            ? 'Settlement In Progress'
            : 'Sold';
      updateLocalTicket(updatedIds, {
        rawStatus: 'Sold',
        status: nextStatusLabel,
        paymentStatus,
      });
      showSuccess(result?.message || 'Settlement status updated successfully.');
      await refreshTicketsAndNotifications();
      setActiveFilterTab('Sold');
    } catch (error: any) {
      showError(error?.message || 'Failed to update settlement status');
    }
    return;
  }

  if (currentStatus === 'Delist' && targetStatus === 'DelistInProgress') {
    try {
      await updateTicketStatus({
        variables: { tp_id: ticket.tpId, status: 'DelistInProgress' }
      });
      showSuccess('Ticket moved to Delist Requested successfully');
      updateLocalTicket([ticket.tpId], { rawStatus: 'DelistInProgress', status: 'Delist Requested' });
      await refreshTicketsAndNotifications();
      setActiveFilterTab('Delisted');
    } catch (error: any) {
      showError(error?.message || 'Failed to move ticket to Delist Requested');
    }
    return;
  }

  if (currentStatus === 'List' && targetStatus === 'DelistInProgress') {
    try {
      const res = await callDelistTicketByAdminRest(ticket.tpId);
      const rawMsg = String(res?.message || '').trim();
      const normalized = rawMsg.toLowerCase();
      if (normalized.includes('fail') || normalized.includes('error')) {
        showError(rawMsg || 'Failed to delist ticket');
        return;
      }
      showSuccess(rawMsg || 'Ticket delisted successfully');
      updateLocalTicket([ticket.tpId], { rawStatus: 'Delist', status: 'Delisted' });
      await refreshTicketsAndNotifications();
      setActiveFilterTab('Delisted');
    } catch (error: any) {
      showError(error?.message || 'Failed to delist ticket');
    }
    return;
  }

  if (targetStatus === 'DelistInProgress' && currentStatus === 'Verified') {
    openDelistEmailModal([ticket.tpId]);
    return;
  }

  if (
    (currentStatus === 'Delist' ||
      currentStatus === 'DelistInProgress' ||
      currentStatus === 'DelistRequested') &&
    targetStatus === 'Return'
  ) {
    try {
      const returnResponse = await callReturnTicketsRest([ticket.tpId]);
      const returnMsg = String(returnResponse?.message || '').toLowerCase();
      if (returnMsg.includes('fail') || returnMsg.includes('error')) {
        showError(returnResponse?.message || 'Failed to return ticket');
        return;
      }
      showSuccess(returnResponse?.message || 'Ticket returned successfully');
      updateLocalTicket([ticket.tpId], { rawStatus: 'Return', status: 'Returned' });
      await refreshTicketsAndNotifications();
      setActiveFilterTab('Delisted');
    } catch (error: any) {
      showError(error?.message || 'Failed to return ticket');
    }
    return;
  }

  if ((currentStatus === 'DelistInProgress' || currentStatus === 'Delist') && targetStatus === 'Delist') {
    try {
      const res = await callDelistTicketByAdminRest(ticket.tpId);
      const rawMsg = String(res?.message || '').trim();
      const normalized = rawMsg.toLowerCase();
      if (normalized.includes('fail') || normalized.includes('error')) {
        showError(rawMsg || 'Failed to delist ticket');
        return;
      }
      showSuccess(rawMsg || 'Ticket delisted successfully');
      updateLocalTicket([ticket.tpId], { rawStatus: 'Delist', status: 'Delisted' });
      await refreshTicketsAndNotifications();
      setActiveFilterTab('Delisted');
    } catch (error: any) {
      showError(error?.message || 'Failed to delist ticket');
    }
    return;
  }

  if (
    (currentStatus === 'DelistInProgress' ||
      currentStatus === 'DelistRequested' ||
      currentStatus === 'Delist') &&
    targetStatus === 'List'
  ) {
    try {
      await executeUndoDelistWithFallback(ticket.tpId, currentStatus);
      showSuccess('Ticket moved to Listed successfully');
      updateLocalTicket([ticket.tpId], { rawStatus: 'List', status: 'Listed' });
      await refreshTicketsAndNotifications();
      setActiveFilterTab('Listed');
    } catch (error: any) {
      showError(error?.message || 'Failed to move ticket to Listed');
    }
    return;
  }

  if (currentStatus === 'List' && targetStatus === 'Unsold') {
    try {
      const response = await callMarkTicketsUnsoldRest([ticket.tpId]);
      showSuccess(response?.message || 'Ticket marked as Unsold successfully');
      updateLocalTicket([ticket.tpId], { rawStatus: 'Unsold', status: 'Unsold' });
      await refreshTicketsAndNotifications();
      setActiveFilterTab('Unsold');
    } catch (error: any) {
      showError(error?.message || 'Failed to mark ticket as Unsold');
    }
    return;
  }

  if (currentStatus === 'ListingRequestCancelled' && targetStatus === 'ToBeVerified') {
    try {
      const result = await restoreCancelledListingToVerification({
        variables: { tp_id: ticket.tpId }
      });
      const updated = result?.data?.update_ticket_placement?.returning?.length || 0;
      if (updated <= 0) {
        showError('Unable to restore ticket to To Be Verified');
        return;
      }
      showSuccess('Ticket restored to To Be Verified');
      updateLocalTicket([ticket.tpId], { rawStatus: 'ToBeVerified', status: 'To Be Verified' });
      await refreshTicketsAndNotifications();
      setActiveFilterTab('Verification');
    } catch (error: any) {
      showError(error?.message || 'Failed to restore ticket to To Be Verified');
    }
    return;
  }

  if (currentStatus === 'Invalid' && targetStatus === 'ToBeVerified') {
    showInfo('Restoring an invalid ticket is not available from this page. Use Manage Tickets instead.');
    return;
  }

  showInfo('This status change is not available from User Details. Use Manage Tickets if you need this transition.');
};
  
  const getResolvedClientType = (ticket: Ticket): 'self-pricing' | 'concierge' => {
    const userPricingMode = currentUser?.type === 'Self Pricing' ? 'self-pricing' : 'concierge';
    return (userPricingMode as 'self-pricing' | 'concierge') || 'concierge';
  };
  
  const openChangeStatusModal = (ticket: Ticket) => {
    const currentToken = mapStatusForMutation(ticket.rawStatus || ticket.status);
    const transitions = getStatusTransitions(currentToken);
    if (transitions.length === 0) {
      showError('No status changes available for this ticket.');
      return;
    }
    setChangeStatusTicket(ticket);
    setSelectedNewStatus('');
    setShowChangeStatusModal(true);
    setOpenActionTicketId(null);
  };
  
  const handleChangeStatusConfirm = async () => {
    if (!changeStatusTicket || !selectedNewStatus) return;
    setChangeStatusLoading(true);
    try {
      await handleStatusChange(changeStatusTicket, selectedNewStatus);
      setShowChangeStatusModal(false);
      setChangeStatusTicket(null);
      setSelectedNewStatus('');
    } catch (error: any) {
      showError(error?.message || 'Failed to change status');
    } finally {
      setChangeStatusLoading(false);
    }
  };
  
 const openEditTicketModal = (ticket: Ticket) => {
  setEditTicket(ticket);
  setEditTicketForm({
    section: ticket.section === '-' ? '' : ticket.section,
    row: ticket.row === '-' ? '' : ticket.row,
    seatNo: ticket.seatNo === '-' ? '' : ticket.seatNo,
    price: parseCurrency(ticket.price).toString()
  });
  setShowEditTicketModal(true);
  setOpenActionTicketId(null);
};
  
const handleEditTicketSave = async () => {
  if (!editTicket) return;
  const { section, row, seatNo, price } = editTicketForm;
  if (!section.trim() || !row.trim() || !seatNo.trim()) {
    showError('Section, Row, and Seat Number are required.');
    return;
  }
  const priceNum = parseFloat(price);
  if (isNaN(priceNum) || priceNum <= 0) {
    showError('Please enter a valid price greater than 0.');
    return;
  }
  const seatNum = parseInt(seatNo.trim(), 10);
  if (!Number.isFinite(seatNum)) {
    showError('Seat must be a valid number.');
    return;
  }
  const eId = editTicket.eventId;
  if (!eId) {
    showError('Missing event id for this ticket. Refresh the page and try again.');
    return;
  }
  setEditTicketLoading(true);
  try {
    const restData = await callEditTicketsRest({
      e_id: eId,
      tp_id: editTicket.tpId,
      tp_section: section.trim(),
      tp_row: row.trim(),
      tp_seat_no: seatNum,
      tp_list_price: priceNum,
    });
    const msg = String(restData?.message || '').toLowerCase();
    if (msg.includes('fail') || msg.includes('error') || msg.includes('unauthorized')) {
      showError(restData?.message || 'Failed to update ticket details');
      return;
    }
    updateLocalTicket([editTicket.tpId], {
      section: section.trim(),
      row: row.trim(),
      seatNo: seatNo.trim(),
      price: formatCurrency(priceNum),
    });
    showSuccess('Ticket details updated successfully');
    setShowEditTicketModal(false);
    setEditTicket(null);
    await refreshTicketsAndNotifications();
  } catch (error: any) {
    showError(error?.message || 'Failed to update ticket details');
  } finally {
    setEditTicketLoading(false);
  }
};
  const handleListedModalConfirm = async () => {
    if (!listedModalTicket) return;
    const priceNum = parseFloat(listedPrice);
    if (isNaN(priceNum) || priceNum <= 0) {
      showError('Please enter a valid price greater than 0.');
      return;
    }
    setListedModalLoading(true);
    try {
      const restData = await callPublishTicketsRest([listedModalTicket.tpId], priceNum);
      const message = String(restData?.message || '');
      const normalized = message.toLowerCase();
      const isSuccess = normalized.includes('success') || normalized.includes('listed');
      if (!isSuccess) {
        showError(message || 'Failed to list ticket');
        setShowListedModal(false);
        setListedModalTicket(null);
        setListedPrice('');
        return;
      }
      showSuccess(message || `Ticket listed at $${priceNum}`);
      updateLocalTicket([listedModalTicket.tpId], { rawStatus: 'List', status: 'Listed', price: formatCurrency(priceNum) });
      setShowListedModal(false);
      setListedModalTicket(null);
      setListedPrice('');
      setActiveFilterTab('Listed');
      await refreshTicketsAndNotifications();
    } catch (error: any) {
      showError(error?.message || 'Failed to list ticket');
    } finally {
      setListedModalLoading(false);
    }
  };
const hasSoldTicketsForTeam = (teamId: string, teamName: string, teamAbbr: string): boolean => {
  const teamNameLower = teamName.toLowerCase().trim();
  const teamAbbrLower = teamAbbr.toLowerCase().trim();

  return localTickets.some(ticket => {
    const rawStatus = (ticket.rawStatus || ticket.status).toLowerCase();
    const isSold = rawStatus === 'sold';
    if (!isSold) return false;

    const eventName = ticket.event.toLowerCase();
    return eventName.includes(teamNameLower) || eventName.includes(teamAbbrLower);
  });
};
  const handleSoldModalConfirm = async () => {
    if (!soldModalTicket) return;
    if (!soldMarketplaceId) { showError('Please select a marketplace.'); return; }
    const mp = marketplacesList.find(m => m.id === soldMarketplaceId);
    if (!mp) { showError('Selected marketplace not found.'); return; }
    const listPrice = parseCurrency(soldModalTicket.price);
    const logitixNum = parseFloat(String(soldLogitixAmount || '').trim());
    if (isNaN(logitixNum) || logitixNum <= 0) { showError('Please enter the amount received from marketplace.'); return; }
    const quickPct = effectiveSoldQuickCutPercent(mp.vl_fee_percentage, soldFeeMode, soldSuperClientPercent);
const quickCutDollars = Number(roundToFixed(logitixNum * (quickPct / 100), 2));
    const settlement = Math.round((logitixNum - quickCutDollars) * 100) / 100;
    if (settlement <= 0) { showError('Amount settled to user must be greater than zero.'); return; }
    setSoldModalLoading(true);
    try {
      const restData = await callMarkAsSoldRest(soldModalTicket.tpId, settlement, logitixNum, mp.name);
      const rawMsg = String(restData?.message || '');
      if (!rawMsg.toLowerCase().includes('success')) throw new Error(rawMsg || 'Failed to mark ticket as sold');
      updateLocalTicket([soldModalTicket.tpId], { rawStatus: 'Sold', status: 'Sold', soldPrice: formatCurrency(settlement), marketplace: mp.name });
      if (userId) {
        saveMarketplaceCache(userId, soldModalTicket.tpId, mp.name);
      }
      showSuccess(rawMsg || 'Ticket marked as sold successfully');
      setShowSoldModal(false);
      setSoldModalTicket(null);
      setSoldMarketplaceId('');
      setSoldLogitixAmount('');
      setActiveFilterTab('Sold');
      console.log('🔄 Refetching tickets and team sold status after marking as sold...');
      await Promise.all([
        refetchTickets(),
        refetchTeamsWithSoldStatus(),
      ]);
      syncDashboardNotifications();
      console.log('✅ Refetch completed - delete button should now update for sold teams');
    } catch (err: any) {
      showError(err?.message || 'Failed to mark ticket as sold');
    } finally {
      setSoldModalLoading(false);
    }
  };
  
  const closeSoldModal = () => {
    setShowSoldModal(false);
    setSoldModalTicket(null);
    setSoldMarketplaceId('');
    setSoldLogitixAmount('');
    setSoldFeeConfigReady(false);
    setSoldFeeMode('default');
    setSoldSuperClientPercent(null);
  };
  
  const handlePriceChangeClick = (ticket: Ticket) => {
    setPriceModalTicket(ticket);
    setNewPrice(parseCurrency(ticket.price).toString());
    setShowPriceModal(true);
  };
  
  const handlePriceModalConfirm = async () => {
    if (!priceModalTicket) return;
    const priceNum = parseFloat(newPrice);
    if (isNaN(priceNum) || priceNum <= 0) {
      showError('Please enter a valid price.');
      return;
    }
    const targetTpIds = bulkPriceTpIds.length > 0 ? bulkPriceTpIds : [priceModalTicket.tpId];
    setPriceModalLoading(true);
    try {
      const res = await editTicketPriceBySuperUser({
        variables: { tp_ids: targetTpIds, tp_list_price: priceNum },
      });
      const payload = res?.data?.editTicketPriceBySuperUser;
      const rawMsg = String(payload?.message || '').trim();
      const normalized = rawMsg.toLowerCase();
      if (payload?.requiresConfirmation) {
        showInfo(rawMsg || 'Price update requires confirmation.');
        return;
      }
      if (
        normalized.includes('fail') ||
        normalized.includes('error') ||
        normalized.includes('invalid') ||
        normalized.includes('unauthorized')
      ) {
        showError(rawMsg || 'Failed to update price');
        return;
      }
      showSuccess(
        rawMsg ||
          (targetTpIds.length > 1
            ? `Price updated to $${priceNum} for ${targetTpIds.length} tickets`
            : `Price updated to $${priceNum}`),
      );
      updateLocalTicket(targetTpIds, { price: formatCurrency(priceNum) });
      setShowPriceModal(false);
      setPriceModalTicket(null);
      setNewPrice('');
      setBulkPriceTpIds([]);
      clearBulkSelection();
      await refreshTicketsAndNotifications();
    } catch (error: any) {
      const gqlMsgs = Array.isArray(error?.graphQLErrors)
        ? error.graphQLErrors.map((e: { message?: string }) => e.message).filter(Boolean)
        : [];
      const detail = gqlMsgs.length ? gqlMsgs.join(' ') : error?.message;
      showError(detail?.trim() || 'Failed to update price');
    } finally {
      setPriceModalLoading(false);
    }
  };
  
  const handleMarkAsSoldFromMenu = async (ticket: Ticket) => {
    setSoldModalTicket(ticket);
    setSoldMarketplaceId('');
    setSoldLogitixAmount('');
    setSoldFeeConfigReady(false);
    setShowSoldModal(true);
    try {
      await refetchMarketplaces();
    } catch {
      /* use cached marketplaces */
    }
    const feeConfig = await resolveSoldFeeConfigForUser();
    setSoldFeeMode(feeConfig.feeMode);
    setSoldSuperClientPercent(feeConfig.superClientPercent);
    setSoldFeeConfigReady(true);
  };
  
  useEffect(() => {
    if (!showSoldModal || !soldModalTicket || !soldMarketplaceId || !soldFeeConfigReady) return;
    const mp = marketplacesList.find(m => m.id === soldMarketplaceId);
    if (!mp) return;
    const listPriceNum = parseCurrency(soldModalTicket.price);
    const expected = Math.round(listPriceNum * (100 - mp.vl_fee_percentage)) / 100;
    setSoldLogitixAmount(String(Number(roundToFixed(expected, 2))));
  }, [showSoldModal, soldMarketplaceId, soldModalTicket?.price, soldFeeConfigReady, marketplacesList]);
  
  
  useEffect(() => {
    const primaryTickets = transformTicketsData(ticketsData, userId);
    if (primaryTickets.length > 0 || !isGetUserTicketsAllowlistError) {
      const preserveMarketplaceFromOldTickets = (oldTickets: Ticket[], newTickets: Ticket[]): Ticket[] => {
        const oldMarketplaceMap = new Map<string, string | null>();
        oldTickets.forEach(t => {
          if (t.marketplace) {
            oldMarketplaceMap.set(t.tpId, t.marketplace);
          }
        });
        
        return newTickets.map(t => {
          const preservedMarketplace = oldMarketplaceMap.get(t.tpId);
          if (preservedMarketplace && !t.marketplace) {
            console.log(`✅ PRESERVED marketplace for ${t.tpId}: ${preservedMarketplace}`);
            return { ...t, marketplace: preservedMarketplace };
          }
          return t;
        });
      };
      
      const mergedTickets = applyMarketplaceLookupToTickets(preserveMarketplaceFromOldTickets(localTickets, primaryTickets));
      setLocalTickets(mergedTickets);
      return;
    }
    setLocalTickets(applyMarketplaceLookupToTickets(transformFallbackManageTicketsData(fallbackManageTicketsData, userId)));
  }, [ticketsData, fallbackManageTicketsData, userId, isGetUserTicketsAllowlistError, marketplaceByTicketPlacementId, applyMarketplaceLookupToTickets]);
  
  const hasAddressValues = (user: UserData): boolean => {
    return !!(user.address1?.trim() || user.address2?.trim() || user.city?.trim() || user.state?.trim() || user.zip?.trim() || user.country?.trim());
  };
  
  // REST API calls
  const callUpdateTicketStatusRest = async (ticketPlacementId: string[], isValid: boolean, isUndoRequest = false) => {
    const idTokenKey = Object.keys(localStorage).find((key) => key.includes('idToken')) as string;
    const idToken = idTokenKey ? localStorage.getItem(idTokenKey) : '';
    const hasuraRole = localStorage.getItem('x-hasura-user-role');
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (idToken) headers.Authorization = `Bearer ${idToken}`;
    if (hasuraRole) headers['x-hasura-role'] = hasuraRole;
    const nodeApiBaseUrl = (process.env.REACT_APP_NODE_API_URL || config.node_api_url || '').replace(/\/+$/, '');
    if (!nodeApiBaseUrl) throw new Error('Backend API URL not configured');
    const response = await fetch(`${nodeApiBaseUrl}/api/updateTicketStatus`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        input: {
          ticketPlacementId,
          isValid,
          isUndoRequest,
        },
      }),
    });
    const responseData = await response.json().catch(() => ({}));
    if (!response.ok) {
      const message =
        responseData?.message ||
        responseData?.error ||
        responseData?.errors?.[0]?.message ||
        'Failed to update ticket status';
      throw new Error(message);
    }
    return responseData;
  };

  const callDelistTicketByAdminRest = async (ticketPlacementId: string) => {
    const idTokenKey = Object.keys(localStorage).find((key) => key.includes('idToken')) as string;
    const idToken = idTokenKey ? localStorage.getItem(idTokenKey) : '';
    const hasuraRole = localStorage.getItem('x-hasura-user-role');
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (idToken) headers.Authorization = `Bearer ${idToken}`;
    if (hasuraRole) headers['x-hasura-role'] = hasuraRole;
    const nodeApiBaseUrl = (process.env.REACT_APP_NODE_API_URL || config.node_api_url || '').replace(/\/+$/, '');
    if (!nodeApiBaseUrl) throw new Error('Backend API URL not configured');
    const response = await fetch(`${nodeApiBaseUrl}/api/delistTicketBySuperUser`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        input: { ticketPlacementId },
      }),
    });
    const responseData = await response.json().catch(() => ({}));
    if (!response.ok) {
      const message =
        responseData?.message ||
        responseData?.error ||
        responseData?.errors?.[0]?.message ||
        'Failed to delist ticket';
      throw new Error(message);
    }
    return responseData;
  };

  const callCancelListingRequestByAdminRest = async (ticketPlacementId: string) => {
    const idTokenKey = Object.keys(localStorage).find((key) => key.includes('idToken')) as string;
    const idToken = idTokenKey ? localStorage.getItem(idTokenKey) : '';
    const hasuraRole = localStorage.getItem('x-hasura-user-role');
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (idToken) headers.Authorization = `Bearer ${idToken}`;
    if (hasuraRole) headers['x-hasura-role'] = hasuraRole;
    const nodeApiBaseUrl = (process.env.REACT_APP_NODE_API_URL || config.node_api_url || '').replace(/\/+$/, '');
    if (!nodeApiBaseUrl) throw new Error('Backend API URL not configured');
    const response = await fetch(`${nodeApiBaseUrl}/api/cancelListingRequestBySuperUser`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        input: { ticketPlacementId },
      }),
    });
    const responseData = await response.json().catch(() => ({}));
    if (!response.ok) {
      const message =
        responseData?.message ||
        responseData?.error ||
        responseData?.errors?.[0]?.message ||
        'Failed to cancel listing request';
      throw new Error(message);
    }
    return responseData;
  };

  const callUndoDelistRequestToListRest = async (tpId: string) => {
    const idTokenKey = Object.keys(localStorage).find((key) => key.includes('idToken')) as string;
    const idToken = idTokenKey ? localStorage.getItem(idTokenKey) : '';
    const hasuraRole = localStorage.getItem('x-hasura-user-role');
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (idToken) headers.Authorization = `Bearer ${idToken}`;
    if (hasuraRole) headers['x-hasura-role'] = hasuraRole;
    const nodeApiBaseUrl = (process.env.REACT_APP_NODE_API_URL || config.node_api_url || '').replace(/\/+$/, '');
    if (!nodeApiBaseUrl) throw new Error('Backend API URL not configured');
    const response = await fetch(`${nodeApiBaseUrl}/api/undoDelistRequestToList`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        input: { tpId },
      }),
    });
    const responseData = await response.json().catch(() => ({}));
    if (!response.ok) {
      const message =
        responseData?.message ||
        responseData?.error ||
        responseData?.errors?.[0]?.message ||
        'Failed to undo delist request';
      throw new Error(message);
    }
    return responseData;
  };

  const executeUndoDelistWithFallback = async (tpId: string, currentStatus?: string): Promise<void> => {
    if (currentStatus === 'Delist') {
      const result = await updateTicketStatus({
        variables: { tp_id: tpId, status: 'List' },
      });
      const updated = result?.data?.update_ticket_placement?.returning?.length || 0;
      if (updated > 0) return;
      throw new Error('Unable to move ticket back to List');
    }

    const response = await callUndoDelistRequestToListRest(tpId);
    const message = String(response?.message || '').toLowerCase();
    if (message.includes('fail') || message.includes('error')) {
      throw new Error(response?.message || 'Failed to undo delist request');
    }
  };

  const callDelistTicketsRest = async (ticketPlacementIds: string[], requestedEmailId: string) => {
    const idTokenKey = Object.keys(localStorage).find((key) => key.includes('idToken')) as string;
    const idToken = idTokenKey ? localStorage.getItem(idTokenKey) : '';
    const hasuraRole = localStorage.getItem('x-hasura-user-role');
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (idToken) headers.Authorization = `Bearer ${idToken}`;
    if (hasuraRole) headers['x-hasura-role'] = hasuraRole;
    const nodeApiBaseUrl = (process.env.REACT_APP_NODE_API_URL || config.node_api_url || '').replace(/\/+$/, '');
    if (!nodeApiBaseUrl) throw new Error('Backend API URL not configured');
    const response = await fetch(`${nodeApiBaseUrl}/api/delistTickets`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        input: {
          delistTicketsInput: {
            ticketPlacementIds,
            requestedEmailId,
          },
        },
      }),
    });
    const responseData = await response.json().catch(() => ({}));
    if (!response.ok) {
      const message =
        responseData?.message ||
        responseData?.error ||
        responseData?.errors?.[0]?.message ||
        'Failed to submit delist request';
      throw new Error(message);
    }
    return responseData;
  };

  const callEditTicketsRest = async (editInput: {
    e_id: string;
    tp_id: string;
    tp_section: string;
    tp_row: string;
    tp_seat_no: number;
    tp_list_price?: number;
    forceUpdate?: boolean;
  }) => {
    const idTokenKey = Object.keys(localStorage).find((key) => key.includes('idToken')) as string;
    const idToken = idTokenKey ? localStorage.getItem(idTokenKey) : '';
    const hasuraRole = localStorage.getItem('x-hasura-user-role');
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (idToken) headers.Authorization = `Bearer ${idToken}`;
    if (hasuraRole) headers['x-hasura-role'] = hasuraRole;
    const nodeApiBaseUrl = (process.env.REACT_APP_NODE_API_URL || config.node_api_url || '').replace(/\/+$/, '');
    if (!nodeApiBaseUrl) throw new Error('Backend API URL not configured');
    const editTicketsInput: Record<string, unknown> = {
      e_id: editInput.e_id,
      tp_id: editInput.tp_id,
      tp_section: editInput.tp_section,
      tp_row: editInput.tp_row,
      tp_seat_no: editInput.tp_seat_no,
    };
    if (editInput.tp_list_price != null) editTicketsInput.tp_list_price = editInput.tp_list_price;
    if (editInput.forceUpdate != null) editTicketsInput.forceUpdate = editInput.forceUpdate;
    const response = await fetch(`${nodeApiBaseUrl}/api/editTickets`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ input: { editTicketsInput } }),
    });
    const responseData = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(responseData?.message || responseData?.error || 'Failed to update ticket details');
    }
    return responseData;
  };

  const callPublishTicketsRest = async (ticketPlacementIds: string[], listPrice: number) => {
    const idTokenKey = Object.keys(localStorage).find((key) => key.includes('idToken')) as string;
    const idToken = idTokenKey ? localStorage.getItem(idTokenKey) : '';
    const hasuraRole = localStorage.getItem('x-hasura-user-role');
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (idToken) headers.Authorization = `Bearer ${idToken}`;
    if (hasuraRole) headers['x-hasura-role'] = hasuraRole;
    const nodeApiBaseUrl = (process.env.REACT_APP_NODE_API_URL || config.node_api_url || '').replace(/\/+$/, '');
    if (!nodeApiBaseUrl) throw new Error('Backend API URL not configured');
    const response = await fetch(`${nodeApiBaseUrl}/api/publishTickets`, {
      method: 'POST', headers,
      body: JSON.stringify({ input: { publishTicketInput: { ticketPlacementIds, listPrice } } })
    });
    const responseData = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(responseData?.message || 'Failed to publish tickets');
    return responseData;
  };
  
  const callReturnTicketsRest = async (ticketPlacementId: string[]) => {
    const idTokenKey = Object.keys(localStorage).find((key) => key.includes('idToken')) as string;
    const idToken = idTokenKey ? localStorage.getItem(idTokenKey) : '';
    const hasuraRole = localStorage.getItem('x-hasura-user-role');
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (idToken) headers.Authorization = `Bearer ${idToken}`;
    if (hasuraRole) headers['x-hasura-role'] = hasuraRole;
    const nodeApiBaseUrl = (process.env.REACT_APP_NODE_API_URL || config.node_api_url || '').replace(/\/+$/, '');
    if (!nodeApiBaseUrl) throw new Error('Backend API URL not configured');
    const response = await fetch(`${nodeApiBaseUrl}/api/returnTickets`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        input: { ticketPlacementId },
      }),
    });
    const responseData = await response.json().catch(() => ({}));
    if (!response.ok) {
      const message =
        responseData?.message ||
        responseData?.error ||
        responseData?.errors?.[0]?.message ||
        'Failed to return tickets';
      throw new Error(message);
    }
    return responseData;
  };

  const callMarkTicketsUnsoldRest = async (ticketPlacementIds: string[]) => {
    const idTokenKey = Object.keys(localStorage).find((key) => key.includes('idToken')) as string;
    const idToken = idTokenKey ? localStorage.getItem(idTokenKey) : '';
    const hasuraRole = localStorage.getItem('x-hasura-user-role');
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (idToken) headers.Authorization = `Bearer ${idToken}`;
    if (hasuraRole) headers['x-hasura-role'] = hasuraRole;
    const nodeApiBaseUrl = (process.env.REACT_APP_NODE_API_URL || config.node_api_url || '').replace(/\/+$/, '');
    if (!nodeApiBaseUrl) throw new Error('Backend API URL not configured');
    const response = await fetch(`${nodeApiBaseUrl}/api/markTicketsUnsold`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        input: { ticketPlacementIds },
      }),
    });
    const responseData = await response.json().catch(() => ({}));
    if (!response.ok) {
      const message =
        responseData?.message ||
        responseData?.error ||
        responseData?.errors?.[0]?.message ||
        'Failed to mark ticket as Unsold';
      throw new Error(message);
    }
    return responseData;
  };
const callMarkAsSoldRest = async (ticketPlacementId: string, soldAmount: number, logitixAmount: number, marketplaceName: string) => {
  const idTokenKey = Object.keys(localStorage).find((key) => key.includes('idToken')) as string;
  const idToken = idTokenKey ? localStorage.getItem(idTokenKey) : '';
  const hasuraRole = localStorage.getItem('x-hasura-user-role');
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (idToken) headers.Authorization = `Bearer ${idToken}`;
  if (hasuraRole) headers['x-hasura-role'] = hasuraRole;
  const nodeApiBaseUrl = (process.env.REACT_APP_NODE_API_URL || config.node_api_url || '').replace(/\/+$/, '');
  if (!nodeApiBaseUrl) throw new Error('Backend API URL not configured');
  
  console.log('📤 SENDING MARK_AS_SOLD REQUEST:', {
    ticketPlacementId,
    soldAmount,
    logitixAmount,
    marketplaceName,
    timestamp: new Date().toISOString()
  });
  
  const response = await fetch(`${nodeApiBaseUrl}/api/markAsSold`, {
    method: 'POST', 
    headers,
    body: JSON.stringify({ 
      input: { 
        ticketPlacementId, 
        soldAmount, 
        logitixAmount, 
        marketplaceName
      } 
    })
  });
  const responseData = await response.json().catch(() => ({}));
  
  if (!response.ok) {
    console.error('❌ MARK_AS_SOLD FAILED:', {
      status: response.status,
      ticketPlacementId,
      marketplaceName,
      error: responseData?.message || 'Unknown error'
    });
    throw new Error(responseData?.message || 'Failed to mark ticket as sold');
  }
  
  console.log('✅ MARK_AS_SOLD SUCCESS:', {
    ticketPlacementId,
    marketplaceName,
    response: responseData
  });
  
  return responseData;
};

  const callUpdateSettlementPaymentStatusRest = async (
    ticketPlacementIds: string[],
    paymentStatus: string | null,
  ) => {
    const idTokenKey = Object.keys(localStorage).find((key) => key.includes('idToken')) as string;
    const idToken = idTokenKey ? localStorage.getItem(idTokenKey) : '';
    const hasuraRole = localStorage.getItem('x-hasura-user-role');
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (idToken) headers.Authorization = `Bearer ${idToken}`;
    if (hasuraRole) headers['x-hasura-role'] = hasuraRole;
    const nodeApiBaseUrl = (process.env.REACT_APP_NODE_API_URL || config.node_api_url || '').replace(/\/+$/, '');
    if (!nodeApiBaseUrl) throw new Error('Backend API URL not configured');
    const response = await fetch(`${nodeApiBaseUrl}/api/updateSettlementPaymentStatus`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        input: {
          ticketPlacementIds,
          paymentStatus,
        },
      }),
    });
    const responseData = await response.json().catch(() => ({}));
    if (!response.ok) {
      const message =
        responseData?.message ||
        responseData?.error ||
        responseData?.errors?.[0]?.message ||
        'Failed to update settlement status';
      throw new Error(message);
    }
    return responseData;
  };

  const handleDelistTicketsSubmit = async () => {
    if (!pendingDelistTpIds.length || !delistEmail.trim()) return;
    setDelistRequestLoading(true);
    const hadPreVerificationTickets = pendingDelistTpIds.some((tpId) => {
      const ticket = currentTickets.find((row) => row.tpId === tpId);
      return mapStatusForMutation(ticket?.rawStatus || ticket?.status || '') === 'ToBeVerified';
    });
    try {
      const data = await callDelistTicketsRest(pendingDelistTpIds, delistEmail.trim());
      const message = String(data?.message || '');
      const normalized = message.toLowerCase();
      const status = String(data?.status || '').toLowerCase();
      const successIds: string[] = Array.isArray(data?.successTpIds) && data.successTpIds.length > 0
        ? data.successTpIds
        : pendingDelistTpIds;

      const successFromApi = Array.isArray(data?.successTpIds) ? data.successTpIds.length : 0;
      const hasBackendSuccesses = successFromApi > 0;
      const nextRawStatus = hadPreVerificationTickets ? 'ListingRequestCancelled' : 'DelistInProgress';
      const nextLabel = hadPreVerificationTickets ? 'Listing Request Cancelled' : 'Delist Requested';
      const nextTab = hadPreVerificationTickets ? 'Listing Request Cancelled' : 'Delisted';

      if (
        hasBackendSuccesses &&
        (status === 'failure' || (normalized.includes('failed') && !normalized.includes('success')))
      ) {
        showSuccess(
          message ||
            (successFromApi === pendingDelistTpIds.length
              ? (hadPreVerificationTickets ? 'Listing request cancelled successfully' : 'Delist request submitted successfully')
              : 'Delist request submitted for some tickets; refresh if counts look off.'),
        );
        updateLocalTicket(successIds, { rawStatus: nextRawStatus, status: nextLabel });
        if (activeFilterTab !== nextTab) setActiveFilterTab(nextTab);
      } else if (status === 'failure' || (normalized.includes('failed') && !normalized.includes('success'))) {
        showError(message || 'Failed to delist tickets');
      } else if (status === 'partial_success' || (normalized.includes('failed') && normalized.includes('success'))) {
        showInfo(message || 'Delist request processed with partial success');
        updateLocalTicket(successIds, { rawStatus: nextRawStatus, status: nextLabel });
        if (activeFilterTab !== nextTab) setActiveFilterTab(nextTab);
      } else if (status === 'success' || normalized.includes('success')) {
        showSuccess(message || (hadPreVerificationTickets ? 'Listing request cancelled successfully' : 'Delist request submitted successfully'));
        updateLocalTicket(successIds, { rawStatus: nextRawStatus, status: nextLabel });
        if (activeFilterTab !== nextTab) setActiveFilterTab(nextTab);
      } else {
        showInfo(message || 'Delist request processed');
        updateLocalTicket(successIds, { rawStatus: nextRawStatus, status: nextLabel });
        if (activeFilterTab !== nextTab) setActiveFilterTab(nextTab);
      }
      await refreshTicketsAndNotifications();
      setShowDelistEmailModal(false);
      setPendingDelistTpIds([]);
      setDelistEmail('');
      setDelistConfirmEmail('');
      clearBulkSelection();
    } catch (err: any) {
      showError(err?.message || 'Failed to delist tickets');
      setShowDelistEmailModal(false);
      setPendingDelistTpIds([]);
      setDelistEmail('');
      setDelistConfirmEmail('');
      clearBulkSelection();
    } finally {
      setDelistRequestLoading(false);
    }
  };
  
  const escapeRegExp = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const getTeamSeatTickets = (teamName: string, abbreviation: string) => {
    const normalizedName = (teamName || '').trim().toLowerCase();
    const normalizedAbbrev = (abbreviation || '').trim();
    const abbrRegex = normalizedAbbrev ? new RegExp(`\\b${escapeRegExp(normalizedAbbrev)}\\b`, 'i') : null;
    return currentTickets.filter((ticket) => {
      const eventName = (ticket.event || '').toLowerCase();
      if (normalizedName && eventName.includes(normalizedName)) return true;
      if (abbrRegex && abbrRegex.test(ticket.event || '')) return true;
      return false;
    });
  };
  
  const getContrastTextColor = (hexColor: string) => {
    const normalized = (hexColor || '').replace('#', '').trim();
    if (normalized.length !== 6 || Number.isNaN(Number(`0x${normalized}`))) return '#ffffff';
    const r = parseInt(normalized.slice(0, 2), 16);
    const g = parseInt(normalized.slice(2, 4), 16);
    const b = parseInt(normalized.slice(4, 6), 16);
    const yiq = (r * 299 + g * 587 + b * 114) / 1000;
    return yiq >= 140 ? '#0f172a' : '#ffffff';
  };

  const getTeamAbbreviation = (abbrev: string | null | undefined, name: string): string => {
    if (abbrev && abbrev.trim()) return abbrev.trim().substring(0, 3).toUpperCase();
    if (!name) return '?';
    const words = name.trim().split(/\s+/);
    if (words.length >= 2) {
      return (words[0][0] + words[1][0]).toUpperCase();
    }
    return name.substring(0, 3).toUpperCase();
  };
  
  // Render Functions
  const renderInlineEditForm = () => {
    if (!editFormData) return null;
    return (
      <>
        <div className={styles.formSection}>
          <h4><Building size={16} /> Business Information</h4>
          <div className={styles.formRow}>
            <div className={styles.formGroup}><label>Business Name</label><input type="text" name="business" value={editFormData.business} onChange={handleInputChange} className={styles.formInput} placeholder="Enter business name" /></div>
            <div className={styles.formGroup}><label>User Fee Percentage (%)</label><input type="number" name="userFee" value={editFormData.userFee} onChange={handleInputChange} className={styles.formInput} placeholder={editFormData.type === 'Self Pricing' ? '15' : '20'} min="0" max="100" step="0.1" disabled={!canEditPricing} /><small className={styles.inputHint}>{canEditPricing ? 'Total fee charged to user' : 'Only admin, sub-admin, or sales rep can update user fee'}</small></div>
          </div>
        </div>
        <div className={styles.formSection}>
          <h4><User size={16} /> Contact Information</h4>
          <div className={styles.formRow}>
            <div className={styles.formGroup}><label>Full Name</label><input type="text" value={editFormData.name} className={styles.formInput} placeholder="Full name" readOnly /><small className={styles.inputHint}>Read-only field</small></div>
            <div className={styles.formGroup}><label>User Type</label><select name="type" value={editFormData.type} onChange={handleInputChange} className={styles.formSelect} disabled={!canEditPricing}><option value="Self Pricing">Self Pricing</option><option value="Concierge">Concierge</option></select>{!canEditPricing && <small className={styles.inputHint}>Only admin, sub-admin, or sales rep can change user type</small>}</div>
          </div>
          <div className={styles.formRow}>
            <div className={styles.formGroup}><label>Email Address</label><input type="email" name="email" value={editFormData.email} onChange={handleInputChange} className={styles.formInput} placeholder="Enter email" /></div>
            <div className={styles.formGroup}><label>Phone Number</label><input type="text" name="phone" value={editFormData.phone} onChange={handleInputChange} className={styles.formInput} placeholder="Enter phone number" /></div>
          </div>
        </div>
        {currentUser && hasAddressValues(currentUser) && (
          <div className={styles.formSection}>
            <h4><MapPin size={16} /> Address</h4>
            <div className={styles.formGroup}><label>Street Address</label><input type="text" name="address1" value={editFormData.address1} onChange={handleInputChange} className={styles.formInput} placeholder="Street address" /></div>
            <div className={styles.formRow}>
              <div className={styles.formGroup}><label>City</label><input type="text" name="city" value={editFormData.city} onChange={handleInputChange} className={styles.formInput} placeholder="City" /></div>
              <div className={styles.formGroup}><label>State</label><input type="text" name="state" value={editFormData.state} onChange={handleInputChange} className={styles.formInput} placeholder="State" /></div>
              <div className={styles.formGroup}><label>ZIP Code</label><input type="text" name="zip" value={editFormData.zip} onChange={handleInputChange} className={styles.formInput} placeholder="ZIP code" /></div>
            </div>
            <div className={styles.formGroup}><label>Country</label><input type="text" name="country" value={editFormData.country} onChange={handleInputChange} className={styles.formInput} placeholder="Country" /></div>
          </div>
        )}
        <div className={styles.editFormFooter}><button className={styles.cancelBtn} onClick={handleCancelEdit} disabled={updateLoading}>Cancel</button><button className={styles.saveBtn} onClick={handleSaveEdit} disabled={updateLoading}>{updateLoading ? 'Saving...' : 'Save Changes'}</button></div>
      </>
    );
  };
  
  const renderDeleteConfirm = () => {
    return (
      <div className={styles.deleteConfirmOverlay}>
        <div className={styles.deleteConfirmContainer}>
          <h3>Delete User</h3>
          <p>Are you sure you want to delete this user? This action cannot be undone.</p>
          <div className={styles.deleteConfirmActions}>
            <button className={styles.cancelBtn} onClick={() => setShowDeleteConfirm(false)} disabled={deleteLoading}>Cancel</button>
            <button className={styles.deleteConfirmBtn} onClick={handleDeleteUser} disabled={deleteLoading}>{deleteLoading ? 'Deleting...' : 'Delete User'}</button>
          </div>
        </div>
      </div>
    );
  };
  
  const renderNotesSection = (type: 'qa' | 'internal' | 'external', title: string) => {
    const filteredNotes = notes.filter(note => note.type === type);
    const showNoteState = type === 'qa' ? showQANote : type === 'internal' ? showInternalNote : showExternalNote;
    const setShowNoteState = type === 'qa' ? setShowQANote : type === 'internal' ? setShowInternalNote : setShowExternalNote;
    return (
      <div className={styles.noteSection}>
        <h4>{title}</h4>
        {filteredNotes.length > 0 && (
          <div className={styles.notesList}>
            {filteredNotes.map((note) => (
              <div key={note.id} className={styles.noteItem}>
                {editingNote?.id === note.id ? (
                  <div className={styles.noteEditContainer}>
                    <textarea className={styles.textarea} rows={2} value={editingNote.content} onChange={(e) => setEditingNote({ ...editingNote, content: e.target.value })} placeholder="Edit note..." />
                    <div className={styles.noteActions}>
                      <button className={styles.saveBtn} onClick={handleUpdateNote} disabled={updateNoteLoading}>Save</button>
                      <button className={styles.cancelBtn} onClick={() => setEditingNote(null)}>Cancel</button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className={styles.noteContent}>
                      <p>{note.content}</p>
                      <div className={styles.noteMeta}>
                        <span className={styles.noteDate}>{new Date(note.createdAt).toLocaleString()}</span>
                        <span className={styles.noteAuthor}>by {note.createdBy}</span>
                      </div>
                    </div>
                    <div className={styles.noteButtons}>
                      <button className={styles.deleteNoteBtn} onClick={() => handleDeleteNote(note.id)} disabled={deleteNoteLoading}><Trash2 size={12} /></button>
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        )}
        {!showNoteState ? (
          <button className={styles.addNote} onClick={() => setShowNoteState(true)}><Plus size={14} /> Add {title.toLowerCase()} note...</button>
        ) : (
          <div className={styles.noteInputContainer}>
            <textarea
              className={styles.textarea}
              rows={3}
              placeholder={`Write ${title.toLowerCase()} note...`}
              value={newNoteContentByType[type]}
              onChange={(e) => setNewNoteContentByType((prev) => ({ ...prev, [type]: e.target.value }))}
            />
            <div className={styles.noteActions}>
              <button className={styles.saveBtn} onClick={() => handleAddNote(type)} disabled={addNoteLoading}>Save</button>
              <button className={styles.cancelBtn} onClick={() => { setShowNoteState(false); setNewNoteContentByType((prev) => ({ ...prev, [type]: '' })); }}>Cancel</button>
            </div>
          </div>
        )}
      </div>
    );
  };
  
  const renderVoidPayoutsModal = () => {
    if (!showVoidPayoutsModal) return null;
    return (
      <div className={styles.modalOverlay}>
        <div className={styles.voidModal}>
          <div className={styles.modalHeader}><AlertTriangle size={20} color="#dc2626" /><h3>Void Payouts</h3><button className={styles.closeBtn} onClick={() => setShowVoidPayoutsModal(false)}><X size={18} /></button></div>
          <div className={styles.modalBody}>
            <p className={styles.warningText}>Are you sure you want to void payouts for <strong>{currentUser?.name}</strong>?</p>
            <p className={styles.warningSubtext}>Tickets sold by this user will not generate payouts until restored.</p>
            <div className={styles.formGroup}>
              <label htmlFor="voidReason">Reason (optional)</label>
              <textarea id="voidReason" className={styles.textarea} rows={3} placeholder="e.g., Pending investigation, payment issue..." value={voidReason} onChange={(e) => setVoidReason(e.target.value)} />
            </div>
            <div className={styles.formGroup} style={{ marginTop: 8 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}><input type="checkbox" checked={addNoteOnVoid} onChange={(e) => setAddNoteOnVoid(e.target.checked)} /> <span>Add a note with this void action</span></label>
              {addNoteOnVoid && (
                <textarea className={styles.textarea} rows={3} placeholder="Optional note to attach to this void action" value={voidPayoutNote} onChange={(e) => setVoidPayoutNote(e.target.value)} />
              )}
            </div>
          </div>
          <div className={styles.modalFooter}>
            <button className={styles.cancelBtn} onClick={() => setShowVoidPayoutsModal(false)} disabled={isVoidingPayouts}>Cancel</button>
            <button className={styles.dangerBtn} onClick={handleVoidPayouts} disabled={isVoidingPayouts}>{isVoidingPayouts ? 'Voiding...' : 'Void Payouts'}</button>
          </div>
        </div>
      </div>
    );
  };
  
const renderAddTeamModal = () => {
  if (!showAddTeamModal) return null;
  
  const assignedTeamIds = new Set(preferredCategoryIds);
  const unassignedTeams = availableTeamsForUser.filter(team => !assignedTeamIds.has(team.lc_id));
  
  const leagueMap = new Map<string, string>(
    (allLeaguesData?.leagues || []).map((league: any) => [league.l_id, league.l_name || league.l_id]),
  );
  unassignedTeams.forEach(team => {
    if (team.l_id && !leagueMap.has(team.l_id)) {
      leagueMap.set(team.l_id, team.l_id);
    }
  });
  
  const uniqueLeagues = Array.from(
    new Set(
      unassignedTeams
        .map((t) => t.l_id)
        .filter((id): id is string => typeof id === 'string' && id.trim().length > 0),
    ),
  );
  
  let leagueFilteredTeams = unassignedTeams;
  if (selectedLeagueFilter !== 'all') {
    leagueFilteredTeams = unassignedTeams.filter(team => team.l_id === selectedLeagueFilter);
  }
  
  const searchLower = teamSearchQuery.toLowerCase().trim();
  const filteredTeams = leagueFilteredTeams.filter(team =>
    !searchLower || 
    (team.lc_name || '').toLowerCase().includes(searchLower) ||
    (team.lc_abbrev || '').toLowerCase().includes(searchLower)
  );
  
  return (
    <div 
      className={styles.modalOverlay} 
      style={{ 
        animation: 'fadeIn 0.2s ease-out', 
        backgroundColor: 'rgba(0,0,0,0.6)',
        backdropFilter: 'blur(2px)',
        zIndex: 1000
      }}
      onClick={() => { setShowAddTeamModal(false); setTeamSearchQuery(''); setSelectedLeagueFilter('all'); }}
    >
      <div 
        className={styles.modal} 
        style={{ 
          maxWidth: '580px', 
          width: '90%',
          animation: 'slideUp 0.3s ease-out', 
          backgroundColor: '#ffffff', 
          borderRadius: '16px', 
          overflow: 'hidden',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ 
          background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)', 
          color: 'white', 
          padding: '20px 24px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div>
            <h3 style={{ margin: 0, fontSize: '20px', fontWeight: '600' }}>Add Team to User</h3>
            <p style={{ margin: '4px 0 0', fontSize: '13px', opacity: 0.8 }}>
              Select a team to assign to this user
            </p>
          </div>
          <button 
            className={styles.closeBtn} 
            onClick={() => { setShowAddTeamModal(false); setTeamSearchQuery(''); setSelectedLeagueFilter('all'); }} 
            style={{ 
              background: 'rgba(255,255,255,0.15)', 
              border: 'none', 
              color: 'white', 
              borderRadius: '8px', 
              padding: '6px 10px', 
              cursor: 'pointer', 
              transition: 'all 0.2s',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.25)'}
            onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.15)'}
          >
            <X size={18} />
            <span style={{ fontSize: '13px' }}>Close</span>
          </button>
        </div>
        
        <div style={{ padding: '16px 24px', borderBottom: '1px solid #e2e8f0', backgroundColor: '#f8fafc' }}>
          {uniqueLeagues.length > 0 && (
            <div style={{ marginBottom: '12px' }}>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#475569', marginBottom: '8px' }}>Filter by League</label>
              <select
                value={selectedLeagueFilter}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setSelectedLeagueFilter(e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #cbd5e1',
                  borderRadius: '10px',
                  fontSize: '13px',
                  fontFamily: 'inherit',
                  backgroundColor: '#ffffff',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onFocus={(e: React.FocusEvent<HTMLSelectElement>) => {
                  e.currentTarget.style.borderColor = '#3b82f6';
                  e.currentTarget.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
                }}
                onBlur={(e: React.FocusEvent<HTMLSelectElement>) => {
                  e.currentTarget.style.borderColor = '#cbd5e1';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                <option value="all">All Leagues</option>
                {uniqueLeagues.map((leagueId) => (
                  <option key={leagueId} value={leagueId}>{leagueMap.get(leagueId) || leagueId || 'Unknown'}</option>
                ))}
              </select>
            </div>
          )}
          
          <div style={{ position: 'relative' }}>
            <Search size={16} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
            <input
              type="text"
              placeholder="Search teams by name or abbreviation..."
              value={teamSearchQuery}
              onChange={(e) => setTeamSearchQuery(e.target.value)}
              autoFocus
              style={{
                width: '100%',
                padding: '10px 12px 10px 36px',
                border: '1px solid #cbd5e1',
                borderRadius: '10px',
                fontSize: '14px',
                fontFamily: 'inherit',
                boxSizing: 'border-box',
                transition: 'all 0.2s',
                backgroundColor: '#ffffff'
              }}
              onFocus={(e) => {
                e.currentTarget.style.borderColor = '#3b82f6';
                e.currentTarget.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
              }}
              onBlur={(e) => {
                e.currentTarget.style.borderColor = '#cbd5e1';
                e.currentTarget.style.boxShadow = 'none';
              }}
            />
          </div>
          {teamSearchQuery && filteredTeams.length === 0 && !availableTeamsLoading && (
            <div style={{ fontSize: '12px', color: '#64748b', marginTop: '8px', textAlign: 'center' }}>
              No teams found matching "{teamSearchQuery}"
            </div>
          )}
        </div>
        
        <div className={styles.modalBody} style={{ backgroundColor: '#ffffff', padding: 0 }}>
          {availableTeamsLoading ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', justifyContent: 'center', padding: '60px 20px' }}>
              <Loader size={24} className={styles.spinner} style={{ color: '#3b82f6' }} />
              <span style={{ color: '#64748b', fontSize: '14px' }}>Loading available teams...</span>
            </div>
          ) : unassignedTeams.length === 0 ? (
            <div style={{ padding: '60px 20px', textAlign: 'center' }}>
              <div style={{ fontSize: '48px', marginBottom: '12px' }}>✅</div>
              <p style={{ color: '#475569', fontSize: '15px', margin: 0, fontWeight: 500 }}>
                All available teams are already assigned
              </p>
              <p style={{ color: '#94a3b8', fontSize: '13px', marginTop: '8px' }}>
                This user has been assigned to all teams
              </p>
            </div>
          ) : filteredTeams.length === 0 ? (
            <div style={{ padding: '60px 20px', textAlign: 'center' }}>
              <div style={{ fontSize: '48px', marginBottom: '12px' }}>🔍</div>
              <p style={{ color: '#475569', fontSize: '15px', margin: 0 }}>
                No teams match "{teamSearchQuery}"
              </p>
            </div>
          ) : (
            <div style={{ 
              maxHeight: '420px', 
              overflowY: 'auto', 
              padding: '16px 20px',
              scrollBehavior: 'smooth'
            }}>
              <div style={{ marginBottom: '12px', fontSize: '12px', color: '#64748b', fontWeight: 500 }}>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {filteredTeams.map((team) => {
                  const teamAbbr = getTeamAbbreviation(team.lc_abbrev, team.lc_name);
                  const isAdding = addingTeamId === team.lc_id;
                  
                  return (
                    <div
                      key={team.lc_id}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: '16px',
                        padding: '14px 10px',
                        backgroundColor: '#ffffff',
                        border: '1px solid #e2e8f0',
                        borderRadius: '12px',
                        cursor: isAdding ? 'default' : 'pointer',
                        transition: 'all 0.2s ease',
                        boxShadow: '0 1px 2px rgba(0,0,0,0.03)'
                      }}
                      onMouseEnter={(e) => {
                        if (!isAdding) {
                          e.currentTarget.style.backgroundColor = '#f8fafc';
                          e.currentTarget.style.borderColor = '#3b82f6';
                          e.currentTarget.style.boxShadow = '0 4px 12px rgba(59, 130, 246, 0.1)';
                          e.currentTarget.style.transform = 'translateX(2px)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = '#ffffff';
                        e.currentTarget.style.borderColor = '#e2e8f0';
                        e.currentTarget.style.boxShadow = '0 1px 2px rgba(0,0,0,0.03)';
                        e.currentTarget.style.transform = 'translateX(0)';
                      }}
                      onClick={() => !isAdding && handleAddTeam(team.lc_id)}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flex: 1 }}>
                        <div
                          style={{
                            width: '48px',
                            height: '48px',
                            backgroundColor: team.lc_colour_code || '#9ca3af',
                            borderRadius: '10px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: getContrastTextColor(team.lc_colour_code || '#9ca3af'),
                            fontWeight: 'bold',
                            fontSize: '13px',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                            flexShrink: 0,
                            overflow: 'hidden',
                            textTransform: 'uppercase'
                          }}
                        >
                          {teamAbbr}
                        </div>
                        
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ 
                            fontWeight: '600', 
                            color: '#1e293b', 
                            fontSize: '15px',
                            whiteSpace: 'nowrap', 
                            overflow: 'hidden', 
                            textOverflow: 'ellipsis',
                            marginBottom: '4px'
                          }}>
                            {team.lc_name}
                          </div>
                          <div style={{ 
                            fontSize: '12px', 
                            color: '#94a3b8',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                          }}>
                            <span>{team.lc_abbrev || '—'}</span>
                            {team.l_id && leagueMap.get(team.l_id) && (
                              <>
                                <span style={{ color: '#cbd5e1' }}>•</span>
                                <span style={{ color: '#64748b', fontWeight: 500 }}>
                                  {leagueMap.get(team.l_id)}
                                </span>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: '40px',
                          height: '40px',
                          backgroundColor: isAdding ? '#e2e8f0' : '#dbeafe',
                          borderRadius: '10px',
                          transition: 'all 0.2s',
                          flexShrink: 0
                        }}
                      >
                        {isAdding ? (
                          <Loader size={18} style={{ animation: 'spin 1s linear infinite', color: '#64748b' }} />
                        ) : (
                          <Plus size={20} color="#3b82f6" strokeWidth={2.5} />
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
        
        <div style={{ 
          borderTop: '1px solid #e2e8f0', 
          padding: '16px 24px', 
          display: 'flex', 
          justifyContent: 'flex-end', 
          gap: '12px', 
          backgroundColor: '#f8fafc' 
        }}>
          <button 
            className={styles.cancelBtn} 
            onClick={() => { setShowAddTeamModal(false); setTeamSearchQuery(''); setSelectedLeagueFilter('all'); }} 
            disabled={addingTeamId !== null}
            style={{ 
              padding: '10px 24px', 
              backgroundColor: '#ffffff', 
              color: '#334155', 
              border: '1px solid #cbd5e1', 
              borderRadius: '8px', 
              cursor: addingTeamId !== null ? 'not-allowed' : 'pointer', 
              fontWeight: '500', 
              fontSize: '14px', 
              transition: 'all 0.2s',
              opacity: addingTeamId !== null ? 0.6 : 1
            }}
            onMouseEnter={(e) => {
              if (addingTeamId === null) {
                e.currentTarget.style.backgroundColor = '#f1f5f9';
                e.currentTarget.style.borderColor = '#94a3b8';
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#ffffff';
              e.currentTarget.style.borderColor = '#cbd5e1';
            }}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};
  const getCtStatusBadgeClass = (status: string) => {
    const s = (status || '').toLowerCase().replace(/\s/g, '');
    if (s === 'verified' || s === 'settled') return styles.ctStatusBadgeVerified;
    if (s === 'list' || s === 'listed') return styles.ctStatusBadgeListed;
    if (s === 'sold') return styles.ctStatusBadgeSold;
    if (s === 'tobeverified') return styles.ctStatusBadgeToBeVerified;
    if (
      s === 'delist' ||
      s === 'delistinprogress' ||
      s === 'delistrequested' ||
      s === 'listingrequestcancelled' ||
      s === 'return' ||
      s === 'returned' ||
      s === 'invalid'
    ) {
      return styles.ctStatusBadgeInvalid;
    }
    if (s === 'unsold') return styles.ctStatusBadgeToBeVerified;
    return styles.ctStatusBadgeDefault;
  };

  const getCtStatusOptionClass = (statusValue: string, isSelected: boolean): string => {
    const isDelistOpt = statusValue === 'DelistInProgress' || statusValue === 'DelistRequested';
    if (isSelected) {
      return isDelistOpt
        ? `${styles.ctStatusOption} ${styles.ctStatusOptionOrangeSelected}`
        : `${styles.ctStatusOption} ${styles.ctStatusOptionSelected}`;
    }
    if (isDelistOpt) {
      return `${styles.ctStatusOption} ${styles.ctStatusOptionOrange}`;
    }
    return styles.ctStatusOption;
  };

  const renderBulkStatusModal = () => {
    if (!showBulkStatusModal || bulkSelectedTickets.length === 0) return null;
    const isBulkListTransition = normalizeStatusValue(bulkNewStatus) === 'List';
    const isBulkSoldTransition = normalizeStatusValue(bulkNewStatus) === 'Sold';
    const bulkUndoToListTickets = bulkSelectedTickets.filter((ticket) => {
      const status = normalizeStatusValue(ticket.rawStatus || ticket.status);
      return status === 'Delist' || status === 'DelistInProgress' || status === 'DelistRequested';
    });
    const bulkPublishToListTickets = bulkSelectedTickets.filter(
      (ticket) => normalizeStatusValue(ticket.rawStatus || ticket.status) === 'Verified',
    );
    const isBulkListUndoOnly =
      isBulkListTransition && bulkUndoToListTickets.length === bulkSelectedTickets.length;
    const isBulkListPublishOnly =
      isBulkListTransition && bulkPublishToListTickets.length === bulkSelectedTickets.length;
    const bulkListPricesValid = isBulkListTransition
      ? areAllBulkStatusListingPricesValid(bulkSelectedTickets, bulkStatusListingPrices)
      : true;
    const bulkSoldReady = isBulkSoldTransitionReady(
      bulkSelectedTickets,
      bulkSoldLogitixAmounts,
      bulkSoldMarketplaceId,
      bulkSoldFeeConfigReady,
    );
    const bulkSelectedMp = marketplacesList.find((m) => m.id === bulkSoldMarketplaceId);
    const bulkSeatLabels = bulkSelectedTickets
      .map((t) => (t.seatNo === '-' ? '' : t.seatNo))
      .filter(Boolean)
      .join(', ');
    const bulkConfirmDisabled =
      !bulkNewStatus ||
      bulkStatusChangeLoading ||
      bulkSoldLoading ||
      (isBulkListTransition && (!isBulkListUndoOnly && !isBulkListPublishOnly || !bulkListPricesValid)) ||
      (isBulkSoldTransition && !bulkSoldReady);
    const first = bulkSelectedTickets[0];

    return (
      <div className={styles.modalOverlay} onClick={closeBulkStatusModal}>
        <div
          className={styles.ctModalContent}
          onClick={(e) => e.stopPropagation()}
          style={{ maxWidth: isBulkListTransition || isBulkSoldTransition ? '720px' : '600px' }}
        >
          <div className={styles.ctModalHeader}>
            <div className={styles.ctModalTitleRow}>
              <RefreshRoundedIcon className={styles.ctRotateIcon} fontSize="small" />
              <span className={styles.ctModalTitle}>
                {isBulkSoldTransition
                  ? 'Change Ticket Status'
                  : `Bulk Status Change (${bulkSelectedTickets.length} tickets)`}
              </span>
            </div>
            <button type="button" className={styles.ctModalClose} onClick={closeBulkStatusModal}>×</button>
          </div>
          <div className={styles.ctModalBody}>
            {(() => {
              const validation = canBulkUpdateTickets(bulkSelectedTickets.map((t) => t.tpId));
              if (!validation.allowed && validation.reason) {
                return (
                  <div className={styles.bulkActionWarning}>
                    <AlertTriangle size={16} />
                    <span>{validation.reason}</span>
                  </div>
                );
              }
              return null;
            })()}
            <div className={styles.ctEventName}>
              {first.event}
              {bulkSelectedTickets.some((t) => t.eventId && t.eventId !== first.eventId) ? ' (Multiple Events)' : ''}
            </div>
            <div className={styles.ctEventDetails}>
              {isBulkSoldTransition ? (
                <>
                  Section {first.section === '-' ? 'N/A' : first.section} • Row {first.row === '-' ? 'N/A' : first.row}
                  {bulkSeatLabels ? ` • Seats ${bulkSeatLabels}` : ''}
                </>
              ) : (
                <>
                  Section: {first.section === '-' ? 'N/A' : first.section} • Row: {first.row === '-' ? 'N/A' : first.row}
                  {bulkSelectedTickets.some(
                    (t) =>
                      (t.section === '-' ? '' : t.section) !== (first.section === '-' ? '' : first.section) ||
                      (t.row === '-' ? '' : t.row) !== (first.row === '-' ? '' : first.row),
                  ) && ' (Multiple sections/rows - bulk update not allowed)'}
                </>
              )}
            </div>
            {isBulkSoldTransition || first.date ? (
              <div className={styles.ctEventDate}>{first.date}</div>
            ) : null}

            {isBulkSoldTransition ? (
              <div className={styles.ctCurrentStatusRow} style={{ marginTop: 8 }}>
                <span className={styles.ctCurrentStatusLabel}>Tickets selected:</span>
                <b>{bulkSelectedTickets.length}</b>
              </div>
            ) : null}

            <div className={styles.ctCurrentStatusRow}>
              <span className={styles.ctCurrentStatusLabel}>
                {isBulkSoldTransition ? 'Current Status:' : 'Current Statuses:'}
              </span>
              {isBulkSoldTransition ? (
                <span className={`${styles.ctStatusBadge} ${styles.ctStatusBadgeListed}`}>
                  {getStatusIcon('List')} Listed
                </span>
              ) : (
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  {Array.from(new Set(bulkSelectedTickets.map((t) => getStatusLabel(t.rawStatus || t.status)))).map((status) => (
                    <span key={status} className={`${styles.ctStatusBadge} ${getCtStatusBadgeClass(status)}`}>
                      {getStatusIcon(status)} {status}
                    </span>
                  ))}
                </div>
              )}
            </div>

            <div className={styles.ctNewStatusTitle}>Select New Status:</div>
            <div className={styles.ctStatusOptions}>
              {getBulkStatusTransitions(bulkSelectedTickets).map((st) => (
                <button
                  key={st.value}
                  type="button"
                  className={getCtStatusOptionClass(st.value, bulkNewStatus === st.value)}
                  onClick={() => handleBulkNewStatusSelect(st.value)}
                >
                  {st.label}
                </button>
              ))}
            </div>

            {isBulkSoldTransition ? null : bulkNewStatus ? (
              <div className={styles.ctTransitionVisual}>
                <span className={styles.ctStatusBadge}>
                  {bulkSelectedTickets.length} Ticket(s)
                </span>
                <ArrowRight size={16} className={styles.ctTransArrow} />
                <span className={`${styles.ctStatusBadge} ${getCtStatusBadgeClass(bulkNewStatus)}`}>
                  {getStatusIcon(bulkNewStatus)} {getStatusLabel(bulkNewStatus)}
                </span>
              </div>
            ) : null}

            {isBulkListTransition && isBulkListUndoOnly ? (
              <div style={{ marginTop: 12, fontSize: 13, color: '#4b5563' }}>
                Undo delist and return these tickets to the <strong>Listed</strong> tab. Enter a listing price for each seat (required if current price is $0).
              </div>
            ) : null}

            {isBulkSoldTransition && (
              <>
                {!bulkSoldFeeConfigReady ? (
                  <div style={{ margin: '12px 0', fontSize: 14 }}>Loading fee settings…</div>
                ) : null}
                <div style={{ marginBottom: 16 }}>
                  <div className={styles.ctPriceLabel} style={{ marginBottom: 8 }}>Marketplace:</div>
                  <select
                    className={styles.ctMarketplaceSelect}
                    value={bulkSoldMarketplaceId}
                    disabled={!bulkSoldFeeConfigReady || marketplacesList.length === 0}
                    onChange={(e) => setBulkSoldMarketplaceId(e.target.value)}
                  >
                    <option value="">
                      {marketplacesList.length === 0 ? 'No active marketplaces' : 'Choose a marketplace...'}
                    </option>
                    {marketplacesList.map((mp) => (
                      <option key={mp.id} value={mp.id}>{mp.name} ({mp.vl_fee_percentage}%)</option>
                    ))}
                  </select>
                </div>
                <div className={styles.ctPriceLabel} style={{ marginTop: 4 }}>Apply same Logitix amount to all (optional)</div>
                <div style={{ fontSize: 12, marginBottom: 6 }}>
                  Defaults to list price minus marketplace fee for each ticket.
                </div>
                <div className={styles.ctPriceInputWrapper} style={{ marginBottom: 12 }}>
                  <span className={styles.ctPriceDollar}>$</span>
                  <input
                    type="number"
                    className={styles.ctPriceInput}
                    value={bulkSoldCommonLogitixAmount}
                    onChange={(e) => setBulkSoldCommonLogitixAmount(e.target.value)}
                    placeholder="Enter amount"
                    min="0"
                    step="0.01"
                    disabled={!bulkSoldFeeConfigReady || !bulkSoldMarketplaceId}
                  />
                  <button
                    type="button"
                    className={styles.ctConfirmBtn}
                    style={{ marginLeft: 12, minWidth: 120 }}
                    onClick={handleApplyBulkSoldAmount}
                    disabled={!bulkSoldFeeConfigReady || !bulkSoldMarketplaceId}
                  >
                    Apply to all
                  </button>
                </div>

                <div className={styles.ctTransitionVisual}>
                  <span className={`${styles.ctStatusBadge} ${styles.ctStatusBadgeListed}`}>
                    {getStatusIcon('List')} Listed
                  </span>
                  <ArrowRight size={16} className={styles.ctTransArrow} />
                  <span className={`${styles.ctStatusBadge} ${styles.ctStatusBadgeSold}`}>
                    {getStatusIcon('Sold')} Sold
                  </span>
                </div>
              </>
            )}

            {isBulkListTransition && (
              <>
                <div className={styles.ctPriceLabel} style={{ marginTop: 12 }}>
                  {isBulkListPublishOnly && bulkSelectedTickets.length === 1
                    ? 'Listing Price (required):'
                    : 'Apply same list price to all (optional)'}
                </div>
                <div className={styles.ctPriceInputWrapper} style={{ marginBottom: 12 }}>
                  <span className={styles.ctPriceDollar}>$</span>
                  <input
                    type="number"
                    className={styles.ctPriceInput}
                    value={bulkStatusListingCommonPrice}
                    onChange={(e) => setBulkStatusListingCommonPrice(e.target.value)}
                    placeholder="Enter price"
                    min="0"
                    step="1"
                  />
                  <button
                    type="button"
                    className={styles.ctConfirmBtn}
                    style={{ marginLeft: 12, minWidth: 120 }}
                    onClick={handleApplyBulkStatusListingPrice}
                  >
                    Apply to all
                  </button>
                </div>
              </>
            )}

            <div
              style={{
                marginTop: 16,
                maxHeight: isBulkListTransition || isBulkSoldTransition ? 360 : 200,
                overflowY: 'auto',
                borderTop: '1px solid #e5e7eb',
                paddingTop: 12,
              }}
            >
              <div style={{ fontSize: 12, fontWeight: 500, marginBottom: 8 }}>
                {isBulkListTransition
                  ? 'Listing price per ticket (required):'
                  : isBulkSoldTransition
                    ? 'Amount received from Logitix per ticket:'
                    : 'Affected tickets:'}
              </div>
              {(isBulkListTransition || isBulkSoldTransition
                ? bulkSelectedTickets
                : bulkSelectedTickets.slice(0, 5)
              ).map((ticket) => {
                const currentPrice = getTicketDisplayPrice(ticket);
                  if (isBulkSoldTransition) {
                    const listPriceNum = getTicketListPrice(ticket);
                    const ticketFeeConfig = getBulkTicketSoldFeeConfig(ticket.tpId);
                    const quickPct = bulkSelectedMp
                      ? effectiveSoldQuickCutPercent(
                          bulkSelectedMp.vl_fee_percentage,
                          ticketFeeConfig.feeMode,
                          ticketFeeConfig.superClientPercent,
                        )
                      : 0;
                    const quickCutDollars = bulkSelectedMp && bulkSoldFeeConfigReady
                      ? Number(roundToFixed(listPriceNum * (quickPct / 100), 2))
                      : 0;
                    const expectedFromLogitix = bulkSelectedMp
                      ? Math.round(listPriceNum * (100 - bulkSelectedMp.vl_fee_percentage)) / 100
                      : null;
                    const logitixParsed = parseFloat(String(bulkSoldLogitixAmounts[ticket.tpId] || '').trim());
                    const hasLogitixInput = !isNaN(logitixParsed) && logitixParsed > 0;
                    const receivedForSettlement =
                      hasLogitixInput ? logitixParsed : expectedFromLogitix != null ? expectedFromLogitix : null;
                    const usingEnteredLogitix = hasLogitixInput;
                    const settlementAmount =
                      receivedForSettlement != null && bulkSelectedMp && bulkSoldFeeConfigReady
                        ? Math.round((receivedForSettlement - quickCutDollars) * 100) / 100
                        : null;
                    const clientFeeLabel =
                      ticketFeeConfig.feeMode === 'super' && ticketFeeConfig.superClientPercent != null
                        ? `${ticketFeeConfig.superClientPercent}% client fee`
                        : `${DEFAULT_CONCIERGE_PERCENT}% client fee (default)`;

                    return (
                      <div key={ticket.tpId} style={{ padding: '12px 0', borderBottom: '1px solid #e5e7eb' }}>
                        <div className={styles.ctEventDetails} style={{ marginBottom: 8 }}>
                          Seat {ticket.seatNo === '-' ? 'N/A' : ticket.seatNo} • Section {ticket.section === '-' ? 'N/A' : ticket.section} • Row {ticket.row === '-' ? 'N/A' : ticket.row}
                        </div>
                        <div className={styles.ctCurrentStatusRow} style={{ marginTop: 0, marginBottom: 8 }}>
                          <span className={styles.ctCurrentStatusLabel}>List price:</span>
                          <b>${formatNumberWithCommas(listPriceNum)}</b>
                        </div>
                        <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 8 }}>{clientFeeLabel}</div>
                        <div className={styles.ctPriceLabel}>Amount received from Logitix</div>
                        <div className={styles.ctPriceInputWrapper} style={{ marginBottom: 8 }}>
                          <span className={styles.ctPriceDollar}>$</span>
                          <input
                            type="number"
                            className={styles.ctPriceInput}
                            value={bulkSoldLogitixAmounts[ticket.tpId] ?? ''}
                            onChange={(e) =>
                              setBulkSoldLogitixAmounts({
                                ...bulkSoldLogitixAmounts,
                                [ticket.tpId]: e.target.value,
                              })
                            }
                            placeholder={
                              expectedFromLogitix != null
                                ? `e.g. ${Number(roundToFixed(expectedFromLogitix, 2))}`
                                : 'Enter amount'
                            }
                            min="0"
                            step="0.01"
                            disabled={!bulkSoldFeeConfigReady || !bulkSoldMarketplaceId}
                          />
                        </div>
                        {bulkSelectedMp && bulkSoldFeeConfigReady ? (
                          <div className={styles.ctFeeBreakdown}>
                            <div><b>Sold details (preview)</b></div>
                            <div>Marketplace: {bulkSelectedMp.name} ({bulkSelectedMp.vl_fee_percentage}%)</div>
                            <div>
                              QuickAsyst fee ({quickPct}% of list):{' '}
                              <span style={{ color: '#b91c1c' }}>
                                {quickCutDollars != null ? `-$${formatNumberWithCommas(quickCutDollars)}` : '—'}
                              </span>
                            </div>
                            <div style={{ marginTop: 8 }}>
                              <span className={styles.ctCurrentStatusLabel}>Amount settled to user</span>
                            </div>
                            <div style={{ fontSize: 20, fontWeight: 700 }}>
                              {settlementAmount != null ? `$${formatNumberWithCommas(settlementAmount)}` : '—'}
                            </div>
                            {receivedForSettlement != null && quickCutDollars != null && settlementAmount != null ? (
                              <div style={{ fontSize: 12, marginTop: 6 }}>
                                ${formatNumberWithCommas(receivedForSettlement)}{' '}
                                {usingEnteredLogitix ? 'received' : 'expected'} − $
                                {formatNumberWithCommas(quickCutDollars)} QuickAsyst fee
                                {usingEnteredLogitix ? '' : ' (preview)'}
                              </div>
                            ) : null}
                          </div>
                        ) : null}
                      </div>
                    );
                  }

                  if (isBulkListTransition) {
                    return (
                      <div
                        key={ticket.tpId}
                        style={{
                          display: 'grid',
                          gridTemplateColumns: '1.2fr 1fr',
                          gap: 10,
                          alignItems: 'center',
                          padding: '10px 0',
                          borderBottom: '1px solid #e5e7eb',
                        }}
                      >
                        <div>
                          <div style={{ fontWeight: 600, fontSize: 13 }}>Seat {ticket.seatNo === '-' ? 'N/A' : ticket.seatNo}</div>
                          <div style={{ fontSize: 12, color: '#6b7280' }}>
                            {getStatusLabel(ticket.rawStatus || ticket.status)}
                            {currentPrice != null && <> • Current: {formatCurrency(currentPrice)}</>}
                          </div>
                        </div>
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                          <span className={styles.ctPriceDollar}>$</span>
                          <input
                            type="number"
                            className={styles.ctPriceInput}
                            value={bulkStatusListingPrices[ticket.tpId] ?? ''}
                            onChange={(e) =>
                              setBulkStatusListingPrices({
                                ...bulkStatusListingPrices,
                                [ticket.tpId]: e.target.value,
                              })
                            }
                            placeholder="Price"
                            min="0"
                            step="1"
                          />
                        </div>
                      </div>
                    );
                  }

                  return (
                    <div
                      key={ticket.tpId}
                      style={{ fontSize: 12, padding: '4px 0', display: 'flex', justifyContent: 'space-between', gap: 12 }}
                    >
                      <span>Seat {ticket.seatNo === '-' ? 'N/A' : ticket.seatNo}</span>
                      <span style={{ color: '#6b7280', textAlign: 'right' }}>
                        {getStatusLabel(ticket.rawStatus || ticket.status)}
                        {currentPrice != null && <> • {formatCurrency(currentPrice)}</>}
                      </span>
                    </div>
                  );
                })}
              {!isBulkListTransition && !isBulkSoldTransition && bulkSelectedTickets.length > 5 ? (
                <div style={{ fontSize: 12, color: '#6b7280', paddingTop: 4 }}>
                  ... and {bulkSelectedTickets.length - 5} more
                </div>
              ) : null}
            </div>
          </div>
          <div className={styles.ctModalFooter}>
            <button type="button" className={styles.ctBackBtn} onClick={closeBulkStatusModal}>
              {isBulkSoldTransition ? 'Back' : 'Cancel'}
            </button>
            <button
              type="button"
              className={`${styles.ctConfirmBtn} ${bulkConfirmDisabled ? styles.ctConfirmBtnDisabled : ''}`}
              disabled={bulkConfirmDisabled}
              onClick={handleBulkStatusChange}
            >
              {bulkStatusChangeLoading || bulkSoldLoading
                ? 'Saving…'
                : isBulkSoldTransition
                  ? `Confirm ${bulkSelectedTickets.length} Ticket(s)`
                  : `Update ${bulkSelectedTickets.length} Ticket(s)`}
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderChangeStatusModal = () => {
    if (!showChangeStatusModal || !changeStatusTicket) return null;
    const currentToken = getTicketTransitionStatus(changeStatusTicket);
    const transitions = getStatusTransitions(currentToken);
    const currentLabel = getStatusLabel(currentToken);
    const selectedLabel = selectedNewStatus ? getStatusLabel(selectedNewStatus) : '';
  
    return (
      <div className={styles.modalOverlay} onClick={() => setShowChangeStatusModal(false)}>
        <div className={styles.ctModalContent} onClick={e => e.stopPropagation()}>
          <div className={styles.ctModalHeader}>
            <div className={styles.ctModalTitleRow}>
              <RefreshRoundedIcon className={styles.ctRotateIcon} fontSize="small" />
              <span className={styles.ctModalTitle}>Change Ticket Status</span>
            </div>
            <button className={styles.ctModalClose} onClick={() => setShowChangeStatusModal(false)}>×</button>
          </div>
          <div className={styles.ctModalBody}>
            <div className={styles.ctEventName}>{changeStatusTicket.event}</div>
            <div className={styles.ctEventDetails}>
              Section {changeStatusTicket.section === '-' ? 'N/A' : changeStatusTicket.section} • Row {changeStatusTicket.row === '-' ? 'N/A' : changeStatusTicket.row} • Seats {changeStatusTicket.seatNo === '-' ? 'N/A' : changeStatusTicket.seatNo}
            </div>
            <div className={styles.ctEventDate}>{changeStatusTicket.date}</div>
  
            <div className={styles.ctCurrentStatusRow}>
              <span className={styles.ctCurrentStatusLabel}>Current Status:</span>
              <span className={`${styles.ctStatusBadge} ${getCtStatusBadgeClass(currentToken)}`}>
                {getStatusIcon(currentToken)} {currentLabel}
              </span>
            </div>
  
            <div className={styles.ctNewStatusTitle}>Select New Status:</div>
            <div className={styles.ctStatusOptions}>
              {transitions.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  className={getCtStatusOptionClass(opt.value, selectedNewStatus === opt.value)}
                  onClick={() => setSelectedNewStatus(opt.value)}
                >
                  {opt.label}
                </button>
              ))}
            </div>
  
            {selectedNewStatus && (
              <div className={styles.ctTransitionVisual}>
                <span className={`${styles.ctStatusBadge} ${getCtStatusBadgeClass(currentToken)}`}>
                  {getStatusIcon(currentToken)} {currentLabel}
                </span>
                <ArrowRight size={16} className={styles.ctTransArrow} />
                <span className={`${styles.ctStatusBadge} ${getCtStatusBadgeClass(selectedNewStatus)}`}>
                  {getStatusIcon(selectedNewStatus)} {selectedLabel}
                </span>
              </div>
            )}
          </div>
          <div className={styles.ctModalFooter}>
            <button className={styles.ctBackBtn} onClick={() => setShowChangeStatusModal(false)}>Back</button>
            <button
              className={`${styles.ctConfirmBtn} ${(!selectedNewStatus || changeStatusLoading) ? styles.ctConfirmBtnDisabled : ''}`}
              disabled={!selectedNewStatus || changeStatusLoading}
              onClick={handleChangeStatusConfirm}
            >
              {changeStatusLoading ? 'Saving...' : 'Confirm Change'}
            </button>
          </div>
        </div>
      </div>
    );
  };
  
const renderEditTicketModal = () => {
  if (!showEditTicketModal || !editTicket) return null;
  return (
    <div className={styles.modalOverlay} onClick={() => setShowEditTicketModal(false)}>
      <div className={styles.modalContent} onClick={e => e.stopPropagation()} style={{ maxWidth: '480px' }}>
        <div className={styles.modalHeader}>
          <h3><Edit size={18} /> Edit Ticket Details</h3>
          <button className={styles.modalClose} onClick={() => setShowEditTicketModal(false)}><X size={18} /></button>
        </div>
        <div className={styles.modalBody}>
          <div className={styles.modalEventInfo}>
            <div className={styles.modalEventName}>{editTicket.event}</div>
            <div>{editTicket.date}</div>
          </div>
          <div className={styles.editField}>
            <label>Section</label>
            <input type="text" value={editTicketForm.section} onChange={e => setEditTicketForm({ ...editTicketForm, section: e.target.value })} placeholder="Section" />
          </div>
          <div className={styles.editField}>
            <label>Row</label>
            <input type="text" value={editTicketForm.row} onChange={e => setEditTicketForm({ ...editTicketForm, row: e.target.value })} placeholder="Row" />
          </div>
          <div className={styles.editField}>
            <label>Seat Number</label>
            <input type="text" value={editTicketForm.seatNo} onChange={e => setEditTicketForm({ ...editTicketForm, seatNo: e.target.value })} placeholder="Seat Number" />
          </div>
          <div className={styles.editField}>
          </div>
        </div>
        <div className={styles.modalFooter}>
          <button className={styles.modalCancel} onClick={() => setShowEditTicketModal(false)}>Cancel</button>
          <button className={styles.modalConfirm} onClick={handleEditTicketSave} disabled={editTicketLoading}>
            {editTicketLoading ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
};
  
  const renderListedModal = () => {
    if (!showListedModal || !listedModalTicket) return null;
    const currentSt = listedModalTicket.rawStatus || listedModalTicket.status;
    const currentLabel = getStatusLabel(currentSt);
    const getBadgeClass = (st: string) => {
      const s = st.toLowerCase().replace(/\s/g, '');
      if (s === 'verified') return styles.ctStatusBadgeVerified;
      if (s === 'list') return styles.ctStatusBadgeListed;
      return styles.ctStatusBadgeDefault;
    };
  
    return (
      <div className={styles.modalOverlay} onClick={() => setShowListedModal(false)}>
        <div className={styles.ctModalContent} onClick={e => e.stopPropagation()}>
          <div className={styles.ctModalHeader}>
            <div className={styles.ctModalTitleRow}>
              <RefreshRoundedIcon className={styles.ctRotateIcon} fontSize="small" />
              <span className={styles.ctModalTitle}>Change Ticket Status</span>
            </div>
            <button className={styles.ctModalClose} onClick={() => setShowListedModal(false)}>×</button>
          </div>
          <div className={styles.ctModalBody}>
            <div className={styles.ctEventName}>{listedModalTicket.event}</div>
            <div className={styles.ctEventDetails}>
              Section {listedModalTicket.section === '-' ? 'N/A' : listedModalTicket.section} • Row {listedModalTicket.row === '-' ? 'N/A' : listedModalTicket.row} • Seats {listedModalTicket.seatNo === '-' ? 'N/A' : listedModalTicket.seatNo}
            </div>
            <div className={styles.ctEventDate}>{listedModalTicket.date}</div>
  
            <div className={styles.ctCurrentStatusRow}>
              <span className={styles.ctCurrentStatusLabel}>Current Status:</span>
              <span className={`${styles.ctStatusBadge} ${getBadgeClass(currentSt)}`}>
                {getStatusIcon(currentSt)} {currentLabel}
              </span>
            </div>
  
            <div className={styles.ctNewStatusTitle}>Select New Status:</div>
            <div className={styles.ctStatusOptions}>
              <button className={`${styles.ctStatusOption} ${styles.ctStatusOptionSelected}`}>Listed</button>
            </div>
  
            <div className={styles.ctTransitionVisual}>
              <span className={`${styles.ctStatusBadge} ${getBadgeClass(currentSt)}`}>
                {getStatusIcon(currentSt)} {currentLabel}
              </span>
              <ArrowRight size={16} className={styles.ctTransArrow} />
              <span className={`${styles.ctStatusBadge} ${styles.ctStatusBadgeListed}`}>
                {getStatusIcon('List')} Listed
              </span>
            </div>
  
            <div className={styles.ctPriceLabel}>Listing Price (required):</div>
            <div className={styles.ctPriceInputWrapper}>
              <span className={styles.ctPriceDollar}>$</span>
              <input
                type="number"
                className={styles.ctPriceInput}
                value={listedPrice}
                onChange={e => setListedPrice(e.target.value)}
                placeholder="Enter listing price"
                min="0"
                step="1"
              />
            </div>
          </div>
          <div className={styles.ctModalFooter}>
            <button className={styles.ctBackBtn} onClick={() => setShowListedModal(false)}>Back</button>
            <button
              className={`${styles.ctConfirmBtn} ${(!listedPrice || listedModalLoading) ? styles.ctConfirmBtnDisabled : ''}`}
              disabled={!listedPrice || listedModalLoading}
              onClick={handleListedModalConfirm}
            >
              {listedModalLoading ? 'Saving...' : 'Confirm Change'}
            </button>
          </div>
        </div>
      </div>
    );
  };
  
  const renderSoldModal = () => {
    if (!showSoldModal || !soldModalTicket) return null;
    const listPriceNum = parseCurrency(soldModalTicket.price);
    const selectedMp = marketplacesList.find(m => m.id === soldMarketplaceId);
    const expectedFromLogitix = selectedMp ? Math.round(listPriceNum * (100 - selectedMp.vl_fee_percentage)) / 100 : null;
    const quickPct = selectedMp && soldFeeConfigReady
      ? effectiveSoldQuickCutPercent(selectedMp.vl_fee_percentage, soldFeeMode, soldSuperClientPercent) : 0;
    const quickCutDollars = selectedMp && listPriceNum && soldFeeConfigReady
      ? Number(roundToFixed(listPriceNum * (quickPct / 100), 2)) : 0;
    const logitixParsed = parseFloat(soldLogitixAmount);
    const hasLogitixInput = !isNaN(logitixParsed) && logitixParsed > 0;
    const receivedForSettlement = hasLogitixInput ? logitixParsed : expectedFromLogitix;
    const settlementAmount = receivedForSettlement != null && quickCutDollars != null
      ? Math.round((receivedForSettlement - quickCutDollars) * 100) / 100 : null;
    const confirmDisabled = !soldFeeConfigReady || soldModalLoading || !soldMarketplaceId ||
      !soldLogitixAmount.trim() || settlementAmount == null || settlementAmount <= 0;
  
    return (
      <div className={styles.modalOverlay} onClick={closeSoldModal}>
        <div className={styles.ctModalContent} onClick={e => e.stopPropagation()}>
          <div className={styles.ctModalHeader}>
            <div className={styles.ctModalTitleRow}>
              <RefreshRoundedIcon className={styles.ctRotateIcon} fontSize="small" />
              <span className={styles.ctModalTitle}>Change Ticket Status</span>
            </div>
            <button type="button" className={styles.ctModalClose} onClick={closeSoldModal}>×</button>
          </div>
          <div className={styles.ctModalBody}>
            <div className={styles.ctEventName}>{soldModalTicket.event}</div>
            <div className={styles.ctEventDetails}>
              Section {soldModalTicket.section === '-' ? 'N/A' : soldModalTicket.section} • Row {soldModalTicket.row === '-' ? 'N/A' : soldModalTicket.row} • Seats {soldModalTicket.seatNo === '-' ? 'N/A' : soldModalTicket.seatNo}
            </div>
            <div className={styles.ctEventDate}>{soldModalTicket.date}</div>
  
            <div className={styles.ctCurrentStatusRow} style={{ marginTop: 8 }}>
              <span className={styles.ctCurrentStatusLabel}>List price:</span>
              <b>${formatNumberWithCommas(listPriceNum)}</b>
            </div>
  
            <div className={styles.ctCurrentStatusRow}>
              <span className={styles.ctCurrentStatusLabel}>Current Status:</span>
              <span className={`${styles.ctStatusBadge} ${styles.ctStatusBadgeListed}`}>
                {getStatusIcon('List')} Listed
              </span>
            </div>
  
            <div className={styles.ctNewStatusTitle}>Select New Status:</div>
            <div className={styles.ctStatusOptions}>
              <button type="button" className={`${styles.ctStatusOption} ${styles.ctStatusOptionSelected}`}>Sold</button>
            </div>
  
            {!soldFeeConfigReady && <div style={{ margin: '12px 0', fontSize: 14 }}>Loading fee settings…</div>}
  
            <div style={{ marginBottom: 16 }}>
              <div className={styles.ctPriceLabel} style={{ marginBottom: 8 }}>Marketplace:</div>
              <select
                className={styles.ctMarketplaceSelect}
                value={soldMarketplaceId}
                disabled={!soldFeeConfigReady || marketplacesList.length === 0}
                onChange={e => setSoldMarketplaceId(e.target.value)}
              >
                <option value="">{marketplacesList.length === 0 ? 'No active marketplaces available' : 'Choose a marketplace...'}</option>
                {marketplacesList.map(mp => (
                  <option key={mp.id} value={mp.id}>{mp.name} ({mp.vl_fee_percentage}%)</option>
                ))}
              </select>
            </div>
  
            <div style={{ marginBottom: 16 }}>
              <div className={styles.ctPriceLabel}>Amount received from Logitix</div>
              <div style={{ fontSize: 12, marginBottom: 6 }}>Defaults to list price minus marketplace fee (edit if your payout differs).</div>
              <div className={styles.ctPriceInputWrapper}>
                <span className={styles.ctPriceDollar}>$</span>
                <input
                  type="number"
                  className={styles.ctPriceInput}
                  value={soldLogitixAmount}
                  onChange={e => setSoldLogitixAmount(e.target.value)}
                  placeholder={expectedFromLogitix != null ? `e.g. ${Number(roundToFixed(expectedFromLogitix, 2))}` : 'Enter amount'}
                  min="0"
                  step="0.01"
                  disabled={!soldFeeConfigReady || !soldMarketplaceId}
                />
              </div>
            </div>
  
            <div className={styles.ctTransitionVisual}>
              <span className={`${styles.ctStatusBadge} ${styles.ctStatusBadgeListed}`}>
                {getStatusIcon('List')} Listed
              </span>
              <ArrowRight size={16} className={styles.ctTransArrow} />
              <span className={`${styles.ctStatusBadge} ${styles.ctStatusBadgeSold}`}>
                {getStatusIcon('Sold')} Sold
              </span>
            </div>
  
            {selectedMp && soldFeeConfigReady && (
              <div className={styles.ctFeeBreakdown}>
                <div><b>Sold details (preview)</b></div>
                <div>Marketplace: {selectedMp.name} ({selectedMp.vl_fee_percentage}%)</div>
                <div>QuickAsyst fee ({quickPct}% of list): <span className={styles.deductionAmount}>{quickCutDollars != null ? `-$${formatNumberWithCommas(quickCutDollars)}` : '—'}</span></div>
                <div style={{ marginTop: 8 }}><span className={styles.ctCurrentStatusLabel}>Amount settled to user</span></div>
                <div style={{ fontSize: 20, fontWeight: 700 }}>{settlementAmount != null ? `$${formatNumberWithCommas(settlementAmount)}` : '—'}</div>
                {receivedForSettlement != null && quickCutDollars != null && settlementAmount != null && (
                  <div style={{ fontSize: 12, marginTop: 6 }}>
                    ${formatNumberWithCommas(receivedForSettlement)} {hasLogitixInput ? 'received' : 'expected'} − ${formatNumberWithCommas(quickCutDollars)} QuickAsyst fee
                  </div>
                )}
              </div>
            )}
          </div>
          <div className={styles.ctModalFooter}>
            <button type="button" className={styles.ctBackBtn} onClick={closeSoldModal}>Back</button>
            <button
              type="button"
              className={`${styles.ctConfirmBtn} ${confirmDisabled ? styles.ctConfirmBtnDisabled : ''}`}
              disabled={confirmDisabled}
              onClick={handleSoldModalConfirm}
            >
              {soldModalLoading ? 'Saving...' : 'Confirm Change'}
            </button>
          </div>
        </div>
      </div>
    );
  };
  
  const renderPriceModal = () => {
    if (!showPriceModal || !priceModalTicket) return null;
    const bulkCount = bulkPriceTpIds.length;
    const currentPriceDisplay = priceModalTicket.price || '$0';
    return (
      <div className={styles.modalOverlay} onClick={() => { setShowPriceModal(false); setBulkPriceTpIds([]); }}>
        <div className={styles.modalContent} onClick={e => e.stopPropagation()}>
          <div className={styles.modalHeader}>
            <h3>Confirm Price Change</h3>
            <button className={styles.modalClose} onClick={() => setShowPriceModal(false)}><X size={18} /></button>
          </div>
          <div className={styles.modalBody}>
            <div className={styles.modalEventInfo}>
              <div className={styles.modalEventName}>{priceModalTicket.event}</div>
              <div className={styles.modalEventDetails}>
                Section {priceModalTicket.section || 'N/A'} • Row {priceModalTicket.row || 'N/A'} • Seats {priceModalTicket.seatNo || 'N/A'}
              </div>
            </div>
            <div className={styles.priceCompare}>
              <div className={styles.currentPrice}>
                <div>Current Price</div>
                <div>{currentPriceDisplay}</div>
              </div>
              <div className={styles.newPriceInput}>
                <div>New Price</div>
                <input
                  type="number"
                  value={newPrice}
                  onChange={(e) => setNewPrice(e.target.value)}
                  placeholder="0"
                  min="0"
                />
              </div>
            </div>
            <div className={styles.selectedCountInfo}>
              <CheckSquare size={12} /> {bulkCount > 0 ? bulkCount : 1} ticket(s) selected
            </div>
          </div>
          <div className={styles.modalFooter}>
            <button className={styles.modalCancel} onClick={() => { setShowPriceModal(false); setBulkPriceTpIds([]); }}>Cancel</button>
            <button className={styles.modalConfirm} onClick={handlePriceModalConfirm} disabled={priceModalLoading}>
              {priceModalLoading ? 'Updating...' : 'Confirm Price Change'}
            </button>
          </div>
        </div>
      </div>
    );
  };
  
  const renderDelistEmailModal = () => {
    if (!showDelistEmailModal) return null;
    const isFormValid = delistEmail && delistConfirmEmail && emailRegexPattern.test(delistEmail) && delistEmail === delistConfirmEmail;
    return (
      <div className={styles.modalOverlay} onClick={() => setShowDelistEmailModal(false)}>
        <div className={styles.modalContent} onClick={(e) => e.stopPropagation()} style={{ maxWidth: '450px' }}>
          <div className={styles.modalHeader}>
            <div>
              <h3><Package size={18} /> Delist Request</h3>
              <p>Please provide the email address to return the tickets to</p>
            </div>
            <button className={styles.modalClose} onClick={() => setShowDelistEmailModal(false)}><X size={18} /></button>
          </div>
          <div className={styles.modalBody}>
            <div className={styles.delistCount}>Delisting <strong>{pendingDelistTpIds.length}</strong> ticket(s)</div>
            <div className={styles.editField}>
              <label>Email *</label>
              <input type="email" placeholder="name@company.com" value={delistEmail} onChange={(e) => setDelistEmail(e.target.value)} className={styles.formInput} />
            </div>
            <div className={styles.editField}>
              <label>Confirm Email *</label>
              <input type="email" placeholder="name@company.com" value={delistConfirmEmail} onChange={(e) => setDelistConfirmEmail(e.target.value)} className={styles.formInput} />
              {delistEmail && delistConfirmEmail && delistEmail !== delistConfirmEmail && (<small className={styles.errorText}>Emails do not match</small>)}
              {delistEmail && !emailRegexPattern.test(delistEmail) && (<small className={styles.errorText}>Enter a valid email address</small>)}
            </div>
          </div>
          <div className={styles.modalFooter}>
            <button className={styles.modalCancel} onClick={() => setShowDelistEmailModal(false)}>Cancel</button>
            <button className={styles.modalConfirm} onClick={() => void handleDelistTicketsSubmit()} disabled={!isFormValid || delistRequestLoading}>
              {delistRequestLoading ? 'Processing...' : 'Submit Delist Request'}
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderBulkPriceModal = () => {
    if (!showBulkPriceModal || bulkPriceModalTickets.length === 0) return null;

    const allPricesValid = areAllBulkPricesValid(bulkPriceModalTickets, bulkPrices);

    return (
      <div className={styles.modalOverlay} onClick={() => setShowBulkPriceModal(false)}>
        <div className={styles.ctModalContent} onClick={(e) => e.stopPropagation()} style={{ maxWidth: '750px' }}>
          <div className={styles.ctModalHeader}>
            <div className={styles.ctModalTitleRow}>
              <DollarSign className={styles.ctRotateIcon} size={20} />
              <span className={styles.ctModalTitle}>Bulk Change Price</span>
            </div>
            <button className={styles.ctModalClose} onClick={() => setShowBulkPriceModal(false)}>×</button>
          </div>
          <div className={styles.ctModalBody}>
            <div className={styles.ctEventName}>{bulkPriceModalTickets[0]?.event || 'Selected Tickets'}</div>
            <div className={styles.ctEventDetails}>
              {bulkPriceModalTickets.length} ticket(s) selected • Section {bulkPriceModalTickets[0]?.section || 'N/A'} • Row {bulkPriceModalTickets[0]?.row || 'N/A'}
            </div>

            <div className={styles.ctPriceLabel} style={{ marginTop: 16, marginBottom: 8 }}>Apply same price to all selected tickets (optional)</div>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 16 }}>
              <div className={styles.ctPriceInputWrapper} style={{ flex: 1 }}>
                <span className={styles.ctPriceDollar}>$</span>
                <input
                  type="number"
                  className={styles.ctPriceInput}
                  value={bulkCommonPrice}
                  onChange={(e) => setBulkCommonPrice(e.target.value)}
                  placeholder="Enter price"
                  min="0"
                  step="1"
                />
              </div>
              <button 
                className={styles.ctConfirmBtn} 
                style={{ minWidth: 140 }}
                onClick={handleApplyBulkCommonPrice}
              >
                <span>Apply to all</span>
              </button>
            </div>

            <div style={{ borderTop: '2px solid #e5e7eb', paddingTop: 16, marginTop: 16 }}>
              <div className={styles.ctPriceLabel} style={{ marginBottom: 12 }}>Individual ticket prices</div>
              <div style={{ maxHeight: '400px', overflowY: 'auto', display: 'grid', gap: 12 }}>
                {bulkPriceModalTickets.map((ticket) => {
                  const currentPrice = getTicketListPrice(ticket);
                  return (
                    <div 
                      key={ticket.tpId} 
                      style={{
                        display: 'grid',
                        gridTemplateColumns: '1.8fr 0.8fr',
                        gap: 12,
                        alignItems: 'center',
                        padding: '12px',
                        backgroundColor: '#f9fafb',
                        borderRadius: '6px',
                        border: '1px solid #e5e7eb'
                      }}
                    >
                      <div>
                        <div style={{ fontWeight: 600, fontSize: 13 }}>Seat {ticket.seatNo || 'N/A'}</div>
                        <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>
                          {ticket.event || 'N/A'}
                        </div>
                        <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>
                          Current price: ${currentPrice > 0 ? currentPrice.toFixed(2) : '0.00'}
                        </div>
                      </div>
                      <div className={styles.ctPriceInputWrapper}>
                        <span className={styles.ctPriceDollar}>$</span>
                        <input
                          type="number"
                          className={styles.ctPriceInput}
                          value={bulkPrices[ticket.tpId] ?? ''}
                          onChange={(e) =>
                            setBulkPrices({
                              ...bulkPrices,
                              [ticket.tpId]: e.target.value,
                            })
                          }
                          placeholder="0"
                          min="0"
                          step="1"
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
          <div className={styles.ctModalFooter}>
            <button className={styles.ctBackBtn} onClick={() => setShowBulkPriceModal(false)}>Cancel</button>
            <button
              className={`${styles.ctConfirmBtn} ${bulkPriceLoading ? styles.ctConfirmBtnDisabled : ''}`}
              disabled={bulkPriceLoading || !allPricesValid}
              onClick={handleConfirmBulkPricing}
            >
              {bulkPriceLoading ? 'Saving…' : `Update ${bulkPriceModalTickets.length} Ticket${bulkPriceModalTickets.length > 1 ? 's' : ''}`}
            </button>
          </div>
        </div>
      </div>
    );
  };
  
const renderTeamSeatsModal = () => {
  if (!selectedTeamForSeats) return null;
  const teamTickets = getTeamSeatTickets(selectedTeamForSeats.name, selectedTeamForSeats.abbreviation);
  const seatInfoKey = `${userId || 'unknown'}:${selectedTeamForSeats.id}`;
  const savedSeatRows: SeatInfoRow[] = Array.isArray(userSavedSeatInfoData?.user_saved_seat_info)
    ? userSavedSeatInfoData.user_saved_seat_info.map((row: any) => ({
        id: `saved-${row.ussi_id}`, event: row.team_name || selectedTeamForSeats.name, date: '-',
        section: row.section || '-', row: row.seat_row || '-', seatNo: row.start_seat || '-',
        source: 'saved' as const, ussiId: row.ussi_id
      })) : [];
  const ticketSeatRows: SeatInfoRow[] = teamTickets.map((ticket) => ({
    id: `ticket-${ticket.tpId}`, event: ticket.event, date: ticket.date,
    section: ticket.section, row: ticket.row, seatNo: ticket.seatNo,
    source: 'ticket' as const, ticketId: ticket.tpId
  }));
  let displayRows: SeatInfoRow[] = ticketSeatRows.length > 0 ? ticketSeatRows : savedSeatRows;
  if (localSeatInfoOverrides[seatInfoKey]) displayRows = localSeatInfoOverrides[seatInfoKey];
  
  const startSeatEdit = (row: SeatInfoRow) => {
    setEditingSeatTicketId(row.id);
    setSeatEditForm({ section: row.section === '-' ? '' : row.section, row: row.row === '-' ? '' : row.row, seatNo: row.seatNo === '-' ? '' : row.seatNo });
  };
  const cancelSeatEdit = () => { setEditingSeatTicketId(null); setSeatEditForm({ section: '', row: '', seatNo: '' }); };
  const saveSeatEdit = async (rowItem: SeatInfoRow) => {
    const section = seatEditForm.section.trim();
    const row = seatEditForm.row.trim();
    const seatNo = Number(seatEditForm.seatNo);
    if (!section || !row || !Number.isFinite(seatNo) || seatNo <= 0) {
      showError('Section, row, and a valid seat number are required.');
      return;
    }
    try {
      setSavingSeatTicketId(rowItem.id);
      if (!selectedTeamForSeats || !isValidUuid(selectedTeamForSeats.id) || !userId) {
        showError('Cannot save seat info for this team.');
        return;
      }
      if (rowItem.source === 'saved' && rowItem.ussiId) {
        await updateUserSavedSeatInfo({ variables: { ussi_id: rowItem.ussiId, section, seat_row: row, start_seat: String(Math.trunc(seatNo)), end_seat: String(Math.trunc(seatNo)) } });
      } else {
        await insertUserSavedSeatInfo({ variables: { u_id: userId, lc_id: selectedTeamForSeats.id, team_name: selectedTeamForSeats.name, section, seat_row: row, start_seat: String(Math.trunc(seatNo)), end_seat: String(Math.trunc(seatNo)) } });
      }
      await refetchUserSavedSeatInfo();
      setLocalSeatInfoOverrides((prev) => {
        const updatedRows = (prev[seatInfoKey] || displayRows).map(seatRow => seatRow.id === rowItem.id ? { ...seatRow, section, row, seatNo: String(Math.trunc(seatNo)) } : seatRow);
        return { ...prev, [seatInfoKey]: updatedRows };
      });
      showSuccess('Seat info saved successfully.');
      cancelSeatEdit();
    } catch (error: any) {
      const message = (error?.message || '').toLowerCase();
      if (message.includes('query is not allowed') || message.includes('allowlist')) {
        setLocalSeatInfoOverrides((prev) => {
          const updatedRows = (prev[seatInfoKey] || displayRows).map(seatRow => seatRow.id === rowItem.id ? { ...seatRow, section, row, seatNo: String(Math.trunc(seatNo)) } : seatRow);
          return { ...prev, [seatInfoKey]: updatedRows };
        });
        showSuccess('Seat info saved locally (allowlist restriction).');
        cancelSeatEdit();
      } else {
        showError(error?.message || 'Failed to save seat info.');
      }
    } finally { setSavingSeatTicketId(null); }
  };
  
  return (
    <div className={styles.teamSeatModalOverlay} onClick={() => setSelectedTeamForSeats(null)}>
      <div className={styles.teamSeatModal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.teamSeatModalHeader}>
          <h3>{selectedTeamForSeats.name} - Seat Info</h3>
          <button 
            className={styles.closeBtn} 
            onClick={() => setSelectedTeamForSeats(null)}
          
          >
            <X size={20} />
          </button>
        </div>
        <div className={styles.teamSeatModalBody}>
          {userSavedSeatInfoLoading ? (
            <div className={styles.loadingSmall}><Loader size={20} className={styles.spinner} /><span>Loading seat info...</span></div>
          ) : displayRows.length > 0 ? (
            <div className={styles.teamSeatTableWrapper}>
              <table className={styles.teamSeatTable}>
                <thead>
                  <tr>
                    <th>Event</th>
                    <th>Date & Time</th>
                    <th>Section</th>
                    <th>Row</th>
                    <th>Seat</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {displayRows.map((rowItem) => (
                    <tr key={`${selectedTeamForSeats.id}-${rowItem.id}`}>
                      <td>{rowItem.event}</td>
                      <td>
                        {rowItem.date !== '-' ? (
                          <>
                            {new Date(rowItem.date).toLocaleDateString()} {' '}
                            {new Date(rowItem.date).toLocaleTimeString([], {
                              hour: '2-digit',
                              minute: '2-digit',
                              hour12: true,
                            })}
                          </>
                        ) : '-'}
                      </td>
                      <td>{editingSeatTicketId === rowItem.id ? <input className={styles.teamSeatInput} value={seatEditForm.section} onChange={(e) => setSeatEditForm(prev => ({ ...prev, section: e.target.value }))} /> : rowItem.section}</td>
                      <td>{editingSeatTicketId === rowItem.id ? <input className={styles.teamSeatInput} value={seatEditForm.row} onChange={(e) => setSeatEditForm(prev => ({ ...prev, row: e.target.value }))} /> : rowItem.row}</td>
                      <td>{editingSeatTicketId === rowItem.id ? <input className={styles.teamSeatInput} type="number" min="1" value={seatEditForm.seatNo} onChange={(e) => setSeatEditForm(prev => ({ ...prev, seatNo: e.target.value }))} /> : rowItem.seatNo}</td>
                      <td>
                        {editingSeatTicketId === rowItem.id ? (
                          <div className={styles.teamSeatActionGroup}>
                            <button className={styles.teamSeatSaveBtn} onClick={() => saveSeatEdit(rowItem)} disabled={savingSeatTicketId === rowItem.id}>{savingSeatTicketId === rowItem.id ? 'Saving...' : 'Save'}</button>
                            <button className={styles.teamSeatCancelBtn} onClick={cancelSeatEdit} disabled={savingSeatTicketId === rowItem.id}>Cancel</button>
                          </div>
                        ) : (
                          <button className={styles.teamSeatEditBtn} onClick={() => startSeatEdit(rowItem)}>Edit</button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className={styles.emptyState}>
              <p>No seat info found for this team.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
  // Computed values for rendering
  const currentUser = effectiveUserRecord ? transformUserData(effectiveUserRecord, superUserData?.super_user?.[0]?.percentage) : null;
  const emailEnabledResolved = (emailStatusData?.user?.[0]?.u_email_enabled) ?? (currentUser?.emailEnabled ?? true);
  const currentTickets = localTickets;
  const filteredTickets = getFilteredTickets();
  const showSoldPriceColumns = activeFilterTab === 'Sold';
  const selectedTickets = filteredTickets.filter((t) => bulkSelectedTicketIds.has(t.tpId));
  const areSelectedTicketsAllListed = selectedTickets.length > 0 && selectedTickets.every((t) => normalizeStatusValue(t.rawStatus || t.status) === 'List');
  const isTicketsLoading = ticketsLoading || (isGetUserTicketsAllowlistError && fallbackTicketsLoading);
  const currentPerformance = transformPerformanceData(performanceData);
  const tierThresholds = [{ label: 'VIP', threshold: '$20k+', minAmount: 20000 }, { label: 'Silver', threshold: '$10k+', minAmount: 10000 }, { label: 'Bronze', threshold: '$5k+', minAmount: 5000 }];
  const rawSalesAmount = effectiveUserRecord?.tickets_sold_amount || 0;
  const activeTier = tierThresholds.find(t => rawSalesAmount >= t.minAmount)?.label || null;
  const analytics = analyticsData?.user_analytics?.[0] || {};
  const activeCount = currentTickets.filter(t => ['list', 'listed'].includes(normalizeStatusToken(t.rawStatus))).length;
  const soldCount = currentTickets.filter(t => normalizeStatusToken(t.rawStatus) === 'sold').length;
  const delistedCount = currentTickets.filter((t) =>
    ['delist', 'delisted', 'delistinprogress', 'delistrequested', 'return'].includes(
      normalizeStatusToken(t.rawStatus || t.status),
    ),
  ).length;
  const unsoldCount = currentTickets.filter(t => normalizeStatusToken(t.rawStatus) === 'unsold').length;
  const verifiedCount = currentTickets.filter(t => normalizeStatusToken(t.rawStatus) === 'verified').length;
  const toBeVerifiedCount = currentTickets.filter(t => normalizeStatusToken(t.rawStatus) === 'tobeverified').length;
  const invalidCount = currentTickets.filter(t => normalizeStatusToken(t.rawStatus) === 'invalid').length;
  const totalTickets = currentTickets.length;
  const soldRevenue = currentTickets.reduce((sum, t) => normalizeStatusToken(t.rawStatus) === 'sold' ? sum + parseCurrency(t.soldPrice) : sum, 0);
  const paidOut = analytics.ua_paid_out ?? (typeof currentPerformance.paidOut === 'string' ? parseCurrency(currentPerformance.paidOut) : currentPerformance.paidOut);
  const avgTicketPrice = soldCount > 0 ? soldRevenue / soldCount : 0;
  const sellThroughDenominator = activeCount + unsoldCount + soldCount;
  const sellThroughRate = sellThroughDenominator > 0 ? (soldCount / sellThroughDenominator) * 100 : 0;
  const delistDenominator = activeCount + delistedCount;
  const delistRate = delistDenominator > 0 ? (delistedCount / delistDenominator) * 100 : 0;
  
  const hearAboutUsBreakdown: Array<{ source: string; count: number; percentage: number }> = [];
  if (hearAboutUsData?.user && Array.isArray(hearAboutUsData.user)) {
    const sourceCount = new Map<string, number>();
    hearAboutUsData.user.forEach((user: any) => {
      const source = user.u_hear_about_us || 'Not Specified';
      sourceCount.set(source, (sourceCount.get(source) || 0) + 1);
    });
    const total = hearAboutUsData.user.length;
    hearAboutUsBreakdown.push(...Array.from(sourceCount.entries()).map(([source, count]) => ({
      source,
      count,
      percentage: total > 0 ? (count / total) * 100 : 0,
    })).sort((a, b) => b.count - a.count));
  }
  
  const preferredLeagueCategoriesList: LeagueCategoryPreference[] = Array.isArray(preferredLeagueCategoriesData?.league_categories) ? preferredLeagueCategoriesData.league_categories : [];
  const preferredTeamsById = new Map(preferredLeagueCategoriesList.map(team => [team.lc_id, team]));
  const selectedTeamsFromPreferences = preferredCategoryIds.map(lcId => preferredTeamsById.get(lcId)).filter(Boolean).map(team => ({
    id: team!.lc_id,
    abbreviation: (team!.lc_abbrev || '').trim() || (team!.lc_name || '').trim().slice(0, 3).toUpperCase(),
    name: (team!.lc_name || '').trim() || 'Unknown Team',
    color: (team!.lc_colour_code || '').trim() || '#475569',
    hasSoldTicket: teamsWithSoldStatus[team!.lc_id] || false,
    leagueId: (team!.l_id || '').trim(),
    leagueName: team!.l_id ? (leagueNameById.get(team!.l_id) || team!.lc_name || 'Unknown League') : team!.lc_name,
  }));
  const fallbackTeamPalette = ['#4c1d95', '#ea580c', '#0e7490', '#1d4ed8', '#be123c', '#166534', '#1f2937'];
  const getStableTeamUuid = (input: string) => {
    let hash = 0;
    for (let i = 0; i < input.length; i++) hash = ((hash << 5) - hash + input.charCodeAt(i)) | 0;
    const absHex = Math.abs(hash).toString(16).padStart(8, '0');
    const repeated = `${absHex}${absHex}${absHex}${absHex}`.slice(0, 32);
    const withVersion = `${repeated.slice(0, 12)}4${repeated.slice(13)}`;
    const withVariant = `${withVersion.slice(0, 16)}8${withVersion.slice(17)}`;
    return `${withVariant.slice(0, 8)}-${withVariant.slice(8, 12)}-${withVariant.slice(12, 16)}-${withVariant.slice(16, 20)}-${withVariant.slice(20, 32)}`;
  };
  const fallbackAssignedTeams = Array.isArray(effectiveUserRecord?.u_teams) ? effectiveUserRecord.u_teams.filter(Boolean) : [];
  const fallbackTeamsFromTickets = Array.from(new Set(currentTickets.flatMap(ticket => {
    const eventName = (ticket.event || '').split('•')[0]?.trim() || '';
    if (!eventName) return [];
    return eventName.split(/\s+vs\.?\s+/i).map(part => part.trim()).filter(Boolean);
  })));
  const selectedTeams: SelectedTeam[] = selectedTeamsFromPreferences.length > 0
    ? selectedTeamsFromPreferences
    : (fallbackAssignedTeams.length > 0 ? fallbackAssignedTeams : fallbackTeamsFromTickets).map((teamName: string, idx: number) => ({
        id: getStableTeamUuid(`${userId || 'unknown'}:${teamName}`),
        abbreviation: teamName.split(' ').filter(Boolean).map(p => p[0]).join('').toUpperCase().slice(0, 3) || 'TM',
        name: teamName, color: fallbackTeamPalette[idx % fallbackTeamPalette.length]
      }));

  const marketplaceMap = React.useMemo(() => {
    const map = new Map<string, { name: string; feePercent: number }>();
    marketplacesList.forEach(mp => {
      map.set(mp.id, { name: mp.name, feePercent: mp.vl_fee_percentage || 0 });
    });
    return map;
  }, [marketplacesList]);


const aggregateMarketplaceDataFinal = (): Array<{ marketplace: string; ticketsSold: number; revenue: number; percentage: number }> => {
  const mpMap = new Map<string, { count: number; revenue: number }>();
  const soldTickets = currentTickets.filter(t => normalizeStatusToken(t.rawStatus) === 'sold');
  if (soldTickets.length === 0) return [];

  soldTickets.forEach(ticket => {
    const soldAmount = parseCurrency(ticket.soldPrice);
    const marketplace = ticket.marketplace && ticket.marketplace.trim() && ticket.marketplace !== 'Other'
      ? ticket.marketplace.trim()
      : 'Unknown';

    const existing = mpMap.get(marketplace) || { count: 0, revenue: 0 };
    existing.count += 1;
    existing.revenue += Math.max(0, soldAmount);
    mpMap.set(marketplace, existing);
  });

  console.log('📊 FINAL MARKETPLACE DATA:', {
    totalSoldTickets: soldTickets.length,
    marketplacesFound: Array.from(mpMap.keys()),
  });

  const totalRevenue = Array.from(mpMap.values()).reduce((sum, item) => sum + item.revenue, 0);
  
  return Array.from(mpMap.entries())
    .map(([marketplace, data]) => ({
      marketplace,
      ticketsSold: data.count,
      revenue: data.revenue,
      percentage: totalRevenue > 0 ? (data.revenue / totalRevenue) * 100 : 0,
    }))
    .sort((a, b) => b.revenue - a.revenue);
};
  const marketplaceDataFinal = useMemo(() => aggregateMarketplaceDataFinal(), [currentTickets]);

const aggregateLeagueBreakdownFinal = (): Array<{ league: string; ticketsSold: number; revenue: number; percentage: number }> => {
  const leagueMap = new Map<string, { count: number; revenue: number }>();
  const soldTickets = currentTickets.filter(t => normalizeStatusToken(t.rawStatus) === 'sold');
  if (soldTickets.length === 0) return [];

  const teamToLeague = new Map<string, string>();
  selectedTeams.forEach(team => {
    if (team.name && team.leagueName) {
      teamToLeague.set(team.name.toLowerCase(), team.leagueName);
    }
    if (team.abbreviation && team.leagueName) {
      teamToLeague.set(team.abbreviation.toLowerCase(), team.leagueName);
    }
  });

  soldTickets.forEach(ticket => {
    const soldAmount = parseCurrency(ticket.soldPrice);
    if (soldAmount <= 0) return;

    let league: string | null = null;
    const eventLower = (ticket.event || '').toLowerCase();

    for (const [teamName, leagueName] of teamToLeague.entries()) {
      if (eventLower.includes(teamName)) {
        league = leagueName;
        break;
      }
    }

    if (!league) {
      const parts = eventLower.split(/\s+vs\.?\s+|•|\|/);
      for (const part of parts) {
        const trimmed = part.trim();
        if (teamToLeague.has(trimmed)) {
          league = teamToLeague.get(trimmed)!;
          break;
        }
      }
    }

    if (!league) {
      if (/nba|basketball/.test(eventLower)) league = 'NBA';
      else if (/nfl|football/.test(eventLower)) league = 'NFL';
      else if (/mlb|baseball/.test(eventLower)) league = 'MLB';
      else if (/nhl|hockey/.test(eventLower)) league = 'NHL';
      else if (/mls|soccer/.test(eventLower)) league = 'MLS';
      else if (/wnba|women'?s basketball/.test(eventLower)) league = 'WNBA';
      else if (/nwsl|women'?s soccer/.test(eventLower)) league = 'NWSL';
      else if (/college|ncaa/.test(eventLower)) {
        if (/basketball/.test(eventLower)) league = 'NCAA Basketball';
        else if (/football/.test(eventLower)) league = 'NCAA Football';
        else league = 'NCAA';
      }
      else if (/concert|live|music|tour/.test(eventLower)) league = 'Concerts';
      else if (/theater|broadway|musical|play/.test(eventLower)) league = 'Theater';
      else league = 'Other';
    }

    const existing = leagueMap.get(league) || { count: 0, revenue: 0 };
    existing.count += 1;
    existing.revenue += soldAmount;
    leagueMap.set(league, existing);
  });

  const totalRevenue = Array.from(leagueMap.values()).reduce((sum, item) => sum + item.revenue, 0);
  return Array.from(leagueMap.entries())
    .map(([league, data]) => ({
      league,
      ticketsSold: data.count,
      revenue: data.revenue,
      percentage: totalRevenue > 0 ? (data.revenue / totalRevenue) * 100 : 0,
    }))
    .sort((a, b) => b.revenue - a.revenue);
};
  const leagueBreakdownFinal = useMemo(() => aggregateLeagueBreakdownFinal(), [currentTickets, selectedTeams]);

  // ========== TIMING ANALYTICS CALCULATION (FRONTEND) ==========
  const calculateTimingAnalyticsFromTickets = useMemo(() => {
    const soldTickets = currentTickets.filter(t => normalizeStatusToken(t.rawStatus || t.status) === 'sold');
    const totalSold = soldTickets.length;
    if (totalSold === 0) {
      return {
        summary: { avgDaysBeforeSold: 0, medianDays: 0, avgDaysBeforeListed: 0, avgPriceChange: 0, basedOn: 'Based on 0 sales' },
        priceMovement: [],
        leagues: []
      };
    }
    const daysBeforeSoldList: number[] = [];
    const daysBeforeListedList: number[] = [];
    const priceChangeList: number[] = [];
    const timingBuckets: Record<string, {
      total: number;
      priceChangeSum: number;
      priceChangePercentSum: number;
      count: number;
    }> = {
      '3 Days Before Event': { total: 0, priceChangeSum: 0, priceChangePercentSum: 0, count: 0 },
      '7 Days Before Event': { total: 0, priceChangeSum: 0, priceChangePercentSum: 0, count: 0 },
      '1-2 Weeks Before Event': { total: 0, priceChangeSum: 0, priceChangePercentSum: 0, count: 0 },
      '2-4 Weeks Before Event': { total: 0, priceChangeSum: 0, priceChangePercentSum: 0, count: 0 },
    };
    const leagueDataMap = new Map<string, { daysTotal: number; count: number; priceChangeTotal: number }>();

    const teamToLeague = new Map<string, string>();
    selectedTeams.forEach(team => {
      if (team.name && team.leagueName) {
        teamToLeague.set(team.name.toLowerCase(), team.leagueName);
      }
      if (team.abbreviation && team.leagueName) {
        teamToLeague.set(team.abbreviation.toLowerCase(), team.leagueName);
      }
    });

    soldTickets.forEach(ticket => {
      let eventDate = parseDateAnalytics(ticket.eventDateRaw);
      if (!eventDate && ticket.date) eventDate = parseDateAnalytics(ticket.date);
      const actionDate = parseDateAnalytics(ticket.updatedAt);
      if (!eventDate || !actionDate) return;
      const daysBefore = getDaysBetweenDates(actionDate, eventDate);
      if (daysBefore >= 0 && daysBefore <= 365) {
        daysBeforeSoldList.push(daysBefore);
        daysBeforeListedList.push(daysBefore);
      }
      const listPrice = parseCurrency(ticket.price);
      const soldPrice = parseCurrency(ticket.soldPrice);
      if (listPrice > 0 && soldPrice > 0 && daysBefore >= 0) {
        const priceChange = soldPrice - listPrice;
        const priceChangePercent = (priceChange / listPrice) * 100;
        priceChangeList.push(priceChange);
        let bucket = '';
        if (daysBefore <= 3) bucket = '3 Days Before Event';
        else if (daysBefore <= 7) bucket = '7 Days Before Event';
        else if (daysBefore <= 14) bucket = '1-2 Weeks Before Event';
        else if (daysBefore <= 28) bucket = '2-4 Weeks Before Event';
        else bucket = '>4 Weeks';
        if (timingBuckets[bucket]) {
          timingBuckets[bucket].total += 1;
          timingBuckets[bucket].priceChangeSum += priceChange;
          timingBuckets[bucket].priceChangePercentSum += priceChangePercent;
          timingBuckets[bucket].count += 1;
        }
        let leagueName: string = 'Other';
        const eventLower = (ticket.event || '').toLowerCase();
        for (const [teamName, lName] of teamToLeague.entries()) {
          if (eventLower.includes(teamName)) {
            leagueName = lName;
            break;
          }
        }
        if (leagueName === 'Other') {
          const parts = eventLower.split(/\s+vs\.?\s+|•|\|/);
          for (const part of parts) {
            const trimmed = part.trim();
            if (teamToLeague.has(trimmed)) {
              leagueName = teamToLeague.get(trimmed)!;
              break;
            }
          }
        }
        if (leagueName === 'Other') {
          if (/nba|basketball/.test(eventLower)) leagueName = 'NBA';
          else if (/nfl|football/.test(eventLower)) leagueName = 'NFL';
          else if (/mlb|baseball/.test(eventLower)) leagueName = 'MLB';
          else if (/nhl|hockey/.test(eventLower)) leagueName = 'NHL';
          else if (/mls|soccer/.test(eventLower)) leagueName = 'MLS';
          else if (/concert|live|music/.test(eventLower)) leagueName = 'Concerts';
          else if (/theater|broadway/.test(eventLower)) leagueName = 'Theater';
          else if (/ncaa|college/.test(eventLower)) leagueName = 'NCAA';
        }
        const existing = leagueDataMap.get(leagueName) || { daysTotal: 0, count: 0, priceChangeTotal: 0 };
        existing.daysTotal += daysBefore;
        existing.count += 1;
        existing.priceChangeTotal += priceChange;
        leagueDataMap.set(leagueName, existing);
      }
    });
    const avgDaysBeforeSold = daysBeforeSoldList.length ? daysBeforeSoldList.reduce((a,b)=>a+b,0)/daysBeforeSoldList.length : 0;
    const sortedDays = [...daysBeforeSoldList].sort((a,b)=>a-b);
    const medianDays = sortedDays.length ? sortedDays[Math.floor(sortedDays.length/2)] : 0;
    const avgDaysBeforeListed = daysBeforeListedList.length ? daysBeforeListedList.reduce((a,b)=>a+b,0)/daysBeforeListedList.length : 0;
    const avgPriceChange = priceChangeList.length ? priceChangeList.reduce((a,b)=>a+b,0)/priceChangeList.length : 0;
    const priceMovement = Object.entries(timingBuckets)
      .filter(([_, d]) => d.count > 0)
      .map(([timing, data]) => ({
        timing,
        total: data.total,
        avgPriceChange: data.priceChangeSum / data.count,
        avgPriceChangePercent: data.priceChangePercentSum / data.count,
      }))
      .sort((a,b) => {
        const order = ['3 Days Before Event','7 Days Before Event','1-2 Weeks Before Event','2-4 Weeks Before Event'];
        return order.indexOf(a.timing) - order.indexOf(b.timing);
      });
    const leagues = Array.from(leagueDataMap.entries())
      .map(([name, data]) => ({
        name,
        avgDays: data.daysTotal / data.count,
        sold: data.count,
        avgPriceChange: data.priceChangeTotal / data.count
      }))
      .sort((a,b) => b.sold - a.sold);
    return {
      summary: {
        avgDaysBeforeSold: Math.round(avgDaysBeforeSold * 10) / 10,
        medianDays: Math.round(medianDays * 10) / 10,
        avgDaysBeforeListed: Math.round(avgDaysBeforeListed * 10) / 10,
        avgPriceChange: Math.round(avgPriceChange * 100) / 100,
        basedOn: `Based on ${totalSold} sale${totalSold !== 1 ? 's' : ''}`
      },
      priceMovement,
      leagues
    };
  }, [currentTickets, selectedTeams]);

  const timingSummaryFinal = useMemo(() => {
    const backend = analytics?.ua_timing_data?.summary;
    if (backend && (backend.avgDaysBeforeSold !== undefined || backend.basedOn)) return backend;
    return calculateTimingAnalyticsFromTickets.summary;
  }, [analytics, calculateTimingAnalyticsFromTickets]);

  const timingRowsFinal = useMemo(() => {
    const backendRows = analytics?.ua_timing_data?.priceMovement;
    if (backendRows && Array.isArray(backendRows) && backendRows.length) return backendRows;
    return calculateTimingAnalyticsFromTickets.priceMovement;
  }, [analytics, calculateTimingAnalyticsFromTickets]);

  const timingByLeagueFinal = useMemo(() => {
    const backendLeagues = analytics?.ua_timing_data?.leagues;
    if (backendLeagues && Array.isArray(backendLeagues) && backendLeagues.length) return backendLeagues;
    return calculateTimingAnalyticsFromTickets.leagues;
  }, [analytics, calculateTimingAnalyticsFromTickets]);

  const priceMovementCounters = useMemo(() => {
    const soldTickets = currentTickets.filter(t => normalizeStatusToken(t.rawStatus || t.status) === 'sold');
    let increased = 0, decreased = 0, unchanged = 0;
    
    soldTickets.forEach(ticket => {
      const listPrice = parseCurrency(ticket.price);
      const soldPrice = parseCurrency(ticket.soldPrice);
      if (listPrice > 0 && soldPrice > 0) {
        const priceChange = soldPrice - listPrice;
        if (priceChange > 0) increased++;
        else if (priceChange < 0) decreased++;
        else unchanged++;
      }
    });
    
    return { increased, decreased, unchanged };
  }, [currentTickets]);

  const teamEventToLeagueMap = React.useMemo(() => {
    const map = new Map<string, string>();
    const allTeams = Array.isArray(availableTeamsData?.league_categories) ? availableTeamsData.league_categories : [];
    
    allTeams.forEach((team: any) => {
      const teamName = (team.lc_name || '').toLowerCase().trim();
      const leagueId = team.l_id;
      const leagueName = leagueId ? (leagueNameById.get(leagueId) || team.lc_name || 'Unknown') : team.lc_name;
      
      if (teamName && leagueName) {
        map.set(teamName, leagueName);
        const abbrev = (team.lc_abbrev || '').toLowerCase().trim();
        if (abbrev) map.set(abbrev, leagueName);
      }
    });
    
    return map;
  }, [availableTeamsData, leagueNameById]);
const aggregateMarketplaceData = (): Array<{ marketplace: string; ticketsSold: number; revenue: number; percentage: number }> => {
  const mpDataMap = new Map<string, { count: number; revenue: number }>();
  const soldTickets = currentTickets.filter(t => normalizeStatusToken(t.rawStatus) === 'sold');
  
  if (soldTickets.length === 0) return [];
  
  let validMarketplaceCount = 0;
  let missingMarketplaceCount = 0;
  
  soldTickets.forEach(ticket => {
    const soldAmount = parseCurrency(ticket.soldPrice);
    // Only count if: has sold amount AND has real marketplace (not empty, not "Other")
    if (soldAmount > 0 && ticket.marketplace && ticket.marketplace.trim() !== '' && ticket.marketplace !== 'Other') {
      const marketplaceName = ticket.marketplace.trim();
      validMarketplaceCount++;
      
      const current = mpDataMap.get(marketplaceName) || { count: 0, revenue: 0 };
      mpDataMap.set(marketplaceName, {
        count: current.count + 1,
        revenue: current.revenue + soldAmount
      });
    } else if (soldAmount > 0) {
      missingMarketplaceCount++;
    }
  });
  
  console.log('📊 MARKETPLACE AGGREGATION REPORT (REAL DATA ONLY):', {
    totalSoldTickets: soldTickets.length,
    realMarketplacesWithData: validMarketplaceCount,
    ticketsWithoutMarketplaceData: missingMarketplaceCount,
    realMarketplacesFound: Array.from(mpDataMap.keys()),
    note: 'Only showing real marketplaces (not "Other" or empty values)'
  });
  
  // Calculate total revenue from ONLY real marketplace data
  const totalRevenue = Array.from(mpDataMap.values()).reduce((sum, item) => sum + item.revenue, 0);
  
  // Filter OUT "Other" and empty values - only return REAL marketplace data
  const realMarketplaceData = Array.from(mpDataMap.entries())
    .filter(([marketplace, _]) => marketplace && marketplace.trim() !== '' && marketplace !== 'Other')
    .map(([marketplace, data]) => ({
      marketplace,
      ticketsSold: data.count,
      revenue: data.revenue,
      percentage: totalRevenue > 0 ? (data.revenue / totalRevenue) * 100 : 0
    }))
    .sort((a, b) => b.revenue - a.revenue);
  
  return realMarketplaceData;
};

  const aggregateLeagueBreakdown = (): Array<{ league: string; ticketsSold: number; revenue: number; percentage: number }> => {
    const leagueDataMap = new Map<string, { count: number; revenue: number }>();
    const soldTickets = currentTickets.filter(t => normalizeStatusToken(t.rawStatus) === 'sold');
    
    if (soldTickets.length === 0) return [];
    
    let matchedByTeam = 0;
    let unmatchedTickets = 0;
    
    soldTickets.forEach(ticket => {
      const soldAmount = parseCurrency(ticket.soldPrice);
      if (soldAmount > 0 && ticket.event) {
        let leagueName = 'Other';
        
        const eventLower = ticket.event.toLowerCase().trim();
        
        if (teamEventToLeagueMap.has(eventLower)) {
          leagueName = teamEventToLeagueMap.get(eventLower) || 'Other';
          matchedByTeam++;
        } else {
          const eventParts = eventLower.split(/\s+vs\.?\s+|•/i);
          let foundMatch = false;
          for (const part of eventParts) {
            const trimmedPart = part.trim();
            if (teamEventToLeagueMap.has(trimmedPart)) {
              leagueName = teamEventToLeagueMap.get(trimmedPart) || 'Other';
              matchedByTeam++;
              foundMatch = true;
              break;
            }
          }
          
          if (!foundMatch) {
            if (eventLower.includes('nba')) leagueName = 'NBA';
            else if (eventLower.includes('nfl')) leagueName = 'NFL';
            else if (eventLower.includes('nhl')) leagueName = 'NHL';
            else if (eventLower.includes('mlb')) leagueName = 'MLB';
            else if (eventLower.includes('mls')) leagueName = 'MLS';
            else if (eventLower.includes('nwsl')) leagueName = 'NWSL';
            else if (eventLower.includes('wnba')) leagueName = 'WNBA';
            else if (eventLower.includes('college') || eventLower.includes('ncaa')) leagueName = 'College Sports';
            else {
              unmatchedTickets++;
            }
          }
        }
        
        const current = leagueDataMap.get(leagueName) || { count: 0, revenue: 0 };
        leagueDataMap.set(leagueName, {
          count: current.count + 1,
          revenue: current.revenue + soldAmount
        });
      }
    });
    
    console.log('🏆 LEAGUE AGGREGATION REPORT:', {
      totalSoldTickets: soldTickets.length,
      matchedByTeam: matchedByTeam,
      unmatchedTickets: unmatchedTickets,
      uniqueLeagues: leagueDataMap.size,
      leagueList: Array.from(leagueDataMap.keys())
    });
    
    const totalRevenue = Array.from(leagueDataMap.values()).reduce((sum, item) => sum + item.revenue, 0);
    
    return Array.from(leagueDataMap.entries())
      .map(([league, data]) => ({
        league,
        ticketsSold: data.count,
        revenue: data.revenue,
        percentage: totalRevenue > 0 ? (data.revenue / totalRevenue) * 100 : 0
      }))
      .sort((a, b) => b.revenue - a.revenue);
  };

  const marketplacePerformanceData = aggregateMarketplaceData();
  const leagueBreakdownData = aggregateLeagueBreakdown();
  
  (window as any).debugTicketData = () => {
    console.group('🔍 TICKET DATA DIAGNOSTIC REPORT - REAL MARKETPLACE DATA');
    console.log('📊 Current Tickets:', currentTickets.length);
    console.log('🏪 Known Marketplaces:', KNOWN_MARKETPLACES);
    
    const soldTickets = currentTickets.filter(t => normalizeStatusToken(t.rawStatus) === 'sold');
    console.log(`📦 Sold Tickets: ${soldTickets.length}`);
    
    if (soldTickets.length > 0) {
      console.group('Marketplace Data for Sold Tickets (REAL DATA ONLY):');
      const withMarketplace = soldTickets.filter(t => t.marketplace && t.marketplace.trim() !== '' && t.marketplace !== 'Other');
      const withoutMarketplace = soldTickets.filter(t => !t.marketplace || t.marketplace.trim() === '' || t.marketplace === 'Other');
      
      console.log(`  ✅ With Real Marketplace Data: ${withMarketplace.length}`);
      withMarketplace.forEach((t, idx) => {
        console.log(`    [${idx}] Marketplace: "${t.marketplace}" | Revenue: $${parseCurrency(t.soldPrice).toFixed(2)}`);
      });
      
      if (withoutMarketplace.length > 0) {
        console.log(`  ⚠️ Missing Marketplace Data: ${withoutMarketplace.length}`);
        withoutMarketplace.forEach((t, idx) => {
          console.log(`    [${idx}] TPId: ${t.tpId} | Marketplace: "${t.marketplace || 'EMPTY'}" | Event: ${t.event}`);
        });
      }
      
      console.groupEnd();
      
      console.group('Real Marketplace Aggregation:');
      marketplaceDataFinal.forEach(m => {
        console.log(`  ✅ ${m.marketplace}: ${m.ticketsSold} tickets | $${m.revenue.toFixed(2)} (${m.percentage.toFixed(1)}%)`);
      });
      if (marketplaceDataFinal.length === 0) {
        console.log('  ❌ NO REAL MARKETPLACE DATA - All sold tickets missing marketplace information');
      }
      console.groupEnd();
    }
    
    console.log('🛒 Marketplace Settings Available:', marketplacesList.length);
    console.groupEnd();
    
    return { soldTickets, realMarketplaceData: marketplaceDataFinal };
  };
  
  const marketplaceData = marketplacePerformanceData;
  const leagueBreakdown = leagueBreakdownData;
  const timingData = analytics.ua_timing_data || {};
  const timingSummary = timingData.summary || {};
  const timingRows = Array.isArray(timingData.priceMovement) ? timingData.priceMovement : [];
  const timingByLeague = Array.isArray(timingData.leagues) ? timingData.leagues : [];
  const statusFlags = Array.isArray(analytics.ua_status_flags) ? analytics.ua_status_flags : (analytics.ua_status_flags ? [analytics.ua_status_flags] : []);
  
  if (isUserLoading && !effectiveUserRecord) return <div className={styles.loadingContainer}><Loader size={40} className={styles.spinner} /><p>Loading user details...</p></div>;
  if (userError && !isGetUserByIdAllowlistError) return <div className={styles.errorContainer}><p>Error loading user: {userError.message}</p><button onClick={() => navigate('/users')} className={styles.backBtn}><ChevronLeft size={14} /> Back to Users</button></div>;
  if (isAffiliateAdmin && isAffiliateScopeResolved && !isAffiliateAllowedUser) return <div className={styles.errorContainer}><p>You can only view details for your assigned users.</p><button onClick={() => navigate('/users')} className={styles.backBtn}><ChevronLeft size={14} /> Back to Users</button></div>;
  if (!effectiveUserRecord || !currentUser) return <div className={styles.errorContainer}><p>User not found</p><button onClick={() => navigate('/users')} className={styles.backBtn}><ChevronLeft size={14} /> Back to Users</button></div>;
  
  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.logo} onClick={handleUserClick} style={{ cursor: 'pointer' }}><ChevronLeft size={14} /> Back</div>
        <div><button className={styles.deleteuser} onClick={() => setShowDeleteConfirm(true)}><Delete size={14} style={{ color: "#fff" }} /> Delete User</button></div>
      </div>
      <div className={styles.mainContent}>
          <div className={styles.pageHeader}>
          <div><p>Users Details</p><h2>{currentUser.name}</h2></div>
       
            <div className={styles.searchSection}>
                {activeTab !== 'teams' && activeTab !== 'profile' && activeTab !== 'analytics' && (
              <div className={styles.searchWrapper}>
                <Search className={styles.searchIcon} size={18} />
                <input type="text" placeholder="Search by event, section, row, seat..." className={styles.searchInput} value={searchQuery} onChange={(e) => handleSearch(e.target.value)} />
              </div>
                   )}
              <div className={styles.iconGroup}><button className={styles.iconBtn} onClick={handleRefresh}><RefreshRoundedIcon /></button><button className={styles.iconBtn} onClick={() => setIsDarkMode(!isDarkMode)}>{isDarkMode ? <LightModeOutlinedIcon /> : <DarkModeOutlinedIcon />}</button></div>
            </div>
     
        </div>
        
        <div className={styles.tabs}>
          <button className={activeTab === "profile" ? styles.activeTab : ""} onClick={() => setActiveTab("profile")}>Profile</button>
          <button className={activeTab === "tickets" ? styles.activeTab : ""} onClick={() => setActiveTab("tickets")}>Tickets ({totalTickets})</button>
          <button className={activeTab === "analytics" ? styles.activeTab : ""} onClick={() => setActiveTab("analytics")}>Analytics</button>
          <button className={activeTab === "teams" ? styles.activeTab : ""} onClick={() => setActiveTab("teams")}>Teams</button>
        </div>
        
         {activeTab === "profile" && (
          <>
            <div className={styles.profileRow}>
              <div className={styles.profileCol}>
                <div className={styles.card}>
                  <div className={styles.cardTitle} style={{ display: "flex", alignItems: "center", gap: "5px", justifyContent: "space-between" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "5px" }}><User size={16} /> User Information</div>
                    <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                      {isEditing ? (<button className={styles.cancelBtn} onClick={handleCancelEdit}><X size={14} /> Cancel</button>) : (<button className={styles.edituser} onClick={handleEditClick}><Edit size={14} /> Edit</button>)}
                    </div>
                  </div>
                  {isEditing && editFormData ? renderInlineEditForm() : (
                    <>
                      <div className={styles.userInfo}><div className={styles.avatar}>{currentUser.initials}</div><div><h3>{currentUser.name}</h3><div className={styles.badges}><span className={styles.userType}>{currentUser.type}</span><span className={styles.tier}>{currentUser.tier}</span></div></div></div>
                      <div className={styles.userFee}><span>User Fee</span><b>{currentUser.userFee}%</b></div>
                      <hr className={styles.divider} />
                      <div className={styles.userInfoDetails}>
                        <div className={styles.infoRow}><Briefcase size={14} className={styles.infoIcon} /><span className={styles.infoLabel}>Business:</span><span className={styles.infoValue}>{currentUser.business || "N/A"}</span></div>
                        <div className={`${styles.infoRow} ${styles.emailRow}`}><Mail size={14} className={styles.infoIcon} /><span className={`${styles.infoLabel} ${styles.emailLabel}`}>Email:</span><span className={`${styles.infoValue} ${styles.emailValue}`}>{currentUser.email || "N/A"}</span></div>
                        <div className={styles.infoRow}><Phone size={14} className={styles.infoIcon} /><span className={styles.infoLabel}>Phone:</span><span className={styles.infoValue}>{currentUser.phone || "N/A"}</span></div>
                        {hasAddressValues(currentUser) && (<><div className={styles.cardTitle} style={{ marginTop: "16px" }}><MapPinned size={16} /> Address</div>{currentUser.address1 && <p>{currentUser.address1}</p>}{currentUser.address2 && <p>{currentUser.address2}</p>}{currentUser.city && currentUser.state && currentUser.zip && (<p>{`${currentUser.city}, ${currentUser.state} ${currentUser.zip}`.trim()}</p>)}{currentUser.country && <p>{currentUser.country}</p>}</>)}
                        <div className={styles.cardTitle} style={{ marginTop: "16px" }}><Clock size={16} /> Last Active</div><p>{currentUser.lastActive}</p>
                        {currentUser.hearAboutUs && (<div style={{ marginTop: "16px", padding: "12px 14px", backgroundColor: "#f0f4ff", borderLeft: "4px solid #3b82f6", borderRadius: "4px", display: "flex", alignItems: "center", gap: "10px" }}><Lightbulb size={18} style={{ color: "#3b82f6", flexShrink: 0 }} /><div><div style={{ fontSize: "11px", fontWeight: "600", color: "#3b82f6", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "3px" }}>Source</div><div style={{ fontSize: "14px", fontWeight: "500", color: "#1f2937" }}>{currentUser.hearAboutUs}</div></div></div>)}
                        <div className={styles.cardTitle} style={{ marginTop: "16px" }}><Award size={16} /> Ticketmaster Catalog</div>
                        <div className={styles.tierDisplay}><span className={styles.tier}>{currentUser.tier}</span><span className={styles.annualSales}>{currentUser.annualSales}</span></div>
                        <div className={styles.requirements}>{tierThresholds.map(({ label, threshold }) => (<span key={label} className={activeTier === label ? styles.requirementActive : undefined}>{label}: {threshold}</span>))}</div>
                        <hr className={styles.divider} />
                        {currentUser.assignedTeams && currentUser.assignedTeams.length > 0 && (<><h4 className={styles.teamsTitle}>Teams</h4><div className={styles.teams}>{currentUser.assignedTeams.map((team, i) => (<span key={i} className={styles.teamTag}>{team}</span>))}</div></>)}
                      </div>
                    </>
                  )}
                </div>
              </div>
              <div className={styles.profileCol}>
                <div className={styles.card}>
                  <div className={styles.cardTitle}><TrendingUp size={16} /> Performance</div>
                  {performanceLoading ? (<div className={styles.loadingSmall}><Loader size={20} className={styles.spinner} /><span>Loading performance...</span></div>) : (
                    <>
                      <div className={styles.stats}>
                        <div className={`${styles.statBox} ${styles.blue}`}><b>{activeCount}</b><span>Active</span></div>
                        <div className={`${styles.statBox} ${styles.green}`}><b>{formatCurrency(soldRevenue)}</b><span>Potential</span></div>
                        <div className={styles.statBox}><b>{soldCount}</b><span>Sold</span></div>
                        <div className={styles.statBox}><b>{formatCurrency(soldRevenue)}</b><span>Revenue</span></div>
                        <div className={`${styles.statBox} ${styles.orange}`}><b>{formatCurrency(typeof paidOut === "number" ? paidOut : 0)}</b><span>Paid Out</span></div>
                        <div className={`${styles.statBox} ${styles.purple}`}><b>{formatPercent(sellThroughRate)}</b><span>Sell-Through</span></div>
                      </div>
                      <hr className={styles.divider} />
                      <div className={styles.ticketStatsGrid}>
                        <div className={styles.ticketStatCard}><span className={styles.ticketStatValue}>{verifiedCount}</span><span className={styles.ticketStatLabel}>Verified</span></div>
                        <div className={styles.ticketStatCard}><span className={styles.ticketStatValue}>{toBeVerifiedCount}</span><span className={styles.ticketStatLabel}>To Be Verified</span></div>
                        <div className={styles.ticketStatCard}><span className={styles.ticketStatValue}>{delistedCount}</span><span className={styles.ticketStatLabel}>Delisted</span></div>
                        <div className={styles.ticketStatCard}><span className={styles.ticketStatValue}>{unsoldCount}</span><span className={styles.ticketStatLabel}>Unsold</span></div>
                        <div className={styles.ticketStatCard}><span className={styles.ticketStatValue}>{invalidCount}</span><span className={styles.ticketStatLabel}>Invalid</span></div>
                        <div className={`${styles.ticketStatCard} ${styles.highlightCard}`}><span className={styles.ticketStatValue}>{totalTickets}</span><span className={styles.ticketStatLabel}>Total Tickets</span></div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
            <div className={styles.card}>
              <div className={styles.cardTitle}><CreditCard size={16} /> Payout Controls</div>
              {currentUser.payoutsVoided ? (
                <>
                  <div className={styles.payoutBoxWarning}><AlertTriangle size={20} color="#dc2626" /><div><b>Payouts are voided for this user</b><p>Tickets sold by this user will not generate payouts</p>{currentUser.voidReason && <p className={styles.voidReasonText}>Reason: {currentUser.voidReason}</p>}{currentUser.voidDate && <p className={styles.voidDateText}>Voided on: {new Date(currentUser.voidDate).toLocaleString()}</p>}</div></div>
                  <button className={styles.restoreBtn} onClick={handleRestorePayouts} disabled={restoreLoading}>{restoreLoading ? "Restoring..." : "Restore Payouts"}</button>
                </>
              ) : (
                <>
                  <div className={styles.payoutBox}><CheckCircle size={20} color="#10b981" /><div><b>Payouts are active for this user</b><p>Tickets sold will generate payouts normally</p></div></div>
                  <button className={styles.dangerBtn} onClick={() => setShowVoidPayoutsModal(true)}>Void Payouts</button>
                </>
              )}
            </div>
            <div className={styles.card}>
              <div className={styles.cardTitle} style={{ display: "flex", justifyContent: "space-between", gap: "5px", alignItems: "center" }}>
                <div style={{ display: "flex", gap: "5px", alignItems: "center" }}><Mail size={16} /> Email Communication</div>
                <span className={emailEnabledResolved ? styles.active : styles.inactive}>{emailEnabledResolved ? "Active" : "Disabled"}</span>
              </div>
              {emailEnabledResolved ? (<div className={styles.payoutBox}><CheckCircle size={20} color="#10b981" /><div><b>Emails are active for this user</b><p>This user receives email communications from QuickAsyst</p></div></div>) : (<div className={styles.payoutBoxWarning}><AlertTriangle size={20} color="#dc2626" /><div><b>Emails are disabled for this user</b><p>QuickAsyst will not send any emails to this user</p></div></div>)}
              <div className={styles.emailActions}>{emailEnabledResolved ? (<button className={styles.dangerBtn} onClick={handleToggleEmail} disabled={emailStatusLoading}>{emailStatusLoading ? "Updating..." : "Disable Emails"}</button>) : (<button className={styles.restoreBtn} onClick={handleToggleEmail} disabled={emailStatusLoading}>{emailStatusLoading ? "Updating..." : "Enable Emails"}</button>)}</div>
            </div>
            <div className={styles.notesRow}>
              <div className={styles.profileCol}><div className={styles.card}><div className={styles.cardTitle}><MessageCircle size={16} /> QA Notes</div>{renderNotesSection("qa", "QA Notes")}</div></div>
              <div className={styles.profileCol}><div className={styles.card}><div className={styles.cardTitle}><FileText size={16} /> VL Notes</div><p className={styles.noteDescription}>VL workflow notes - internal and external</p>{renderNotesSection("internal", "Internal Notes")}{renderNotesSection("external", "External Notes")}</div></div>
            </div>
          </>
        )}
        {/* Tickets Tab */}
        {activeTab === "tickets" && (
          <div className={styles.ticketsSection}>
   <div className={styles.card}>
  <div className={styles.cardTitle} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
    <span><FileText size={16} /> Tickets by Status</span>
    <button
      className={styles.exportBtn}
      onClick={() => exportTicketsCsv(filteredTickets, activeFilterTab)}
      disabled={isTicketsLoading || filteredTickets.length === 0}
    >
      <Download size={14} /> Export CSV
    </button>
  </div>

  {isTicketsLoading ? (
    <div className={styles.loadingSmall}>
      <Loader size={20} className={styles.spinner} />
      <span>Loading tickets...</span>
    </div>
  ) : (
    <>
      <div className={styles.ticketsHeader}>
        <div className={styles.filterTabs}>
          {['All', 'Verification', 'Listed', 'Sold', 'Delisted', 'Listing Request Cancelled', 'Unsold', 'Expired'].map((tab) => (
            <button
              key={tab}
              className={activeFilterTab === tab ? styles.filterTabActive : styles.filterTab}
              onClick={() => setActiveFilterTab(tab)}
            >
              {tab}{getTabCount(tab) > 0 || tab === 'All' ? ` (${getTabCount(tab)})` : ''}
            </button>
          ))}
        </div>
      </div>

     {activeFilterTab === 'Sold' && (() => {
  const soldCounts = getSoldSubFilterCounts();
  const soldFilterOptions: { key: typeof soldSubFilter; label: string }[] = [
    { key: 'all', label: 'All Sold' },
    { key: 'sold', label: 'Sold' },
    { key: 'settled', label: 'Settled' },
    { key: 'settlementinprogress', label: 'Settlement In Progress' },
  ];
  return (
    <div className={styles.soldSubFilters}>
      {soldFilterOptions.map((opt) => (
        <button
          key={opt.key}
          type="button"
          className={soldSubFilter === opt.key ? styles.filterTabActive : styles.filterTab}
          onClick={() => setSoldSubFilter(opt.key)}
        >
          {opt.label} ({soldCounts[opt.key]})
        </button>
      ))}
    </div>
  );
})()}
      {bulkSelectedTicketIds.size > 0 && (
        <div className={styles.bulkActionsBar}>
          <div className={styles.bulkActionsLeft}>
            <div><strong>{bulkSelectedTicketIds.size}</strong> ticket{bulkSelectedTicketIds.size > 1 ? 's' : ''} selected</div>
            {filteredTickets.filter((t) => bulkSelectedTicketIds.has(t.tpId)).length > 0 && (
              <div style={{ marginLeft: 8 }}>
                Event: {filteredTickets.find((t) => bulkSelectedTicketIds.has(t.tpId))?.event || 'N/A'}
                {' • '}
                Section: {filteredTickets.find((t) => bulkSelectedTicketIds.has(t.tpId))?.section || 'N/A'}
                {' • '}
                Row: {filteredTickets.find((t) => bulkSelectedTicketIds.has(t.tpId))?.row || 'N/A'}
              </div>
            )}
            <button type="button" className={styles.clearSelectionBtn} onClick={clearBulkSelection}>
              Clear Selection
            </button>
          </div>
          <div className={styles.bulkActionsRight}>
            <button type="button" className={styles.bulkChangePrice} onClick={openBulkStatusModal}>
              Bulk Update Status
            </button>
            {areSelectedTicketsAllListed && (
              <button type="button" className={styles.bulkChangePrice} onClick={handleBulkChangePrice}>
                <Edit size={14} /> Change Price
              </button>
            )}
          </div>
        </div>
      )}

      <div className={styles.tableWrapper}>
        <table className={styles.ticketsTable}>
          <thead>
            <tr>
              <th className={styles.checkboxColumn}>
                <input
                  type="checkbox"
                  className={styles.bulkSelectCheckbox}
                  checked={filteredTickets.length > 0 && bulkSelectedTicketIds.size === filteredTickets.length}
                  onChange={(e) => handleBulkSelectAllPage(e.target.checked)}
                />
              </th>
              <th className={styles.thEvent}>Event</th>
              <th className={styles.thSection}>Section</th>
              <th className={styles.thRow}>Row</th>
              <th className={styles.thSeat}>Seat #</th>
              <th className={styles.thStatus}>Status</th>
              <th className={styles.thPrice}>List Price</th>
              {showSoldPriceColumns && (
                <th className={styles.thPrice}>Sold Price</th>
              )}
              <th className={styles.thActions}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredTickets.length > 0 ? filteredTickets.map((ticket) => {
              const pres = getTicketStatusPresentation(ticket);
              const paymentUiKey = pres.iconStatus.toLowerCase();
              const rawStatusLower = (ticket.rawStatus || ticket.status).toLowerCase();
              const statusClassForColor = getStatusColorClass(pres.iconStatus);
              const currentStatusForTransitions = getTicketTransitionStatus(ticket);
              const transitions = getStatusTransitions(currentStatusForTransitions);
              const isPaymentReadOnly = !canOverrideSettlementStatus && (paymentUiKey === 'settled' || paymentUiKey === 'settlementinprogress');
              return (
                <tr key={ticket.tpId}>
                  <td className={styles.checkboxCell} onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      className={styles.ticketCheckbox}
                      checked={bulkSelectedTicketIds.has(ticket.tpId)}
                      onChange={(e) => handleBulkSelectTicket(ticket.tpId, e.target.checked)}
                    />
                  </td>
                  <td className={styles.eventCell}>{ticket.event}<br></br>
                  <div> {new Date(ticket.date).toLocaleDateString()} {' '} . {new Date(ticket.date).toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  })}</div>
                  </td>
                  <td className={styles.tdCenter}>{ticket.section === '-' ? 'N/A' : ticket.section}</td>
                  <td className={styles.tdCenter}>{ticket.row === '-' ? 'N/A' : ticket.row}</td>
                  <td className={styles.tdCenter}>{ticket.seatNo === '-' ? 'N/A' : ticket.seatNo}</td>
                  <td className={styles.tdCenter}>
                    {(() => {
                      if (
                        isPaymentReadOnly ||
                        rawStatusLower === 'closed' ||
                        rawStatusLower === 'unsold' ||
                        rawStatusLower === 'invalid' ||
                        rawStatusLower === 'return'
                      ) {
                        return (
                          <span className={`${styles.status} ${styles[statusClassForColor] || ''}`}>
                            {getStatusIcon(pres.iconStatus)} {pres.label}
                          </span>
                        );
                      }
                      if (!transitions.length) {
                        return (
                          <span className={`${styles.status} ${styles[statusClassForColor] || ''}`}>
                            {getStatusIcon(pres.iconStatus)} {pres.label}
                          </span>
                        );
                      }
                      return (
                        <div className={styles.statusDropdownWrapper}>
                          <button
                            className={`${styles.status} ${styles[statusClassForColor] || ''} ${styles.statusDropdownBtn}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              setOpenStatusDropdown(openStatusDropdown === ticket.tpId ? null : ticket.tpId);
                            }}
                          >
                            {getStatusIcon(pres.iconStatus)} {pres.label}{' '}
                            <ChevronDown size={12} style={{ marginLeft: 4 }} />
                          </button>
                          {openStatusDropdown === ticket.tpId && (
                            <div className={styles.statusDropdownList}>
                              {transitions.map((opt) => (
                                <div
                                  key={opt.value}
                                  className={styles.statusDropdownListItem}
                                  onClick={async (e) => {
                                    e.stopPropagation();
                                    setOpenStatusDropdown(null);
                                    await handleStatusChange(ticket, opt.value);
                                  }}
                                >
                                  {getStatusIcon(opt.value)} {opt.label}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })()}
                  </td>
                  <td className={styles.tdPrice}>{displayPrice(ticket.price)}</td>
                  {showSoldPriceColumns && (
                    <td className={styles.tdPrice}>{displayPrice(ticket.soldPrice)}</td>
                  )}
                  <td className={styles.actionsCell}>
                    <button className={styles.actionBtn} onClick={(e) => { e.stopPropagation(); setOpenActionTicketId((prev) => (prev === ticket.tpId ? null : ticket.tpId)); }}><MoreVertical size={18} /></button>
                    {openActionTicketId === ticket.tpId && (
                      <div className={styles.ticketActionMenu}>
                        {!isSoldStatus(ticket) && (
                          <button className={styles.ticketActionItem} onClick={(e) => { e.stopPropagation(); handlePriceChangeClick(ticket); }}><Edit size={14} /> Change Price</button>
                        )}
                        {isListedStatus(ticket.status) && (
                          <button className={styles.ticketActionItem} onClick={(e) => { e.stopPropagation(); handleMarkAsSoldFromMenu(ticket); }}><CreditCard size={14} /> Mark as Sold</button>
                        )}
                        <button className={styles.ticketActionItem} onClick={(e) => { e.stopPropagation(); openChangeStatusModal(ticket); }}><RefreshRoundedIcon style={{ fontSize: 14 }} /> Change Status</button>
                        <button className={styles.ticketActionItem} onClick={(e) => { e.stopPropagation(); openEditTicketModal(ticket); }}><Edit size={14} /> Edit Details</button>
                      </div>
                    )}
                  </td>
                </tr>
              );
            }) : (
              <tr>
                <td colSpan={showSoldPriceColumns ? 9 : 8} className={styles.noData}>
                  {searchQuery.trim() ? 'No tickets match your search' : 'No tickets found for this filter'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </>
  )}
</div>
          </div>
        )}

        {/* Analytics Tab */}
        {activeTab === "analytics" && (
       <div className={styles.analyticsSection}>
  {analyticsLoading ? (
    <div className={styles.loadingSmall}><Loader size={20} className={styles.spinner} /><span>Loading analytics...</span></div>
  ) : (
    <>
      <div className={styles.card}>
        <div className={styles.cardHeader}><h3>Performance Analytics</h3></div>
        <div className={styles.analyticsCard}>
          <h4>Seller Status</h4>
          <div className={styles.statusBadges}>
            <span className={styles.statusLabel}>{analytics.ua_seller_status || currentUser.tier || 'No Status'}</span>
            <span className={styles.thresholdItem}>{analytics.ua_annual_sales_thresholds || 'Annual Sales Thresholds: VIP ($20k+) | Silver ($10k+) | Bronze ($5k+) | No Status (<$5k)'}</span>
          </div>
          <div className={styles.totalRevenue}><span className={styles.revenuLabel}>Total Revenue This Year</span><span className={styles.revenueValue}>{formatCurrency(analytics.ua_total_revenue_year ?? soldRevenue)}</span></div>
        </div>
        <div className={styles.metricsGrid}>
          <div className={styles.metricCard}><span className={styles.metricValue}>{analytics.ua_tickets_sold ?? soldCount}</span><span className={styles.metricLabel}>Tickets Sold</span></div>
          <div className={styles.metricCard}><span className={styles.metricValue}>{formatCurrency(analytics.ua_avg_ticket_price ?? avgTicketPrice)}</span><span className={styles.metricLabel}>Avg Ticket Price</span></div>
          <div className={styles.metricCard}><span className={styles.metricValue}>{formatPercent(analytics.ua_delist_rate ?? delistRate)}</span><span className={styles.metricLabel}>Delist Rate</span></div>
          <div className={styles.metricCard}><span className={styles.metricValue}>{formatPercent(analytics.ua_sell_through ?? sellThroughRate)}</span><span className={styles.metricLabel}>Sell-Through</span></div>
        </div>
        <hr className={styles.divider} />
        <div className={styles.statusFlagsSection}><h4>Status Flags</h4><div className={styles.flagsContainer}><CheckCircle size={24} color="#10b981" /><span className={styles.flagsText}>{statusFlags.length > 0 ? statusFlags.join(', ') : 'No flags'}</span></div></div>
      </div>
      <div className={styles.card}>
        <div className={styles.cardHeader}><h3>Marketplace Performance</h3></div>
        {marketplaceDataFinal.length > 0 ? (
          <>
            <div style={{ overflowX: 'auto' }}>
              <table className={styles.priceMovementTable}>
                <thead><tr><th>MARKETPLACE</th><th>TICKETS SOLD</th><th>REVENUE GENERATED</th><th>% OF TOTAL</th></tr></thead>
                <tbody>
                  {marketplaceDataFinal.map((item, idx) => (
                    <tr key={idx}><td style={{ fontWeight: '500' }}>{item.marketplace}</td><td>{item.ticketsSold}</td><td style={{ fontWeight: '600', color: '#059669' }}>{formatCurrency(item.revenue)}</td><td>{item.percentage.toFixed(1)}%</td></tr>
                  ))}
                  <tr style={{ borderTop: '2px solid #e5e7eb', fontWeight: '600', backgroundColor: '#f9fafb' }}>
                    <td>TOTAL</td><td>{marketplaceDataFinal.reduce((sum, item) => sum + item.ticketsSold, 0)}</td>
                    <td style={{ color: '#059669' }}>{formatCurrency(marketplaceDataFinal.reduce((sum, item) => sum + item.revenue, 0))}</td><td>100%</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </>
        ) : (
          <div className={styles.emptyState}>
            <p>
              {soldCount === 0 
                ? '📦 No sold tickets yet. Mark tickets as sold to see marketplace performance.'
                : '⚠️ No real marketplace data available. Sold tickets are missing marketplace information. When marking tickets as sold, select the actual marketplace: AXS, Gametime, GoTickets, SeatGeek, StubHub, Ticket Evolution, Ticketmaster, TicketNetwork, TickPick, or Vivid Seats.'}
            </p>
          </div>
        )}
      </div>

      <div className={styles.card}>
        <div className={styles.cardHeader}><h3>League Breakdown</h3><div style={{ fontSize: '12px', color: '#6b7280', marginLeft: 'auto' }}>Based on ticket events & teams</div></div>
        {leagueBreakdownFinal.length > 0 ? (
          <>
            <div style={{ overflowX: 'auto' }}>
              <table className={styles.priceMovementTable}>
                <thead><tr><th>LEAGUE</th><th>TICKETS SOLD</th><th>REVENUE GENERATED</th><th>% OF TOTAL</th></tr></thead>
                <tbody>
                  {leagueBreakdownFinal.map((item, idx) => (
                    <tr key={idx}><td style={{ fontWeight: '500' }}>{item.league}</td><td>{item.ticketsSold}</td><td style={{ fontWeight: '600', color: '#059669' }}>{formatCurrency(item.revenue)}</td><td>{item.percentage.toFixed(1)}%</td></tr>
                  ))}
                  <tr style={{ borderTop: '2px solid #e5e7eb', fontWeight: '600', backgroundColor: '#f9fafb' }}>
                    <td>TOTAL</td><td>{leagueBreakdownFinal.reduce((sum, item) => sum + item.ticketsSold, 0)}</td>
                    <td style={{ color: '#059669' }}>{formatCurrency(leagueBreakdownFinal.reduce((sum, item) => sum + item.revenue, 0))}</td><td>100%</td>
                  </tr>
                </tbody>
              </table>
            </div>
        
          </>
        ) : (<div className={styles.emptyState}><p>No league data available. {soldCount === 0 ? 'Mark tickets as sold to see league breakdown.' : 'No leagues matched for sold tickets.'}</p></div>)}
      </div>

   {/* Timing & Pricing Intelligence */}
<div className={styles.card}>
  <div className={styles.cardHeader}>
    <div>
      <h3 style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
        <Clock size={20} />
        Timing & Pricing Intelligence
      </h3>
      <div className={styles.sellerBadge}>
        {timingSummaryFinal.basedOn?.includes('0 sales') ? 'Insufficient Data' : 'Very Early Seller'}
      </div>
      <div className={styles.basedOnText}>
        {timingSummaryFinal.basedOn || `Based on ${soldCount} sales`}
      </div>
    </div>
  </div>

  <div className={styles.timingMetricsGrid}>
    <div className={`${styles.timingMetric} ${styles.timingMetricBlue}`}>
      <div className={styles.timingValue}>{timingSummaryFinal.avgDaysBeforeSold ?? 0}</div>
      <div className={styles.timingLabel}>Avg Days Before Sold</div>
    </div>
    <div className={`${styles.timingMetric} ${styles.timingMetricGray}`}>
      <div className={styles.timingValue}>{timingSummaryFinal.medianDays ?? 0}</div>
      <div className={styles.timingLabel}>Median Days</div>
    </div>
    <div className={`${styles.timingMetric} ${styles.timingMetricGreen}`}>
      <div className={styles.timingValue}>{timingSummaryFinal.avgDaysBeforeListed ?? 0}</div>
      <div className={styles.timingLabel}>Avg Days Before Listed</div>
    </div>
    <div className={`${styles.timingMetric} ${styles.timingMetricTeal}`}>
      <div className={styles.timingValue}>
        {timingSummaryFinal.avgPriceChange >= 0 ? '+' : ''}
        {formatCurrency(timingSummaryFinal.avgPriceChange ?? 0)}
      </div>
      <div className={styles.timingLabel}>Avg Price Change</div>
    </div>
  </div>

  <div className={styles.priceMovementCountersRow}>
    <div className={styles.priceCounter}>
      <div className={`${styles.counterValue} ${styles.positive}`}>{priceMovementCounters.increased}</div>
      <div className={styles.counterLabel}>Price Increased</div>
    </div>
    <div className={styles.priceCounter}>
      <div className={`${styles.counterValue} ${styles.negative}`}>{priceMovementCounters.decreased}</div>
      <div className={styles.counterLabel}>Price Decreased</div>
    </div>
    <div className={styles.priceCounter}>
      <div className={`${styles.counterValue} ${styles.neutral}`}>{priceMovementCounters.unchanged}</div>
      <div className={styles.counterLabel}>Price Unchanged</div>
    </div>
  </div>

  {timingRowsFinal.length > 0 && (
    <div className={styles.saleTimingDistribution}>
      <div className={styles.distributionHeader}>
        <h4>Sale Timing Distribution</h4>
        <select 
          value={selectedLeagueFilter} 
          onChange={(e) => setSelectedLeagueFilter(e.target.value)}
          className={styles.leagueSelector}
        >
          <option value="all">All Leagues</option>
          {timingByLeagueFinal.map((league: any) => (
            <option key={league.name} value={league.name}>{league.name}</option>
          ))}
        </select>
      </div>
      <div className={styles.distributionBars}>
        {timingRowsFinal.map((bucket) => {
          const totalSold = soldCount || 1;
          const percent = (bucket.total / totalSold) * 100;
          return (
            <div key={bucket.timing} className={styles.distributionItem}>
              <div className={styles.distributionLabel}>
                <span>{bucket.timing}</span>
                <span>{bucket.total} ({percent.toFixed(0)}%)</span>
              </div>
              <div className={styles.barBackground}>
                <div className={styles.barFill} style={{ width: `${percent}%` }} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  )}

  <hr className={styles.divider} />

  <div className={styles.priceMovementSection}>
    <h4 style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
      <DollarSign size={16} />
      Price Movement by Sale Timing
    </h4>
    <table className={styles.priceMovementTable}>
      <thead>
        <tr>
          <th>TIMING</th>
          <th>TOTAL</th>
          <th>AVG PRICE CHANGE</th>
          <th>AVG PRICE CHANGE %</th>
        </tr>
      </thead>
      <tbody>
        {timingRowsFinal.length > 0 ? (
          timingRowsFinal.map((row: any, idx: number) => (
            <tr key={idx}>
              <td>{row.timing || '-'}</td>
              <td>{row.total ?? 0}</td>
              <td className={(row.avgPriceChange || 0) >= 0 ? styles.positive : styles.negative}>
                {(row.avgPriceChange || 0) >= 0 ? '+' : ''}
                {formatCurrency(row.avgPriceChange || 0)}
              </td>
              <td className={(row.avgPriceChangePercent || 0) >= 0 ? styles.positive : styles.negative}>
                {(row.avgPriceChangePercent || 0) >= 0 ? '+' : ''}
                {formatPercent(row.avgPriceChangePercent || 0)}
              </td>
            </tr>
          ))
        ) : (
          <tr>
            <td colSpan={4} className={styles.noData}>No timing breakdown data</td>
          </tr>
        )}
      </tbody>
    </table>
  </div>

  <hr className={styles.divider} />

  <div className={styles.timingByLeagueSection}>
    <h4>Timing by League</h4>
    {timingByLeagueFinal.length > 0 ? (
      <div className={styles.leagueCards}>
        {timingByLeagueFinal.map((league: any, idx: number) => (
          <div key={idx} className={styles.leagueCard}>
            <div className={styles.leagueCardHeader}>
              <span className={styles.leagueName}>{league.name}</span>
              <span className={styles.leagueSold}>{league.sold} sold</span>
            </div>
            <div className={styles.leagueStatsRow}>
              <div>
                <div className={styles.leagueStatLabel}>Avg Days to Sell</div>
                <div className={styles.leagueStatValue}>{(league.avgDays ?? 0).toFixed(1)} days</div>
              </div>
              <div>
                <div className={styles.leagueStatLabel}>Avg Price Change</div>
                <div className={`${styles.leagueStatValue} ${(league.avgPriceChange || 0) >= 0 ? styles.positive : styles.negative}`}>
                  {(league.avgPriceChange || 0) >= 0 ? '+' : ''}
                  {formatCurrency(league.avgPriceChange || 0)}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    ) : (
      <p className={styles.noData}>No league timing data</p>
    )}
  </div>
</div>
    </>
  )}
</div>
        )}

        {/* Teams Tab */}
        {activeTab === "teams" && (
          <div className={styles.analyticsSection}>
            <div className={styles.card}>
              <div className={styles.cardHeader}>
                <h3>Your Teams</h3>
                <div style={{ display: 'flex', gap: '16px', alignItems: 'center',marginTop: '12px' }}>
                  {selectedTeams.length > 0 && (
                    <div className={styles.searchWrapper} style={{ width: '300px' }}>
                      <Search className={styles.searchIcon} size={16} />
                      <input
                        type="text"
                        placeholder="Search teams..."
                        className={styles.searchInput}
                        value={teamSearchQuery}
                        onChange={(e) => setTeamSearchQuery(e.target.value)}
                        style={{ fontSize: '14px' }}
                      />
                    </div>
                  )}
                  <button
                    className={styles.addButton}
                    onClick={() => handleOpenAddTeamModal()}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '10px 20px',
                      background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: '600',
                      boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)',
                      transition: 'all 0.3s ease'
                    }}
                 
                  >
                    <Plus size={18} strokeWidth={3} />
                    Add Team
                  </button>
                </div>
              </div>
              {userPreferenceLoading || preferredLeagueCategoriesLoading || teamsWithSoldLoading ? (
                <div className={styles.loadingSmall}><Loader size={20} className={styles.spinner} /><span>Loading teams...</span></div>
              ) : selectedTeams.length > 0 ? (
                <>
                  {getFilteredTeams().length > 0 ? (
                    <div className={styles.selectedTeamsGrid} style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '16px', marginTop: '12px' }}>
                      {getFilteredTeams().map((team: SelectedTeam) => {
 const hasSoldTicket = localTickets.some(ticket => {
  const rawStatus = (ticket.rawStatus || ticket.status).toLowerCase();
  if (rawStatus !== 'sold') return false;
  const eventName = ticket.event.toLowerCase();
  return eventName.includes(team.name.toLowerCase()) || 
         eventName.includes(team.abbreviation.toLowerCase());
});
                        return (
                          <div
                            className={styles.selectedTeamCard}
                            key={team.id}
                            title={team.name}
                   
                            onMouseEnter={(e) => {
                              if (!hasSoldTicket) {
                                e.currentTarget.style.boxShadow = '0 8px 16px rgba(0,0,0,0.12)';
                                e.currentTarget.style.borderColor = '#bfdbfe';
                              }
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.06)';
                              e.currentTarget.style.borderColor = '#e5e7eb';
                            }}
                          >
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px' }}>
                              <div style={{ flex: 1 , flexDirection: 'column', display: 'flex', alignItems: 'center', textAlign: 'center'}}>
                                <div
                                  className={styles.selectedTeamBadge}
                                  style={{ backgroundColor: team.color, color: getContrastTextColor(team.color), width: '44px', height: '44px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontWeight: '700', borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', marginBottom: '8px', flexShrink: 0, overflow: 'hidden', textAlign: 'center', paddingTop: '0px' }}
                                >
                                  {team.abbreviation}
                                </div>
                                <div className={styles.selectedTeamName}   style={{
    fontSize: "15px",
    fontWeight: 600,
    color: "rgb(17, 24, 39)",
    width: "100%",
    maxWidth: "180px",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    display: "block",
  }}>
                                  <div>{team.name}</div>
                                  {team.leagueName && (
                                    <div style={{ fontSize: '11px', color: '#64748b', fontWeight: '500' }}>
                                      ({team.leagueName})
                                    </div>
                                  )}
                                </div>
                              </div>
                              {hasSoldTicket && (
                                <div title="Tickets sold for this team - Cannot remove" style={{ display: 'flex', alignItems: 'center', gap: '4px', backgroundColor: '#fef3c7', padding: '6px 12px', borderRadius: '8px', flexShrink: 0 }}>
                                  <CheckCheck size={18} color="#b45309" style={{ flexShrink: 0 }} />
                                  <span style={{ fontSize: '11px', fontWeight: '600', color: '#b45309' }}>Sold</span>
                                </div>
                              )}
                            </div>
                            <div style={{ display: 'flex', gap: '10px', paddingTop: '12px', borderTop: '1px solid #f3f4f6' }}>
                              <button
                                onClick={() => setSelectedTeamForSeats(team)}
                                style={{
                                  flex: 1,
                                  padding: '10px 14px',
                                  backgroundColor: '#f0f9ff',
                                  border: '1px solid #bfdbfe',
                                  borderRadius: '8px',
                                  cursor: 'pointer',
                                  fontSize: '13px',
                                  fontWeight: '600',
                                  color: '#1d4ed8',
                                  transition: 'all 0.2s ease'
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.backgroundColor = '#3b82f6';
                                  e.currentTarget.style.color = '#ffffff';
                                  e.currentTarget.style.borderColor = '#3b82f6';
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.backgroundColor = '#f0f9ff';
                                  e.currentTarget.style.color = '#1d4ed8';
                                  e.currentTarget.style.borderColor = '#bfdbfe';
                                }}
                              >
                                View Seats
                              </button>
                              {!canEditPricing && (
                                <button
                                  onClick={() => handleRemoveTeam(team.id)}
                                  disabled={hasSoldTicket || removingTeamId === team.id}
                                  style={{
                                    padding: '10px 14px',
                                    backgroundColor: hasSoldTicket ? '#f3f4f6' : '#fee2e2',
                                    color: hasSoldTicket ? '#9ca3af' : '#dc2626',
                                    border: hasSoldTicket ? '1px solid #e5e7eb' : '1px solid #fecaca',
                                    borderRadius: '8px',
                                    cursor: hasSoldTicket ? 'not-allowed' : 'pointer',
                                    fontSize: '13px',
                                    fontWeight: '600',
                                    opacity: hasSoldTicket ? 0.5 : 1,
                                    transition: 'all 0.2s ease',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: '6px'
                                  }}
                                  onMouseEnter={(e) => {
                                    if (!hasSoldTicket) {
                                      e.currentTarget.style.backgroundColor = '#fca5a5';
                                      e.currentTarget.style.color = '#7f1d1d';
                                    }
                                  }}
                                  onMouseLeave={(e) => {
                                    if (!hasSoldTicket) {
                                      e.currentTarget.style.backgroundColor = '#fee2e2';
                                      e.currentTarget.style.color = '#dc2626';
                                    }
                                  }}
                                  title={hasSoldTicket ? 'Cannot remove team with sold tickets' : 'Remove this team'}
                                >
                                  {removingTeamId === team.id ? <Loader size={16} style={{ animation: 'spin 1s linear infinite' }} /> : <Trash2 size={16} />}
                                </button>
                              )}
                              {canEditPricing && (
                                <button
                                  onClick={() => handleAdminRemoveTeam(team.id)}
                                  disabled={hasSoldTicket || adminRemovingTeamId === team.id}
                                  style={{
                                    padding: '10px 14px',
                                    backgroundColor: hasSoldTicket ? '#f3f4f6' : '#fee2e2',
                                    color: hasSoldTicket ? '#9ca3af' : '#dc2626',
                                    border: hasSoldTicket ? '1px solid #e5e7eb' : '1px solid #fecaca',
                                    borderRadius: '8px',
                                    cursor: hasSoldTicket ? 'not-allowed' : 'pointer',
                                    fontSize: '13px',
                                    fontWeight: '600',
                                    opacity: hasSoldTicket ? 0.5 : 1,
                                    transition: 'all 0.2s ease',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: '6px'
                                  }}
                                  onMouseEnter={(e) => {
                                    if (!hasSoldTicket) {
                                      e.currentTarget.style.backgroundColor = '#fca5a5';
                                      e.currentTarget.style.color = '#7f1d1d';
                                    }
                                  }}
                                  onMouseLeave={(e) => {
                                    if (!hasSoldTicket) {
                                      e.currentTarget.style.backgroundColor = '#fee2e2';
                                      e.currentTarget.style.color = '#dc2626';
                                    }
                                  }}
                                  title={hasSoldTicket ? 'Cannot remove team with sold tickets' : 'Remove team from user'}
                                >
                                  {adminRemovingTeamId === team.id ? <Loader size={16} style={{ animation: 'spin 1s linear infinite' }} /> : <Trash2 size={16} />}
                                </button>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className={styles.emptyState}><p>No teams match your search.</p></div>
                  )}
                </>
              ) : (
                <div className={styles.emptyState}><p>No teams assigned to this user. Click "Add Team" to assign teams.</p></div>
              )}
            </div>
          </div>
        )}

        {showDeleteConfirm && renderDeleteConfirm()}
        {renderVoidPayoutsModal()}
        {renderAddTeamModal()}
        {renderTeamSeatsModal()}
        {renderBulkStatusModal()}
        {renderChangeStatusModal()}
        {renderEditTicketModal()}
        {renderListedModal()}
        {renderSoldModal()}
        {renderPriceModal()}
        {renderBulkPriceModal()}
        {renderDelistEmailModal()}
      </div>
    </div>
  );
};

export default UserDetailsPage;